// src/components/CreateSponsorshipForm.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { appDialog } from '../utils/appDialog';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
  Card,
  CardContent,
  Grid,
  Container,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { t, useI18n } from '../i18n';
import { environment } from '../config/environment';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';
import {
  fetchSponsorshipTypes,
  getAvailableSponsorshipTypes,
  type SponsorshipTypeConfig,
} from '../utils/sponsorshipTypesUtils';
import { Filter } from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import { getCompleteImagePath } from '../utils/imageUtils';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun, Media, BorderStyle, SectionType, TabStopType } from 'docx';
import { saveAs } from 'file-saver';
import contestService from '../services/contestService';
import sponsorshipService from '../services/sponsorshipService';
import couponService from '../services/couponService';
import {
  Business as BuildingIcon,
  AttachMoney as DollarIcon,
  Campaign as MegaphoneIcon,
  CalendarToday as CalendarIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Visibility as EyeIcon,
  Description as FileTextIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

type SponsorshipType =
  | "rewards_only"
  | "full_entrance"
  | "partial_entrance"
  | "branding_only"
  | "custom";

export interface SponsorshipPayload {
  sponsor_name: string;
  sponsor_short_name?: string;
  industry?: string;
  contact_name?: string;
  contact_email?: string;

  sponsorship_type: SponsorshipType;

  sponsor_tier?: 'gold' | 'silver' | 'bronze'; // Tier del sponsor para asignación automática de tags
  ambassador_id?: string; // ID del embajador que firmó el sponsor

  reward_contribution?: number; // dinero que aporta al pozo de premios
  entrance_coverage_percentage?: number; // % de cobertura de entradas
  total_sponsor_cost: number; // costo total (editable)

  impressions?: number;
  engagements?: number;

  placements: {
    header_logo: boolean;
    listing_badge: boolean;
    modal_banner: boolean;
    email_mention: boolean;
    social_posts: number;
  };

  media_assets: {
    logo_url?: string;
    banner_url?: string;
    brand_guidelines_url?: string;
    landing_url?: string;
    slogan?: string;
    website_url?: string;
  };

  schedule: {
    start: string; // ISO
    end: string;   // ISO
  };

  notes?: string;
  social_links?: string;
  
  // Nuevas propiedades para plataforma personalizada
  custom_platform_image?: string;
  custom_platform_name?: string;
  custom_platforms?: Array<{
    name: string;
    image: string;
    x: number;
    y: number;
    width: number;
    height: number;
    contest_id?: string;
  }>;
  flyer_position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  
  // Configuración de plataformas habilitadas
  enabled_platforms?: {
    facebook: boolean;
    instagram: boolean;
    voletaje: boolean;
    [key: string]: boolean | undefined; // Para plataformas base dinámicas (usando su ID como clave)
  };
  enabled_base_platforms?: string[]; // IDs de plataformas base habilitadas
  
  // Orden de plataformas desde la base de datos
  platform_order?: Array<{
    name: string;
    position: number;
    platform_id: string;
    type: string;
  }>;
  
  // Tipo de entrada (independiente del tipo de patrocinio)
  entrance_type?: "full_entrance" | "partial_entrance" | null;
}

interface Props {
  onCancel: () => void;
  onCreated: (payload: SponsorshipPayload) => void;
  onShowContestOptions?: () => void;
  selectedContest?: any; // Datos del concurso seleccionado
  existingSponsorship?: any; // Datos del patrocinio existente para edición
}

const steps = [
  { id: 1, name: t('stepsSponsorshipForm.basic'), icon: BuildingIcon },
  { id: 2, name: t('stepsSponsorshipForm.assets_and_visibility'), icon: MegaphoneIcon },
  { id: 3, name: t('stepsSponsorshipForm.dates_and_summary'), icon: CalendarIcon },
];

const CreateSponsorshipForm: React.FC<Props> = ({ onCancel, onCreated, onShowContestOptions, selectedContest, existingSponsorship }) => {
  const { t: tI18n, locale } = useI18n();
  // Usar tI18n (reactivo) en lugar de t (no reactivo) para las traducciones dinámicas
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [isButtonsAtBottom, setIsButtonsAtBottom] = useState(true);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const formScrollRef = useRef<HTMLDivElement>(null);

  // Detectar cambios en el ancho de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detectar si el contenedor de botones está en su posición original
  useEffect(() => {
    const checkPosition = () => {
      if (buttonsContainerRef.current) {
        const rect = buttonsContainerRef.current.getBoundingClientRect();
        const containerRect = buttonsContainerRef.current.parentElement?.getBoundingClientRect();
        
        if (containerRect) {
          // Verificar si el contenedor está en su posición natural (no sticky)
          // Si el bottom del contenedor está cerca del bottom del padre, está en posición original
          const isAtBottom = Math.abs(rect.bottom - containerRect.bottom) < 10;
          setIsButtonsAtBottom(isAtBottom);
        }
      }
    };

    checkPosition();
    window.addEventListener('scroll', checkPosition);
    window.addEventListener('resize', checkPosition);
    
    return () => {
      window.removeEventListener('scroll', checkPosition);
      window.removeEventListener('resize', checkPosition);
    };
  }, [currentStep]);

  // Determinar si es móvil (ancho < 1000px)
  const isMobile = windowWidth < 1000;
  const [previews, setPreviews] = useState({
    logo: null,
    banner: null,
    brand_guidelines: null,
    landing: null
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [showInstagramPreview, setShowInstagramPreview] = useState(false);
  const [showInstagramPreviewV2, setShowInstagramPreviewV2] = useState(false);
  const [showInstagramPreviewV3, setShowInstagramPreviewV3] = useState(false);
  const [showFacebookPreview, setShowFacebookPreview] = useState(false);
  const [showSmallFlyerPreview, setShowSmallFlyerPreview] = useState(false);
  const [sponsorshipData, setSponsorshipData] = useState<SponsorshipPayload | null>(null);
  const [showSponsorSelection, setShowSponsorSelection] = useState(false);
  const [showSponsorList, setShowSponsorList] = useState(false);
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPlatformModal, setShowAddPlatformModal] = useState(false);
  const [customPlatformImage, setCustomPlatformImage] = useState<string | null>(null);
  const [flyerPosition, setFlyerPosition] = useState({ x: 50, y: 50, width: 200, height: 150 });
  const [showCustomPlatformPreview, setShowCustomPlatformPreview] = useState(false);
  const [customPlatformName, setCustomPlatformName] = useState<string>(t('preview_modal_info_sponsorship.customPlatform'));
  const [selectedPlatformIndex, setSelectedPlatformIndex] = useState<number | null>(null);
  const [editingPlatformIndex, setEditingPlatformIndex] = useState<number | null>(null);
  
  // Estados para la interfaz de Adjust Preview (igual que Add Platform)
  const [platformShape, setPlatformShape] = useState<'circular' | 'square'>('circular');
  const [platformLogoUrl, setPlatformLogoUrl] = useState<string>('');
  const [platformCustomText, setPlatformCustomText] = useState<string>('your ad goes here');
  const [platformFontSize, setPlatformFontSize] = useState<number>(0.9);
  const [logoSize, setLogoSize] = useState({ width: 60, height: 60 });
  const [tempWidth, setTempWidth] = useState<string>('200');
  const [showVoletoSizeDialog, setShowVoletoSizeDialog] = useState(false);
  const [selectedPlatformForWord, setSelectedPlatformForWord] = useState<any>(null);
  const [voletoSize, setVoletoSize] = useState<{ width: number; height: number } | null>(null);
  const [distanciaX, setDistanciaX] = useState<number>(60);
  const [imageGeneralSize, setImageGeneralSize] = useState<{ width: number | null; height: number | null }>({ width: null, height: null });
  const [originalImageSize, setOriginalImageSize] = useState<{ width: number; height: number } | null>(null);
  const [platformImageOrientations, setPlatformImageOrientations] = useState<Record<string, 'horizontal' | 'vertical'>>({});
  const [previewImageDimensions, setPreviewImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [tempHeight, setTempHeight] = useState<string>('150');
  const [tempLogoWidth, setTempLogoWidth] = useState<string>('60');
  const [tempLogoHeight, setTempLogoHeight] = useState<string>('60');
  
  // Estado para preview temporal con datos actuales del estado
  const [tempPreviewPlatform, setTempPreviewPlatform] = useState<any | null>(null);
  
  // Estados para edición de configuración Word
  const [showWordConfigModal, setShowWordConfigModal] = useState(false);
  const [selectedPlatformForWordConfig, setSelectedPlatformForWordConfig] = useState<any>(null);
  const [wordConfig, setWordConfig] = useState({
    box_width: 244.8,
    box_height: 316.8,
    box_x: 541.2,
    box_y: 709.2,
    contest_name_y: 40,
    text_y: 296.8,
    font_size_contest_name: 18,
    font_size_custom_text: 16,
    logo_y: 15,
    logo_top_distance: 15,
    logo_text_distance: 40,
    border_width: 3
  });
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isEnterPressedRef = useRef<boolean>(false);
  
  // Estados para drag and drop del cuadro
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [previewImageSize, setPreviewImageSize] = useState<{ width: number; height: number; naturalWidth: number; naturalHeight: number } | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const wordConfigRef = useRef(wordConfig);
  
  // Mantener la referencia actualizada
  useEffect(() => {
    wordConfigRef.current = wordConfig;
  }, [wordConfig]);
  
  // Limpiar timeouts y abort controllers al desmontar
  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // Efecto para ocultar scrollbar inferior del contenedor de imagen
  useEffect(() => {
    if (customPlatformImage && showAddPlatformModal) {
      const scrollbarId = `scrollbar-bottom-sponsorship-${editingPlatformIndex || 'new'}`;
      const styleId = `hide-scrollbar-${scrollbarId}`;
      
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          #${scrollbarId}::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            background: transparent !important;
          }
          #${scrollbarId}::-webkit-scrollbar-track {
            display: none !important;
            background: transparent !important;
          }
          #${scrollbarId}::-webkit-scrollbar-thumb {
            display: none !important;
            background: transparent !important;
          }
          #${scrollbarId} {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        `;
        document.head.appendChild(style);
      }
      
      return () => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, [customPlatformImage, showAddPlatformModal, editingPlatformIndex]);
  const [showPreviewConfigModal, setShowPreviewConfigModal] = useState(false);
  const [visiblePreviews, setVisiblePreviews] = useState({
    facebook: false,
    instagram: false,
    voletaje: true
  });
  const [visibleBasePlatforms, setVisibleBasePlatforms] = useState<Record<string, boolean>>({});
  const [selectedBasePlatformForPreview, setSelectedBasePlatformForPreview] = useState<any | null>(null);
  const [showBasePlatformPreview, setShowBasePlatformPreview] = useState(false);
  const [previewScale, setPreviewScale] = useState<number>(100);
  const [tempPreviewScale, setTempPreviewScale] = useState<string>('100');
  const [previewViewMode, setPreviewViewMode] = useState<'list' | 'gallery'>('list');
  const [previewPlatformsSearchTerm, setPreviewPlatformsSearchTerm] = useState('');
  const [platformOrder, setPlatformOrder] = useState<string[]>([]);
  const [draggedPlatformId, setDraggedPlatformId] = useState<string | null>(null);
  const [dragOverPlatformId, setDragOverPlatformId] = useState<string | null>(null);
  
  // Monitorear cambios en platformOrder
  useEffect(() => {
    console.log('📊 platformOrder cambió:', platformOrder);
    console.log('📊 Longitud de platformOrder:', platformOrder.length);
  }, [platformOrder]);
  
  // Estado para recordar el tipo base cuando se selecciona una entrada
  const [baseSponsorshipType, setBaseSponsorshipType] = useState<"rewards_only" | "branding_only" | null>(null);
  
  // Estado para plataformas base
  const [basePlatforms, setBasePlatforms] = useState<any[]>([]);
  const [loadingBasePlatforms, setLoadingBasePlatforms] = useState(false);
  
  // Estado para tipos de patrocinio configurados
  const [sponsorshipTypesConfig, setSponsorshipTypesConfig] = useState<any[]>([]);
  const [loadingSponsorshipTypesConfig, setLoadingSponsorshipTypesConfig] = useState(false);
  
  // Estado para embajadores (para el selector de ambassador_id)
  const [ambassadors, setAmbassadors] = useState<any[]>([]);
  const [loadingAmbassadors, setLoadingAmbassadors] = useState(false);
  
  // Estados para controlar el focus de los campos de tier y ambassador
  const [sponsorTierFocused, setSponsorTierFocused] = useState(false);
  // Estado para controlar si la sección de Tiers está activa
  const [tiersSectionActive, setTiersSectionActive] = useState(() => {
    // Si hay un sponsorship existente con tier, activar la sección
    if (existingSponsorship?.sponsor_tier) {
      return true;
    }
    return false;
  });
  const [ambassadorFocused, setAmbassadorFocused] = useState(false);

  // Función helper para obtener las plataformas personalizadas filtradas por concurso
  const getFilteredCustomPlatforms = () => {
    if (!Array.isArray(formData.custom_platforms) || formData.custom_platforms.length === 0) {
      return [];
    }
    const currentContestId = selectedContest?.id || existingSponsorship?.contest_id;
    
    // Si no hay concurso seleccionado (creación nueva), mostrar todas las plataformas sin contest_id
    if (!currentContestId) {
      return formData.custom_platforms.filter(plat => !plat.contest_id);
    }
    
    // Si hay concurso seleccionado, mostrar solo las plataformas del concurso o las sin contest_id (para asignar después)
    return formData.custom_platforms.filter(plat => {
      // Mostrar si no tiene contest_id (nuevas) o coincide con el concurso actual
      return !plat.contest_id || plat.contest_id === currentContestId;
    });
  };

  // Función para generar un ID único para una plataforma
  const getPlatformId = (platform: any, index?: number): string => {
    if (platform.isBase) {
      return `base-${platform.id || platform.platform_type || index}`;
    }
    if (platform.contest_id) {
      return `custom-${platform.contest_id}-${platform.name}-${platform.image}`;
    }
    return `custom-${platform.name}-${platform.image}-${index || 0}`;
  };

  // Función helper para construir el orden según platform_order de la BD
  const buildPlatformsFromDBOrder = () => {
    const platforms: any[] = [];
    
    // Verificar si existe platform_order en formData (desde la BD)
    if (formData.platform_order && Array.isArray(formData.platform_order) && formData.platform_order.length > 0) {
      // Ordenar el array por position
      const sortedPlatformOrder = [...formData.platform_order].sort((a, b) => a.position - b.position);
      
      // Construir el array de plataformas según el orden de platform_order
      sortedPlatformOrder.forEach((orderItem) => {
        if (orderItem.type === 'voletaje') {
          // Verificar si voletaje está visible
          if (visiblePreviews.voletaje) {
            platforms.push({
              id: 'voletaje',
              name: orderItem.name || t('preview_modal_info_sponsorship.voletaje'),
              type: 'voletaje',
              isBase: true,
              platform_type: 'voletaje'
            });
          }
        } else if (orderItem.type === 'base') {
          // Buscar la plataforma base por platform_id
          // platform_id puede venir como "base-platform_xxx" o "platform_xxx"
          let platformId = orderItem.platform_id;
          if (platformId.startsWith('base-')) {
            platformId = platformId.replace('base-', '');
          }
          const basePlatform = basePlatforms.find(bp => bp.id === platformId);
          if (basePlatform) {
            const isEnabled = (formData.enabled_base_platforms || []).includes(basePlatform.id);
            const isVisible = visibleBasePlatforms[basePlatform.id] !== false;
            if (isEnabled && isVisible) {
              platforms.push({
                ...basePlatform,
                isBase: true,
                id: basePlatform.id,
                name: orderItem.name || basePlatform.name
              });
            }
          }
        } else if (orderItem.type === 'custom') {
          // Buscar plataforma personalizada
          const customPlatform = getFilteredCustomPlatforms().find(cp => {
            const customId = `custom-${cp.name}-${cp.image}`;
            return orderItem.platform_id.includes(customId) || orderItem.platform_id === customId;
          });
          if (customPlatform) {
            platforms.push({
              ...customPlatform,
              isBase: false,
              name: orderItem.name || customPlatform.name
            });
          }
        }
      });
      
      // Agregar plataformas que no están en platform_order pero están habilitadas
      // Voletaje
      if (visiblePreviews.voletaje && !platforms.some(p => p.type === 'voletaje' || p.platform_type === 'voletaje')) {
        platforms.unshift({
          id: 'voletaje',
          name: t('preview_modal_info_sponsorship.voletaje'),
          type: 'voletaje',
          isBase: true,
          platform_type: 'voletaje'
        });
      }
      
      // Plataformas base habilitadas que no están en platform_order
      basePlatforms
        .filter(platform => {
          const isEnabled = (formData.enabled_base_platforms || []).includes(platform.id);
          const isVisible = visibleBasePlatforms[platform.id] !== false;
          const alreadyIncluded = platforms.some(p => p.isBase && p.id === platform.id);
          return isEnabled && isVisible && !alreadyIncluded;
        })
        .forEach(platform => {
          platforms.push({
            ...platform,
            isBase: true,
            id: platform.id
          });
        });
      
      // Plataformas personalizadas que no están en platform_order
      getFilteredCustomPlatforms()
        .filter(cp => !platforms.some(p => !p.isBase && p.name === cp.name && p.image === cp.image))
        .forEach(cp => {
          platforms.push({
            ...cp,
            isBase: false
          });
        });
    } else {
      // Si no hay platform_order, usar el orden de basePlatforms como fallback
      // Voletaje (siempre primero si está visible)
      if (visiblePreviews.voletaje) {
        platforms.push({
          id: 'voletaje',
          name: t('preview_modal_info_sponsorship.voletaje'),
          type: 'voletaje',
          isBase: true,
          platform_type: 'voletaje'
        });
      }
      
      // Plataformas base - usar el orden EXACTO de basePlatforms tal cual viene de la lista
      const enabledBasePlatforms = basePlatforms
        .filter(platform => {
          const isEnabled = (formData.enabled_base_platforms || []).includes(platform.id);
          const isVisible = visibleBasePlatforms[platform.id] !== false;
          return isEnabled && isVisible;
        })
        .map((platform) => ({
          ...platform,
          isBase: true,
          id: platform.id
        }));
      platforms.push(...enabledBasePlatforms);
      
      // Plataformas personalizadas
      const customPlatforms = getFilteredCustomPlatforms().map((plat, idx) => ({
        ...plat,
        isBase: false
      }));
      platforms.push(...customPlatforms);
      
      // Si hay una plataforma personalizada antigua (formato legacy)
      if (formData.custom_platform_image && !getFilteredCustomPlatforms().length) {
        platforms.push({
          id: 'custom-legacy',
          name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
          type: 'custom',
          isBase: false,
          isLegacy: true
        });
      }
    }
    
    return platforms;
  };

  // Función para obtener todas las plataformas visibles en el orden correcto
  const getOrderedPlatforms = () => {
    // Usar la función helper para construir el orden según platform_order de la BD
    const platforms = buildPlatformsFromDBOrder();
    
    // Si platformOrder está vacío o no coincide con el número de plataformas, usar orden BD
    if (platformOrder.length === 0 || platformOrder.length !== platforms.length) {
      return platforms;
    }
    
    // Verificar si el orden en platformOrder coincide con el orden de la base de datos
    const currentOrder = platforms.map((p) => getPlatformId(p));
    const orderMatches = currentOrder.length === platformOrder.length && 
      currentOrder.every((id, idx) => platformOrder[idx] === id);
    
    // Si el orden coincide con el de la BD, retornar orden BD directamente
    if (orderMatches) {
      return platforms;
    }
    
    // Solo ordenar según platformOrder si hay un orden personalizado diferente al de la BD
    // Esto solo ocurre cuando el usuario ha arrastrado elementos para cambiar el orden
    const orderedPlatforms = [...platforms].sort((a, b) => {
      const idA = getPlatformId(a);
      const idB = getPlatformId(b);
      const indexA = platformOrder.indexOf(idA);
      const indexB = platformOrder.indexOf(idB);
      
      // Si alguna plataforma no está en el orden, mantener su posición original (orden BD)
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });
    
    return orderedPlatforms;
  };

  // Función para mover una plataforma hacia arriba
  const movePlatformUp = (platformId: string) => {
    console.log('🔼 movePlatformUp llamado con platformId:', platformId);
    setPlatformOrder(prevOrder => {
      console.log('🔼 Orden anterior:', prevOrder);
      const index = prevOrder.indexOf(platformId);
      if (index <= 0) {
        console.log('🔼 Ya está en la primera posición, no se cambia');
        return prevOrder; // Ya está en la primera posición
      }
      
      const newOrder = [...prevOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      console.log('🔼 Nuevo orden después de mover arriba:', newOrder);
      return newOrder;
    });
  };

  // Función para mover una plataforma hacia abajo
  const movePlatformDown = (platformId: string) => {
    console.log('🔽 movePlatformDown llamado con platformId:', platformId);
    setPlatformOrder(prevOrder => {
      console.log('🔽 Orden anterior:', prevOrder);
      const index = prevOrder.indexOf(platformId);
      if (index < 0 || index >= prevOrder.length - 1) {
        console.log('🔽 Ya está en la última posición, no se cambia');
        return prevOrder; // Ya está en la última posición
      }
      
      const newOrder = [...prevOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      console.log('🔽 Nuevo orden después de mover abajo:', newOrder);
      return newOrder;
    });
  };

  // Función para mover una plataforma a una nueva posición (drag and drop)
  const movePlatformToPosition = (draggedId: string, targetId: string) => {
    console.log('🔄 movePlatformToPosition llamado - draggedId:', draggedId, 'targetId:', targetId);
    setPlatformOrder(prevOrder => {
      console.log('🔄 Orden anterior:', prevOrder);
      const draggedIndex = prevOrder.indexOf(draggedId);
      const targetIndex = prevOrder.indexOf(targetId);
      
      if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
        console.log('🔄 No se puede mover (índices inválidos o misma posición)');
        return prevOrder;
      }
      
      const newOrder = [...prevOrder];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, removed);
      console.log('🔄 Nuevo orden después de drag and drop:', newOrder);
      return newOrder;
    });
  };

  // Handlers para drag and drop
  const handleDragStart = (e: React.DragEvent, platformId: string) => {
    setDraggedPlatformId(platformId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', platformId);
  };

  const handleDragOver = (e: React.DragEvent, platformId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (platformId !== draggedPlatformId) {
      setDragOverPlatformId(platformId);
    }
  };

  const handleDragLeave = () => {
    setDragOverPlatformId(null);
  };

  const handleDrop = (e: React.DragEvent, targetPlatformId: string) => {
    e.preventDefault();
    console.log('🎯 handleDrop - draggedPlatformId:', draggedPlatformId, 'targetPlatformId:', targetPlatformId);
    if (draggedPlatformId && draggedPlatformId !== targetPlatformId) {
      movePlatformToPosition(draggedPlatformId, targetPlatformId);
    } else {
      console.log('🎯 handleDrop - No se mueve (misma plataforma o sin draggedPlatformId)');
    }
    setDraggedPlatformId(null);
    setDragOverPlatformId(null);
  };

  const handleDragEnd = () => {
    setDraggedPlatformId(null);
    setDragOverPlatformId(null);
  };

  // Función para calcular el tamaño del voleto basado en el condicional
  const calculateVoletoSize = async (platform: any): Promise<{ width: number; height: number }> => {
    // Si existen Fix_width y Fix_height, usarlos directamente
    if (platform.Fix_width && platform.Fix_height) {
      return { width: platform.Fix_width, height: platform.Fix_height };
    }
    
    return new Promise((resolve) => {
      const bgImage = new Image();
      bgImage.crossOrigin = 'anonymous';
      bgImage.onload = () => {
        const previewMaxWidth = 800;
        const previewDisplayWidth = Math.min(bgImage.width, previewMaxWidth);
        const previewDisplayHeight = (bgImage.height / bgImage.width) * previewDisplayWidth;
        
        const flyerWidthPercent = ((platform.width || 200) / previewDisplayWidth) * 100;
        const flyerHeightPercent = ((platform.height || 150) / previewDisplayHeight) * 100;
        
        let flyerWidth = (flyerWidthPercent / 100) * bgImage.width;
        let flyerHeight = (flyerHeightPercent / 100) * bgImage.height;
        
        const maxDimension = Math.max(bgImage.width, bgImage.height);
        const minDimension = Math.min(bgImage.width, bgImage.height);
        const differencePercent = (minDimension / maxDimension) * 100;
        
        let tallImageScaleFactor = 1.0;
        if (bgImage.height > bgImage.width && differencePercent < 100) {
          if (differencePercent <= 40.18) {
            tallImageScaleFactor = 0.4;
          } else if (differencePercent <= 50) {
            tallImageScaleFactor = 0.4 + ((differencePercent - 40.18) / (50 - 40.18)) * (0.65 - 0.5);
          } else {
            tallImageScaleFactor = 0.677 + ((differencePercent - 50) / 50) * 0.35;
          }
          flyerWidth = flyerWidth * tallImageScaleFactor;
          flyerHeight = flyerHeight * tallImageScaleFactor;
        }
        
        resolve({ width: Math.round(flyerWidth), height: Math.round(flyerHeight) });
      };
      bgImage.onerror = () => {
        resolve({ width: platform.width || 200, height: platform.height || 150 });
      };
      bgImage.src = platform.image || platform.image_url;
    });
  };

  // Función para abrir modal de configuración Word
  const openWordConfigModal = async (platform: any) => {
    try {
      const platformName = platform.name || 'default';
      setSelectedPlatformForWordConfig(platform);
      
      // Obtener configuración guardada
      let configToUse;
      const response = await fetch(`${environment.apiBaseUrl}/platform-word/${encodeURIComponent(platformName)}`, {
        headers: getApiHeadersForGet()
      });
      if (response.ok) {
        const config = await response.json();
        configToUse = config;
        setWordConfig(config);
      } else {
        // Usar valores por defecto
        configToUse = {
          box_width: 244.8,
          box_height: 316.8,
          box_x: 541.2,
          box_y: 709.2,
          contest_name_y: 40,
          text_y: 296.8,
          font_size_contest_name: 18,
          font_size_custom_text: 16,
          logo_y: 15,
          logo_top_distance: 15,
          logo_text_distance: 40,
          border_width: 3
        };
        setWordConfig(configToUse);
      }
      
      // Generar URL de preview
      const imageUrl = platform.image || platform.image_url;
      let normalizedImageUrl = imageUrl;
      if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
      }
      
      const logoUrl = platform.logo_url || formData.media_assets.logo_url;
      let normalizedLogoUrl = logoUrl;
      if (logoUrl && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
        normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
      }
      
      const customText = platform.custom_text || 'your ad goes here';
      const contestName = selectedContest?.name || 'CONTEST NAME';
      
      // Generar preview inicial usando el config cargado (no el estado que aún no se ha actualizado)
      await updateWordPreview(platform, normalizedImageUrl, normalizedLogoUrl, customText, contestName, platformName, configToUse);
      
      setShowWordConfigModal(true);
    } catch (err: any) {
      console.error('Error abriendo modal de configuración Word:', err);
      void appDialog.alert(`Error: ${err.message}`);
    }
  };
  
  // Función para actualizar preview
  const updateWordPreview = async (
    platform: any,
    normalizedImageUrl?: string,
    normalizedLogoUrl?: string | null,
    customText?: string,
    contestName?: string,
    platformName?: string,
    configOverride?: typeof wordConfig
  ) => {
    if (!selectedPlatformForWordConfig && !platform) return;
    
    // Cancelar petición anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Crear nuevo AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    const currentPlatform = platform || selectedPlatformForWordConfig;
    const currentPlatformName = platformName || currentPlatform?.name || 'default';
    
    // Usar configOverride si se proporciona, sino usar wordConfig del estado
    const configToUse = configOverride || wordConfig;
    
    try {
      const imageUrl = normalizedImageUrl || currentPlatform?.image || currentPlatform?.image_url;
      if (!imageUrl) return;
      
      let finalImageUrl = imageUrl;
      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        finalImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
      }
      
      const logoUrl = normalizedLogoUrl !== undefined 
        ? normalizedLogoUrl 
        : (currentPlatform?.logo_url || formData.media_assets.logo_url);
      let finalLogoUrl = logoUrl;
      if (logoUrl && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
        finalLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
      }
      
      const finalCustomText = customText !== undefined 
        ? customText 
        : (currentPlatform?.custom_text || 'your ad goes here');
      const finalContestName = contestName !== undefined 
        ? contestName 
        : (selectedContest?.name || 'CONTEST NAME');
      
      const previewResponse = await fetch(`${environment.apiBaseUrl}/generar-preview-word`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          imageUrl: finalImageUrl,
          logoUrl: finalLogoUrl || null,
          customText: finalCustomText || null,
          contestName: finalContestName || null,
          platformName: currentPlatformName,
          ...configToUse
        }),
        signal: abortController.signal
      });
      
      if (previewResponse.ok && !abortController.signal.aborted) {
        const blob = await previewResponse.blob();
        if (!abortController.signal.aborted) {
          const url = window.URL.createObjectURL(blob);
          // Liberar URL anterior
          if (previewImageUrl) {
            window.URL.revokeObjectURL(previewImageUrl);
          }
          setPreviewImageUrl(url);
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Error actualizando preview:', err);
      }
    }
  };
  
  // Función helper para actualizar preview con config actualizado
  const updatePreviewWithConfig = (updatedConfig: typeof wordConfig) => {
    // Marcar que se está procesando Enter
    isEnterPressedRef.current = true;
    
    // Cancelar cualquier debounce pendiente
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    
    // Cancelar petición anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setWordConfig(updatedConfig);
    if (selectedPlatformForWordConfig) {
      updateWordPreview(undefined, undefined, undefined, undefined, undefined, undefined, updatedConfig).finally(() => {
        // Resetear el flag después de que termine la actualización
        setTimeout(() => {
          isEnterPressedRef.current = false;
        }, 100);
      });
    } else {
      isEnterPressedRef.current = false;
    }
  };
  
  // Función debounced para actualizar preview
  const debouncedUpdateWordPreview = useCallback(() => {
    // No ejecutar si se está procesando Enter
    if (isEnterPressedRef.current) {
      return;
    }
    
    // Cancelar timeout anterior
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    
    // Cancelar petición anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Crear nuevo timeout
    previewTimeoutRef.current = setTimeout(() => {
      // Llamar sin argumentos para usar los valores del estado
      if (selectedPlatformForWordConfig) {
        updateWordPreview(undefined, undefined, undefined, undefined, undefined, undefined);
      }
    }, 500); // Aumentado a 500ms para reducir llamadas
  }, [selectedPlatformForWordConfig]);
  
  // Función para guardar configuración
  const saveWordConfig = async () => {
    try {
      const platformName = selectedPlatformForWordConfig?.name || 'default';
      const response = await fetch(`${environment.apiBaseUrl}/platform-word`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          platform_name: platformName,
          ...wordConfig
        }),
      });
      
      if (response.ok) {
        void appDialog.alert('Configuración guardada exitosamente');
        // Actualizar preview con la configuración guardada
        if (selectedPlatformForWordConfig) {
          await updateWordPreview(undefined, undefined, undefined, undefined, undefined, undefined);
        }
      } else {
        throw new Error('Error guardando configuración');
      }
    } catch (err: any) {
      console.error('Error guardando configuración:', err);
      void appDialog.alert(`Error: ${err.message}`);
    }
  };

  // Funciones para drag and drop del cuadro
  // Convertir coordenadas de la imagen mostrada a coordenadas reales de la imagen
  const displayToRealCoords = (displayX: number, displayY: number): { x: number; y: number } => {
    if (!previewImageSize) return { x: 0, y: 0 };
    const scaleX = previewImageSize.naturalWidth / previewImageSize.width;
    const scaleY = previewImageSize.naturalHeight / previewImageSize.height;
    return {
      x: displayX * scaleX,
      y: displayY * scaleY
    };
  };

  // Convertir coordenadas reales a coordenadas de la imagen mostrada
  const realToDisplayCoords = (realX: number, realY: number): { x: number; y: number } => {
    if (!previewImageSize) return { x: 0, y: 0 };
    const scaleX = previewImageSize.width / previewImageSize.naturalWidth;
    const scaleY = previewImageSize.height / previewImageSize.naturalHeight;
    return {
      x: realX * scaleX,
      y: realY * scaleY
    };
  };

  // Handler para iniciar el arrastre
  const handleBoxMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!previewImageSize) return;
    
    const currentConfig = wordConfigRef.current;
    const displayCoords = realToDisplayCoords(currentConfig.box_x, currentConfig.box_y);
    const rect = previewImageRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - (rect.left + displayCoords.x),
      y: e.clientY - (rect.top + displayCoords.y)
    });
  };

  // Handler para mover el cuadro
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !previewImageSize || !previewImageRef.current) return;
    
    const currentConfig = wordConfigRef.current;
    const rect = previewImageRef.current.getBoundingClientRect();
    const displayX = e.clientX - rect.left - dragStart.x;
    const displayY = e.clientY - rect.top - dragStart.y;
    
    // Limitar el cuadro dentro de los límites de la imagen
    const maxX = previewImageSize.width - (currentConfig.box_width * (previewImageSize.width / previewImageSize.naturalWidth));
    const maxY = previewImageSize.height - (currentConfig.box_height * (previewImageSize.height / previewImageSize.naturalHeight));
    
    const clampedX = Math.max(0, Math.min(displayX, maxX));
    const clampedY = Math.max(0, Math.min(displayY, maxY));
    
    const realCoords = displayToRealCoords(clampedX, clampedY);
    
    const updatedConfig = {
      ...currentConfig,
      box_x: realCoords.x,
      box_y: realCoords.y
    };
    
    setWordConfig(updatedConfig);
  }, [isDragging, previewImageSize, dragStart]);

  // Handler para finalizar el arrastre
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // NO actualizar automáticamente, solo cuando se presione Enter o se guarde
    }
  }, [isDragging]);

  // Handler para presionar Enter en la imagen/contenedor para actualizar preview
  const handlePreviewKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Si se está arrastrando, finalizar el arrastre primero
      if (isDragging) {
        setIsDragging(false);
      }
      // Actualizar preview
      if (selectedPlatformForWordConfig) {
        updateWordPreview(undefined, undefined, undefined, undefined, undefined, undefined);
      }
    }
  };

  // Efecto para manejar eventos de mouse globales
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Efecto para recalcular dimensiones cuando cambie la imagen
  useEffect(() => {
    if (previewImageUrl && previewImageRef.current) {
      const img = previewImageRef.current;
      const checkSize = () => {
        if (img.complete && img.naturalWidth && img.naturalHeight) {
          const rect = img.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            setPreviewImageSize({
              width: rect.width,
              height: rect.height,
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight
            });
          }
        }
      };
      
      // Intentar inmediatamente
      checkSize();
      
      // También después de un pequeño delay para asegurar que el layout esté completo
      const timeout = setTimeout(checkSize, 200);
      return () => clearTimeout(timeout);
    } else {
      setPreviewImageSize(null);
    }
  }, [previewImageUrl]);

  // Limpiar estado de drag al cerrar el modal
  useEffect(() => {
    if (!showWordConfigModal) {
      setIsDragging(false);
      setPreviewImageSize(null);
    }
  }, [showWordConfigModal]);

  // Función simple para generar Word usando el endpoint del servidor
  const generateWordDocSimple = async (platform: any) => {
    try {
      // Obtener la URL de la imagen de la plataforma
      const imageUrl = platform.image || platform.image_url;
      if (!imageUrl) {
        void appDialog.alert('La plataforma no tiene imagen');
        return;
      }

      // Normalizar la URL usando getCompleteImagePath si es necesario
      let normalizedImageUrl = imageUrl;
      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
        normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
      }

      // Obtener el logo_url y el texto personalizado
      const logoUrl = platform.logo_url || formData.media_assets.logo_url;
      const customText = platform.custom_text || 'your ad goes here';
      const contestName = selectedContest?.name || 'CONTEST NAME';

      // Normalizar logo URL si es necesario
      let normalizedLogoUrl = logoUrl;
      if (logoUrl && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
        normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
      }

      console.log('generateWordDocSimple: Generando Word con imagen:', normalizedImageUrl);
      console.log('generateWordDocSimple: Logo URL:', normalizedLogoUrl);
      console.log('generateWordDocSimple: Custom text:', customText);
      console.log('generateWordDocSimple: Contest name:', contestName);

      // Llamar al endpoint del servidor
      const response = await fetch(`${environment.apiBaseUrl}/generar-docx`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ 
          imageUrl: normalizedImageUrl,
          logoUrl: normalizedLogoUrl || null,
          customText: customText || null,
          contestName: contestName || null,
          platformName: platform.name || 'default'
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Error generando el documento');
      }

      // Recibir el .docx como blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Crear link temporal para forzar la descarga
      const platformName = (platform.name || 'Platform').replace(/[^a-z0-9]/gi, '_');
      const fileName = `${platformName}_${new Date().toISOString().split('T')[0]}.docx`;
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error generando Word:', err);
      void appDialog.alert(err.message || 'Error generando el documento');
    }
  };

  // Función para descargar una sola plataforma en Word
  // Función helper para cargar imágenes correctamente para el documento Word
  const loadImageForWord = async (imageUrl: string | null | undefined): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
    if (!imageUrl) {
      console.warn('loadImageForWord: No image URL provided');
      return null;
    }

    // Normalizar la URL usando getCompleteImagePath si es necesario
    let normalizedUrl = imageUrl;
    if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
      // Si no es una URL completa, intentar generar la URL completa
      normalizedUrl = getCompleteImagePath(imageUrl) || imageUrl;
    }

    console.log('loadImageForWord: Loading image from URL:', normalizedUrl);

    // Función helper para cargar imagen usando proxy (siempre necesario para evitar canvas tainted)
    const loadImageViaProxy = async (url: string): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
      try {
        // environment.apiBaseUrl ya incluye /api, así que solo agregamos /proxy/image
        const proxyUrl = `${environment.apiBaseUrl}/proxy/image?url=${encodeURIComponent(url)}`;
        console.log('loadImageForWord: Loading via proxy URL:', proxyUrl);
        
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('loadImageForWord: Proxy request failed:', response.status, errorText);
          throw new Error(`Proxy request failed: ${response.status} - ${errorText}`);
        }

        // Verificar que la respuesta sea una imagen
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.startsWith('image/')) {
          const errorText = await response.text();
          console.error('loadImageForWord: Proxy returned non-image content:', contentType, errorText);
          throw new Error(`Proxy returned non-image content: ${contentType}`);
        }

        const blob = await response.blob();
        console.log('loadImageForWord: Blob received, size:', blob.size, 'type:', blob.type);
        
        if (blob.size === 0) {
          console.error('loadImageForWord: Received empty blob from proxy');
          throw new Error('Received empty blob from proxy');
        }

        const arrayBuffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        console.log('loadImageForWord: ArrayBuffer created, size:', arrayBuffer.byteLength);

        // Crear imagen para obtener dimensiones
        const img = new Image();
        const imageUrl = URL.createObjectURL(blob);
        console.log('loadImageForWord: Created object URL from blob');
        
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            URL.revokeObjectURL(imageUrl);
            console.error('loadImageForWord: Image load timeout after 30 seconds');
            reject(new Error('Image load timeout'));
          }, 30000);
          
          img.onload = () => {
            clearTimeout(timeout);
            URL.revokeObjectURL(imageUrl);
            console.log('loadImageForWord: Image loaded successfully from blob, dimensions:', img.width, 'x', img.height);
            resolve(null);
          };
          img.onerror = (error) => {
            clearTimeout(timeout);
            URL.revokeObjectURL(imageUrl);
            console.error('loadImageForWord: Error loading image from blob:', error);
            console.error('loadImageForWord: Image src was:', imageUrl);
            reject(new Error('Failed to load image from blob'));
          };
          img.src = imageUrl;
        });

        console.log('loadImageForWord: Image loaded via proxy successfully, dimensions:', img.width, 'x', img.height, 'data size:', uint8Array.length);
        return {
          data: uint8Array,
          width: img.width,
          height: img.height
        };
      } catch (error) {
        console.error('loadImageForWord: Error loading via proxy:', error);
        console.error('loadImageForWord: Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          url: url
        });
        return null;
      }
    };

    // Para documentos Word, siempre usar el proxy para evitar problemas de canvas tainted
    // El proxy asegura que la imagen se carga con CORS correcto y puede ser exportada del canvas
    return await loadImageViaProxy(normalizedUrl);
  };

  const downloadSinglePlatformWord = async (platform: any) => {
    // Buscar la plataforma base correspondiente para obtener Fix_width, Fix_height y distancia_x
    let platformWithFixSize = { ...platform };
    
    // Si es voletaje, buscar en basePlatforms
    if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
      const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
      if (voletajeBase) {
        platformWithFixSize = {
          ...platformWithFixSize,
          Fix_width: voletajeBase.Fix_width,
          Fix_height: voletajeBase.Fix_height,
          distancia_x: voletajeBase.distancia_x,
          general_width: voletajeBase.general_width,
          general_height: voletajeBase.general_height
        };
      }
    } else if (platform.isBase && platform.id) {
      // Si es plataforma base, buscar en basePlatforms
      const basePlatform = basePlatforms.find((bp: any) => bp.id === platform.id);
      if (basePlatform) {
        platformWithFixSize = {
          ...platformWithFixSize,
          Fix_width: basePlatform.Fix_width,
          Fix_height: basePlatform.Fix_height,
          distancia_x: basePlatform.distancia_x,
          general_width: basePlatform.general_width,
          general_height: basePlatform.general_height
        };
      }
    }
    
    // Si existen Fix_width y Fix_height, usarlos directamente
    // Si no, calcular el tamaño actual del voleto
    let currentSize: { width: number; height: number };
    if (platformWithFixSize.Fix_width && platformWithFixSize.Fix_height) {
      currentSize = { width: platformWithFixSize.Fix_width, height: platformWithFixSize.Fix_height };
    } else {
      currentSize = await calculateVoletoSize(platformWithFixSize);
    }
    
    // Cargar distancia_x desde la plataforma base (valor por defecto 60)
    const distanciaXValue = platformWithFixSize.distancia_x || 60;
    
    // Cargar la imagen para obtener su tamaño actual
    let imageCurrentSize = { width: null as number | null, height: null as number | null };
    let originalSize = null as { width: number; height: number } | null;
    
    // Verificar si hay valores guardados en la base de datos
    if (platformWithFixSize.general_width && platformWithFixSize.general_height) {
      imageCurrentSize = { width: platformWithFixSize.general_width, height: platformWithFixSize.general_height };
    }
    
    try {
      const imageUrl = platformWithFixSize.image || platformWithFixSize.image_url;
      if (imageUrl) {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            originalSize = { width: img.width, height: img.height };
            // Si no hay valores guardados, usar el tamaño de la imagen
            if (!imageCurrentSize.width || !imageCurrentSize.height) {
              imageCurrentSize = { width: img.width, height: img.height };
            }
            resolve(null);
          };
          img.onerror = reject;
          img.src = imageUrl;
        });
      }
    } catch (error) {
      console.error('Error cargando imagen para obtener tamaño:', error);
    }
    
    setSelectedPlatformForWord(platformWithFixSize);
    setVoletoSize(currentSize);
    setDistanciaX(distanciaXValue);
    setImageGeneralSize(imageCurrentSize);
    setOriginalImageSize(originalSize);
    setShowVoletoSizeDialog(true);
  };

  // Función que realmente genera y descarga el documento Word
  const generateAndDownloadWord = async (platform: any, customSize?: { width: number; height: number }, distanciaX?: number, generalSize?: { width: number | null; height: number | null }) => {
    try {
      // Función para crear imagen compuesta con volante (reutilizada)
      const createCompositeImage = async (platformData: any, customSize?: { width: number; height: number }, distanciaXParam?: number, generalSize?: { width: number | null; height: number | null }): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return null;

          // Cargar imagen de fondo usando la función helper
          const imageUrl = platformData.image || platformData.image_url;
          if (!imageUrl) {
            console.error('createCompositeImage: No image URL provided for platform:', platformData.name);
            throw new Error('No image URL provided');
          }
          
          // Normalizar URL si es necesario
          let normalizedImageUrl = imageUrl;
          if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
          }
          
          console.log('createCompositeImage: Loading background image from:', normalizedImageUrl);
          
          // Usar loadImageForWord para manejar CORS automáticamente
          console.log('createCompositeImage: Calling loadImageForWord for:', normalizedImageUrl);
          const imageData = await loadImageForWord(normalizedImageUrl);
          console.log('createCompositeImage: loadImageForWord returned:', imageData ? `data with size ${imageData.data.length} and dimensions ${imageData.width}x${imageData.height}` : 'null');
          if (!imageData) {
            console.error('createCompositeImage: loadImageForWord returned null, cannot proceed');
            throw new Error(`Failed to load background image: ${normalizedImageUrl}`);
          }
          
          // Crear un elemento Image desde los datos cargados
          // Usar 'as any' para evitar problemas de tipo con TypeScript
          const blob = new Blob([imageData.data as any], { type: 'image/png' });
          const imageUrlFromBlob = URL.createObjectURL(blob);
          const bgImage = new Image();
          
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              URL.revokeObjectURL(imageUrlFromBlob);
              reject(new Error('Background image load timeout'));
            }, 30000);
            
            bgImage.onload = () => {
              clearTimeout(timeout);
              URL.revokeObjectURL(imageUrlFromBlob);
              console.log('createCompositeImage: Background image loaded successfully, dimensions:', imageData.width, 'x', imageData.height);
              resolve(null);
            };
            bgImage.onerror = (error) => {
              clearTimeout(timeout);
              URL.revokeObjectURL(imageUrlFromBlob);
              console.error('createCompositeImage: Error loading background image from blob:', error);
              reject(new Error(`Failed to load background image from blob`));
            };
            bgImage.src = imageUrlFromBlob;
          });
          
          // Usar las dimensiones reales de los datos cargados
          bgImage.width = imageData.width;
          bgImage.height = imageData.height;

          // Calcular el tamaño final del canvas según generalSize si está definido
          let finalCanvasWidth = bgImage.width;
          let finalCanvasHeight = bgImage.height;
          let scaleX = 1;
          let scaleY = 1;

          if (generalSize && (generalSize.width !== null || generalSize.height !== null)) {
            if (generalSize.width !== null && generalSize.height !== null) {
              // Si ambos están definidos, usar esos valores directamente
              finalCanvasWidth = generalSize.width;
              finalCanvasHeight = generalSize.height;
            } else if (generalSize.width !== null) {
              // Si solo el ancho está definido, mantener la proporción
              scaleX = generalSize.width / bgImage.width;
              finalCanvasWidth = generalSize.width;
              finalCanvasHeight = bgImage.height * scaleX;
            } else if (generalSize.height !== null) {
              // Si solo la altura está definida, mantener la proporción
              scaleY = generalSize.height / bgImage.height;
              finalCanvasWidth = bgImage.width * scaleY;
              finalCanvasHeight = generalSize.height;
            }
          }

          canvas.width = finalCanvasWidth;
          canvas.height = finalCanvasHeight;
          
          // Dibujar la imagen de fondo escalada
          ctx.drawImage(bgImage, 0, 0, finalCanvasWidth, finalCanvasHeight);

          let flyerWidth: number;
          let flyerHeight: number;
          let tallImageScaleFactor = 1.0;
          
          // Si se proporciona un tamaño personalizado, usarlo directamente
          if (customSize) {
            flyerWidth = customSize.width;
            flyerHeight = customSize.height;
          } else if (platformData.Fix_width && platformData.Fix_height) {
            // Si existen Fix_width y Fix_height en la plataforma, usarlos
            flyerWidth = platformData.Fix_width;
            flyerHeight = platformData.Fix_height;
          } else {
            // Calcular tamaño basado en el condicional original
            const previewMaxWidth = 800;
            const previewDisplayWidth = Math.min(bgImage.width, previewMaxWidth);
            const previewDisplayHeight = (bgImage.height / bgImage.width) * previewDisplayWidth;
            
            const flyerWidthPercent = ((platformData.width || 200) / previewDisplayWidth) * 100;
            const flyerHeightPercent = ((platformData.height || 150) / previewDisplayHeight) * 100;
            
            flyerWidth = (flyerWidthPercent / 100) * bgImage.width;
            flyerHeight = (flyerHeightPercent / 100) * bgImage.height;
            
            const maxDimension = Math.max(bgImage.width, bgImage.height);
            const minDimension = Math.min(bgImage.width, bgImage.height);
            const differencePercent = (minDimension / maxDimension) * 100;
            
            if (bgImage.height > bgImage.width && differencePercent < 100) {
              if (differencePercent <= 40.18) {
                tallImageScaleFactor = 0.4;
              } else if (differencePercent <= 50) {
                tallImageScaleFactor = 0.4 + ((differencePercent - 40.18) / (50 - 40.18)) * (0.65 - 0.5);
              } else {
                tallImageScaleFactor = 0.677 + ((differencePercent - 50) / 50) * 0.35;
              }
              flyerWidth = flyerWidth * tallImageScaleFactor;
              flyerHeight = flyerHeight * tallImageScaleFactor;
            }
          }
          
          // Aplicar el escalado a las posiciones y tamaños del volante
          const flyerX = ((platformData.x || 50) / 100) * finalCanvasWidth;
          const flyerY = ((platformData.y || 50) / 100) * finalCanvasHeight;
          
          // Aplicar el escalado a los tamaños del volante
          if (generalSize && (generalSize.width !== null || generalSize.height !== null)) {
            if (generalSize.width !== null && generalSize.height !== null) {
              scaleX = finalCanvasWidth / bgImage.width;
              scaleY = finalCanvasHeight / bgImage.height;
            } else if (generalSize.width !== null) {
              scaleX = finalCanvasWidth / bgImage.width;
              scaleY = scaleX;
            } else if (generalSize.height !== null) {
              scaleY = finalCanvasHeight / bgImage.height;
              scaleX = scaleY;
            }
            flyerWidth = flyerWidth * scaleX;
            flyerHeight = flyerHeight * scaleY;
          }

          // Dibujar fondo del volante con bordes redondeados
          ctx.fillStyle = '#f5f5dc';
          ctx.strokeStyle = '#ff6b35'; // Naranja
          ctx.lineWidth = 2;
          const radius = 8;
          console.log('🎨 Drawing box with rounded corners - Color:', ctx.strokeStyle, 'Radius:', radius);
          
          // Usar roundRect si está disponible (método más moderno)
          if (typeof (ctx as any).roundRect === 'function') {
            ctx.beginPath();
            (ctx as any).roundRect(flyerX, flyerY, flyerWidth, flyerHeight, radius);
            ctx.fill();
            ctx.stroke();
          } else {
            // Método alternativo usando quadraticCurveTo para bordes redondeados (más compatible)
            ctx.beginPath();
            // Empezar desde el punto después de la esquina superior izquierda
            ctx.moveTo(flyerX + radius, flyerY);
            // Lado superior
            ctx.lineTo(flyerX + flyerWidth - radius, flyerY);
            // Esquina superior derecha
            ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY, flyerX + flyerWidth, flyerY + radius);
            // Lado derecho
            ctx.lineTo(flyerX + flyerWidth, flyerY + flyerHeight - radius);
            // Esquina inferior derecha
            ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY + flyerHeight, flyerX + flyerWidth - radius, flyerY + flyerHeight);
            // Lado inferior
            ctx.lineTo(flyerX + radius, flyerY + flyerHeight);
            // Esquina inferior izquierda
            ctx.quadraticCurveTo(flyerX, flyerY + flyerHeight, flyerX, flyerY + flyerHeight - radius);
            // Lado izquierdo
            ctx.lineTo(flyerX, flyerY + radius);
            // Esquina superior izquierda
            ctx.quadraticCurveTo(flyerX, flyerY, flyerX + radius, flyerY);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
          }

          // Cargar y dibujar logo
          const logoUrl = formData.media_assets.logo_url;
          console.log('[createCompositeImage] Logo URL desde formData:', logoUrl);
          if (logoUrl && logoUrl.trim() !== '') {
            try {
              // Normalizar URL del logo si es necesario
              let normalizedLogoUrl = logoUrl;
              if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
                normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
              }
              
              console.log('[createCompositeImage] Cargando logo desde:', normalizedLogoUrl);
              
              // Usar el proxy para cargar el logo (evita problemas de CORS)
              const proxyUrl = `${environment.apiBaseUrl}/proxy/image?url=${encodeURIComponent(normalizedLogoUrl)}`;
              console.log('[createCompositeImage] Cargando logo vía proxy:', proxyUrl);
              
              const logoResponse = await fetch(proxyUrl);
              if (!logoResponse.ok) {
                throw new Error(`Error al cargar logo: ${logoResponse.status} ${logoResponse.statusText}`);
              }
              
              const logoBlob = await logoResponse.blob();
              const logoUrlFromBlob = URL.createObjectURL(logoBlob);
              
              const logoImage = new Image();
              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  URL.revokeObjectURL(logoUrlFromBlob);
                  reject(new Error('Logo load timeout'));
                }, 30000);
                
                logoImage.onload = () => {
                  clearTimeout(timeout);
                  URL.revokeObjectURL(logoUrlFromBlob);
                  console.log('[createCompositeImage] Logo cargado correctamente. Dimensiones:', logoImage.width, 'x', logoImage.height);
                  resolve(null);
                };
                logoImage.onerror = (error) => {
                  clearTimeout(timeout);
                  URL.revokeObjectURL(logoUrlFromBlob);
                  console.error('[createCompositeImage] Error loading logo image from blob:', error);
                  reject(new Error(`Failed to load logo image from blob`));
                };
                logoImage.src = logoUrlFromBlob;
              });

              // Reducir el tamaño del logo (aplicar factor de escala de 0.7 para hacerlo más pequeño)
              const logoScaleFactor = 0.7;
              const maxLogoWidth = ((platformData.logo_width / 100) * flyerWidth) * logoScaleFactor;
              const maxLogoHeight = ((platformData.logo_height / 100) * flyerHeight) * logoScaleFactor;
              console.log('🖼️ Logo size reduced - Scale factor:', logoScaleFactor, 'Max size:', maxLogoWidth, 'x', maxLogoHeight);
              
              const imageAspectRatio = logoImage.width / logoImage.height;
              const maxAspectRatio = maxLogoWidth / maxLogoHeight;
              
              let logoWidth, logoHeight;
              if (imageAspectRatio > maxAspectRatio) {
                logoWidth = maxLogoWidth;
                logoHeight = maxLogoWidth / imageAspectRatio;
              } else {
                logoHeight = maxLogoHeight;
                logoWidth = maxLogoHeight * imageAspectRatio;
              }
              
              const logoX = flyerX + (flyerWidth - logoWidth) / 2;
              const logoY = flyerY + (distanciaXParam || platformData.distancia_x || 60); // Espacio desde la parte superior

              ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
              
              // Guardar la posición donde termina el logo para posicionar el texto justo debajo
              const logoBottom = logoY + logoHeight;
              
              // Dibujar texto del concurso justo debajo del logo con salto de línea automático
              const contestName = (selectedContest?.name || 'NOMBRE DEL CONCURSO').toUpperCase();
              const fontSize = (platformData.Text_size || 0.9) * 16 * tallImageScaleFactor;
              ctx.fillStyle = '#7c3aed';
              ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              // Espacio entre logo y primer texto
              const textMargin = Math.max(40, 40 * tallImageScaleFactor);
              let contestY = logoBottom + textMargin;
              
              // Función helper para dividir texto en líneas
              const drawMultilineText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number) => {
                const words = text.split(' ');
                const lines: string[] = [];
                let currentLine = words[0];
                
                for (let i = 1; i < words.length; i++) {
                  const word = words[i];
                  const testLine = currentLine + ' ' + word;
                  const metrics = ctx.measureText(testLine);
                  if (metrics.width > maxWidth && currentLine.length > 0) {
                    lines.push(currentLine);
                    currentLine = word;
                  } else {
                    currentLine = testLine;
                  }
                }
                lines.push(currentLine);
                
                // Dibujar cada línea
                lines.forEach((line, index) => {
                  ctx.fillText(line, x, y + (index * lineHeight));
                });
                
                return lines.length;
              };
              
              // Ancho disponible para el texto (con márgenes laterales)
              const textMaxWidth = flyerWidth * 0.9; // 90% del ancho del voleto
              const lineHeight = fontSize * 1.2;
              const lineCount = drawMultilineText(contestName, flyerX + flyerWidth / 2, contestY, textMaxWidth, lineHeight);
              contestY += (lineCount - 1) * lineHeight; // Ajustar posición Y si hay múltiples líneas

              // Dibujar texto personalizado debajo del nombre del concurso
              const customText = platformData.custom_text || 'your ad goes here';
              // Espacio entre los dos textos (aumentado)
              const customTextY = contestY + fontSize * 1.8;
              ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
            } catch (logoError) {
              console.error('Error loading logo:', logoError);
            }
          } else {
            // Si no hay logo, posicionar el texto desde arriba
            const contestName = (selectedContest?.name || 'NOMBRE DEL CONCURSO').toUpperCase();
            const fontSize = (platformData.Text_size || 0.9) * 16 * tallImageScaleFactor;
            ctx.fillStyle = '#7c3aed';
            ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            const contestY = flyerY + 20;
            ctx.fillText(contestName, flyerX + flyerWidth / 2, contestY);

            const customText = platformData.custom_text || 'your ad goes here';
            const customTextY = contestY + fontSize * 1.2;
            ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
          }

          return new Promise((resolve) => {
            canvas.toBlob((blob) => {
              if (!blob) {
                resolve(null);
                return;
              }
              blob.arrayBuffer().then((buffer) => {
                // Usar generalSize si está definido, sino usar el tamaño del canvas
                const finalWidth = (generalSize && generalSize.width !== null && generalSize.height !== null) 
                  ? generalSize.width 
                  : canvas.width;
                const finalHeight = (generalSize && generalSize.width !== null && generalSize.height !== null) 
                  ? generalSize.height 
                  : canvas.height;
                
                resolve({
                  data: new Uint8Array(buffer),
                  width: finalWidth,
                  height: finalHeight
                });
              });
            }, 'image/png');
          });
        } catch (error) {
          console.error('createCompositeImage: Error creating composite image:', error);
          console.error('createCompositeImage: Error details:', {
            platform: platformData.name,
            imageUrl: platformData.image || platformData.image_url,
            error: error instanceof Error ? error.message : String(error)
          });
          return null;
        }
      };

      // Preparar la plataforma con todos los datos necesarios
      let platformData = { ...platform };
      
      // Si es voletaje, buscar los datos de la plataforma base
      if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
        const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
        if (voletajeBase) {
          platformData = {
            ...voletajeBase,
            name: t('preview_modal_info_sponsorship.voletaje'),
            image: voletajeBase.image_url || voletajeBase.image,
            logo_url: voletajeBase.logo_url || formData.media_assets.logo_url,
            logo_width: voletajeBase.logo_width || 60,
            logo_height: voletajeBase.logo_height || 60,
            Image_form: voletajeBase.Image_form || 'circular',
            custom_text: voletajeBase.custom_text || 'your ad goes here',
            Text_size: voletajeBase.Text_size || 0.9,
            contest_text: voletajeBase.contest_text || 'Nombre del concurso'
          };
        }
      } else if (platform.isBase && platform.id) {
        // Si es plataforma base, obtener los datos completos
        const basePlatform = basePlatforms.find((bp: any) => bp.id === platform.id);
        if (basePlatform) {
          platformData = {
            ...basePlatform,
            image: basePlatform.image_url || basePlatform.image,
            logo_url: basePlatform.logo_url || formData.media_assets.logo_url,
            logo_width: basePlatform.logo_width || 60,
            logo_height: basePlatform.logo_height || 60,
            Image_form: basePlatform.Image_form || 'circular',
            custom_text: basePlatform.custom_text || 'your ad goes here',
            Text_size: basePlatform.Text_size || 0.9,
            contest_text: basePlatform.contest_text || 'Nombre del concurso'
          };
        }
      } else if (platform.isLegacy) {
        // Si es legacy, usar los datos del formulario
        platformData = {
          name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
          image: formData.custom_platform_image,
          x: formData.flyer_position?.x || 50,
          y: formData.flyer_position?.y || 50,
          width: formData.flyer_position?.width || 200,
          height: formData.flyer_position?.height || 150,
          logo_url: formData.media_assets.logo_url,
          logo_width: 60,
          logo_height: 60,
          Image_form: 'circular',
          custom_text: 'your ad goes here',
          Text_size: 0.9
        };
      }

      if (!platformData.image && !platformData.image_url) {
        console.error('generateAndDownloadWord: La plataforma no tiene imagen', platformData);
        void appDialog.alert('La plataforma no tiene imagen');
        return;
      }

      console.log('generateAndDownloadWord: Creating composite image for platform:', platformData.name, 'Image URL:', platformData.image || platformData.image_url);

      // Crear imagen compuesta
      const compositeImage = await createCompositeImage(platformData, customSize, distanciaX, generalSize);
      
      const children: any[] = [];
      
      // Título de la plataforma
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: platformData.name || 'Platform',
              size: 36,
              bold: true
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 400, after: 0, line: 240 }
        })
      );

      // Añadir imagen compuesta
      if (compositeImage) {
        console.log('generateAndDownloadWord: Composite image created successfully, size:', compositeImage.width, 'x', compositeImage.height);
        // Usar generalSize si está definido, sino usar el tamaño del compositeImage
        const imageWidth = (generalSize && generalSize.width !== null && generalSize.height !== null) 
          ? generalSize.width 
          : compositeImage.width;
        const imageHeight = (generalSize && generalSize.width !== null && generalSize.height !== null) 
          ? generalSize.height 
          : compositeImage.height;
        
        children.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: compositeImage.data,
                transformation: {
                  width: imageWidth,
                  height: imageHeight,
                },
                type: 'png',
              } as any),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 300 }
          })
        );
      } else {
        // Fallback: solo imagen de fondo usando la función helper
        console.log('generateAndDownloadWord: Composite image creation failed, trying fallback with loadImageForWord');
        try {
          const imageData = await loadImageForWord(platformData.image || platformData.image_url);
          
          if (imageData) {
            console.log('generateAndDownloadWord: Fallback image loaded successfully, size:', imageData.width, 'x', imageData.height);
            // Usar generalSize si está definido, sino usar el tamaño original de la imagen
            const imageWidth = (generalSize && generalSize.width !== null && generalSize.height !== null) 
              ? generalSize.width 
              : (imageData.width || 600);
            const imageHeight = (generalSize && generalSize.width !== null && generalSize.height !== null) 
              ? generalSize.height 
              : (imageData.height || 400);
            
            children.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imageData.data,
                    transformation: {
                      width: imageWidth,
                      height: imageHeight,
                    },
                    type: 'png',
                  } as any),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 }
              })
            );
          } else {
            console.error('generateAndDownloadWord: Failed to load fallback image');
            throw new Error('Failed to load image');
          }
        } catch (error) {
          console.error('generateAndDownloadWord: Error loading fallback image:', error);
          // No agregar mensaje de error al documento, simplemente omitir la imagen
        }
      }

      // Crear documento Word
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              size: {
                width: 15840, // 11 pulgadas
                height: 20160, // 14 pulgadas (Legal)
              },
              margin: {
                top: 720,
                right: 720,
                bottom: 720,
                left: 360,
              },
            },
          },
          children: children
        }]
      });

      // Generar y descargar
      const blob = await Packer.toBlob(doc);
      const platformName = (platformData.name || 'Platform').replace(/[^a-z0-9]/gi, '_');
      const fileName = `${platformName}_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, fileName);
      
      // Cerrar el diálogo
      setShowVoletoSizeDialog(false);
      setSelectedPlatformForWord(null);
      setVoletoSize(null);
    } catch (error) {
      console.error('Error downloading platform Word document:', error);
      void appDialog.alert('Error al generar el documento. Por favor, inténtalo de nuevo.');
    }
  };

  // Función helper para generar el contenido del documento de "view details"
  const generateContestDetailsContent = (contest: any, economics: any, sponsorship: any, dynamicMembershipBreakdown: any[]) => {
    const totalRevenue = Number(economics.total_revenue) || 0;
    const totalCosts = Number(economics.total_costs) || 0;
    
    // Usar tI18n que es la función reactiva del hook
    const t = tI18n;
    
    // Indentación base para todos los elementos (720 twips = 0.5 pulgadas)
    const baseIndent = 720;
    
    // Definir posición de tabulación (3600 twips = 2.5 pulgadas desde el margen izquierdo)
    const tabPosition = 3600;
    
    // Helper para crear párrafos con tabulación
    const createTabbedParagraph = (label: string, value: string, boldLabel: boolean = true) => {
      return new Paragraph({
        children: [
          new TextRun({ text: `${label}:\t`, bold: boldLabel, size: 28 }),
          new TextRun({ text: value, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      });
    };

    const content: any[] = [
      // Título
      new Paragraph({
        children: [
          new TextRun({
            text: `Economic Dashboard for the Contest "${contest.name}"`,
            size: 44,  // 22pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
        indent: { left: baseIndent }
      }),
      
      // Información General del Concurso
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.ContestConfiguration'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Status')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.status || 'N/A', size: 28 })
        ],
        indent: { left: baseIndent }, // Misma indentación para todos los textos
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Phase')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.phase?.replace(/_/g, ' ') || 'N/A', size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.AccessLevel')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.subscribers_only ? 'Subscribers Only' : contest.members_only ? 'Members Only' : 'Public', size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.PhotoRewardsMode')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.photo_rewards_mode || 'N/A', size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.PrizeType')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.prize_type || 'N/A', size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      
      // Línea divisoria antes de Rendimiento Económico
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Rendimiento Económico
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.economic_performance'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.total_revenue')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${totalRevenue.toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.participant')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(Number(economics.participant_revenue) || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Sponsor')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(Number(contest.total_sponsor_cost) || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.total_costs')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${totalCosts.toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Main_Prize')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(Number(economics.prize_costs) || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Secondary_Prize')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(Number(economics.ops_costs) || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.net_profit')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(Number(economics.noahs_ark_profit) || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Margin')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${(Number(economics.profit_margin) || 0).toFixed(1)}%`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `ROI:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${(Number(economics.total_roi) || 0).toFixed(1)}%`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Per_Participant')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(Number(economics.revenue_per_participant) || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      
      // Línea divisoria antes de Análisis de Ingresos
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Análisis de Ingresos
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.RevenueAnalysis'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.FullPriceRevenue')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(economics.full_price_revenue || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.DiscountedRevenue')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(economics.discounted_revenue || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.VoucherValueUsed')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(economics.voucher_redemption_value || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.TotalDiscountsGiven')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(economics.total_discount_given || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.RevenueEfficiency')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${(economics.revenue_efficiency_percentage || 0).toFixed(1)}%`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.PotentialRevenue')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${(economics.potential_revenue || 0).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      
      // Línea divisoria antes de Premios y Patrocinio
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Premios y Patrocinio
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.PrizeandSponsorship'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.PrizePoolBreakdown'),
            size: 32,  // 16pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 100 },
        indent: { left: baseIndent }
      }),
      ...(contest.prize_first_place > 0 ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Frist_Place')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${contest.prize_first_place.toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      ...(contest.prize_second_place > 0 ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Second_Place')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${contest.prize_second_place.toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      ...(contest.prize_third_place > 0 ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Third_Place')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${contest.prize_third_place.toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      ...(contest.prize_fourth_place > 0 ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Fourth_Place')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `$${contest.prize_fourth_place.toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      new Paragraph({
        children: [
          new TextRun({ text: 'Total:\t', bold: true, size: 28 }),
          new TextRun({ text: `$${((Number(contest.prize_first_place) || 0) + 
              (Number(contest.prize_second_place) || 0) + 
              (Number(contest.prize_third_place) || 0) + 
              (Number(contest.prize_fourth_place) || 0) + 
              (Number(contest.prize_fifth_place) || 0) + 
              (Number(contest.prize_sixth_place) || 0) + 
              (Number(contest.prize_seventh_place) || 0) + 
              (Number(contest.prize_eighth_place) || 0)).toFixed(2)}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      
      // Detalles de Patrocinio
      ...((sponsorship || contest.sponsor_name) ? [
        new Paragraph({
          children: [
            new TextRun({
              text: t('details_contest.Sponsorship_Details'),
              size: 32,  // 16pt
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
          indent: { left: baseIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.Sponsor')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: sponsorship?.sponsor_name || contest.sponsor_name || 'Noah\'s Ark', size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.type')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: sponsorship?.sponsorship_type?.replace(/_/g, ' ') || contest.sponsorship_type?.replace(/_/g, ' ') || 'N/A', size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.contribution')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: `$${(sponsorship?.total_sponsor_cost || contest.total_sponsor_cost || 0).toFixed(2)}`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        ...(sponsorship?.impressions > 0 ? [new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.impressions')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: sponsorship.impressions.toLocaleString(), size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        })] : []),
        ...(sponsorship?.engagements > 0 ? [new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.engagements')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: sponsorship.engagements.toLocaleString(), size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        })] : []),
        ...(sponsorship?.contact_name ? [new Paragraph({
          children: [
            new TextRun({ text: 'Contact:\t', bold: true, size: 28 }),
            new TextRun({ text: sponsorship.contact_name, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        })] : []),
        ...(sponsorship?.contact_email ? [new Paragraph({
          children: [
            new TextRun({ text: 'Email:\t', bold: true, size: 28 }),
            new TextRun({ text: sponsorship.contact_email, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        })] : [])
      ] : []),
      
      // Photo Rewards Split
      ...(contest.photo_rewards_mode !== 'disabled' ? [
        new Paragraph({
          children: [
            new TextRun({
              text: t('details_contest.PhotoRewardsSplit'),
              size: 32,  // 16pt
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 100 },
          indent: { left: baseIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.Photo_Rewards_User_Percentage')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: `${contest.photo_rewards_user_percentage || 0}%`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.Photo_Rewards_Organizer_Percentage')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: `${contest.photo_rewards_organizer_percentage || 0}%`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `${t('details_contest.Photo_Rewards_Noahs_Ark_Percentage')}:\t`, bold: true, size: 28 }),
            new TextRun({ text: `${contest.photo_rewards_noahs_ark_percentage || 0}%`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        })
      ] : []),
      
      // Línea divisoria antes de Cronograma
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Cronograma
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.ScheduleandTimeline'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.SubmissionPeriod')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${new Date(contest.submission_start).toLocaleDateString()} - ${new Date(contest.submission_deadline).toLocaleDateString()}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      ...(contest.voting_start ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.VotingPeriod')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${new Date(contest.voting_start).toLocaleDateString()} - ${new Date(contest.voting_end).toLocaleDateString()}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.WinnerAnnouncement')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: new Date(contest.winner_announcement).toLocaleDateString(), size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      
      // Línea divisoria antes de Requisitos de Medios
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Requisitos de Medios
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.MediaRequirements'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Allowed_Types')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.allowed_media_types || 'both', size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      ...(contest.allowed_media_types === 'image' || contest.allowed_media_types === 'both' ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Max_Images')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: String(contest.max_images || 0), size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      ...(contest.allowed_media_types === 'video' || contest.allowed_media_types === 'both' ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Max_Videos')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: String(contest.max_videos || 0), size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Imagesize_limit')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${contest.max_file_size || 0}MB`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Videosize_limit')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${contest.max_file_size || 0}MB`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Video_duration')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${contest.max_video_duration_seconds || 0}s`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      ...(contest.media_requirements ? [new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Media_Requirements')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: contest.media_requirements, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      })] : []),
      
      // Línea divisoria antes de Progreso de Participación
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Progreso de Participación
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.ParticipationProgress'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${t('details_contest.Current_Progress')}:\t`, bold: true, size: 28 }),
          new TextRun({ text: `${economics.total_pass_codes_used || 0} / ${contest.max_participants || contest.min_participants} ${t('details_contest.participants')}`, size: 28 })
        ],
        indent: { left: baseIndent },
        tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
      }),
      
      // Línea divisoria antes de Membership Breakdown
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            size: 6,
            style: BorderStyle.SINGLE
          }
        },
        spacing: { after: 300, before: 200 }
      }),
      
      // Membership Breakdown
      new Paragraph({
        children: [
          new TextRun({
            text: t('details_contest.MembershipAndPaymentBreakdown'),
            size: 36,  // 18pt
            bold: true
          })
        ],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 200 },
        indent: { left: baseIndent }
      }),
      ...(dynamicMembershipBreakdown.map((member: any) => [
        new Paragraph({
          children: [
            new TextRun({ text: `${member.membership_level || member.discount_type}:\t`, bold: true, size: 28 }),
            new TextRun({ text: `${member.participant_count || 0} participantes`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Ingresos:\t`, bold: true, size: 28 }),
            new TextRun({ text: `$${(member.total_revenue || 0).toFixed(2)}`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        }),
        new Paragraph({
          children: [
            new TextRun({ text: `Porcentaje:\t`, bold: true, size: 28 }),
            new TextRun({ text: `${(member.percentage_of_total || 0).toFixed(1)}%`, size: 28 })
          ],
          indent: { left: baseIndent },
          tabStops: [{ type: TabStopType.LEFT, position: tabPosition }]
        })
      ]).flat())
    ];

    return content;
  };

  // Función para descargar todas las plataformas en un solo documento Word
  const downloadAllPlatformsWord = async () => {
    try {
      const platforms = getOrderedPlatforms();
      if (platforms.length === 0) {
        void appDialog.alert('No hay plataformas para generar el documento.');
        return;
      }

      // Mostrar mensaje de progreso
      const progressMessage = `Generando documento Word para ${platforms.length} plataforma(s)...`;
      console.log(progressMessage);

      const sections: any[] = [];
      let hasContestDetails = false;

      // Función para formatear fechas
      const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('es-ES', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
        } catch {
          return dateString;
        }
      };

      // Cargar logo de Noah's Ark
      let noahsArkLogoData: Uint8Array | null = null;
      let noahsArkLogoWidth = 375; // 25px * 15 TWIPS/px
      let noahsArkLogoHeight = 375;
      
      try {
        const noahsArkLogoUrl = '/Noah.logo.color5.png';
        const noahsArkLogoResponse = await fetch(noahsArkLogoUrl);
        if (noahsArkLogoResponse.ok) {
          const arrayBuffer = await noahsArkLogoResponse.arrayBuffer();
          noahsArkLogoData = new Uint8Array(arrayBuffer);
          
          const img = new Image();
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              const maxDimensionPx = 25;
              let finalWidthPx = img.width;
              let finalHeightPx = img.height;
              
              if (finalWidthPx > maxDimensionPx || finalHeightPx > maxDimensionPx) {
                const scale = Math.min(maxDimensionPx / finalWidthPx, maxDimensionPx / finalHeightPx);
                finalWidthPx = finalWidthPx * scale;
                finalHeightPx = finalHeightPx * scale;
              }
              
              noahsArkLogoWidth = Math.round(finalWidthPx * 15);
              noahsArkLogoHeight = Math.round(finalHeightPx * 15);
              resolve();
            };
            img.onerror = reject;
            img.src = noahsArkLogoUrl;
          });
        }
      } catch (noahsArkLogoError) {
        console.error('Error loading Noah\'s Ark logo:', noahsArkLogoError);
      }

      // Cargar logo de la empresa
      let logoImageData: Uint8Array | null = null;
      let logoWidth = 2000;  // Ancho en TWIPS (default)
      let logoHeight = 2000; // Alto en TWIPS (default)
      
      if (formData.media_assets.logo_url) {
        try {
          // Normalizar URL del logo si es necesario
          let normalizedLogoUrl = formData.media_assets.logo_url;
          if (!normalizedLogoUrl.startsWith('http://') && !normalizedLogoUrl.startsWith('https://')) {
            normalizedLogoUrl = getCompleteImagePath(normalizedLogoUrl) || normalizedLogoUrl;
          }
          
          const logoResponse = await fetch(normalizedLogoUrl);
          const logoArrayBuffer = await logoResponse.arrayBuffer();
          logoImageData = new Uint8Array(logoArrayBuffer);
          
          // Cargar la imagen para obtener sus dimensiones reales
          const img = new Image();
          const blob = new Blob([logoArrayBuffer], { type: 'image/png' });
          const imgUrl = URL.createObjectURL(blob);
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imgUrl;
          });
          
          // Calcular dimensiones manteniendo proporción y limitando altura a 18.75px
          const maxHeightPx = 18.75;
          let finalWidthPx = img.width;
          let finalHeightPx = img.height;
          
          if (finalHeightPx > maxHeightPx) {
            const scale = maxHeightPx / finalHeightPx;
            finalWidthPx = finalWidthPx * scale;
            finalHeightPx = maxHeightPx;
          }
          
          // Convertir píxeles a TWIPS (1 píxel = 15 TWIPS a 96 DPI)
          logoWidth = Math.round(finalWidthPx * 15);
          logoHeight = Math.round(finalHeightPx * 15);
          
          URL.revokeObjectURL(imgUrl);
        } catch (logoError) {
          console.error('Error loading logo:', logoError);
        }
      }

      // PRIMERA PÁGINA: Título, Logo de Noah's Ark, y información básica del patrocinador
      const firstPageChildren: any[] = [
        // Salto de línea al inicio
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        // Logo de Noah's Ark en la parte superior
        ...(noahsArkLogoData ? [
          new Paragraph({
            children: [
              new ImageRun({
                data: noahsArkLogoData,
                transformation: {
                  width: noahsArkLogoWidth,
                  height: noahsArkLogoHeight,
                },
                type: 'png',
              } as any),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 400 }
          })
        ] : []),
        
        // Título principal
        new Paragraph({
          children: [
            new TextRun({
              text: `Contest Information - ${selectedContest?.name || 'Contest'}`,
              size: 44,
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 }
        }),
        
        // Nombre del patrocinador
        new Paragraph({
          children: [
            new TextRun({
              text: 'Sponsorship:',
              size: 32,
              bold: true
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: formData.sponsor_name || 'N/A',
              size: 32
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 }
        }),

        // Logo de la empresa
        ...(logoImageData ? [
          new Paragraph({
            children: [
              new ImageRun({
                data: logoImageData,
                transformation: {
                  width: logoWidth,
                  height: logoHeight,
                },
                type: 'png',
              } as any),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          })
        ] : [])
      ];

      sections.push({
        properties: {
          page: {
            size: {
              width: 15840, // 11 pulgadas
              height: 20160, // 14 pulgadas (Legal)
            },
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 1080,
            },
          },
        },
        children: firstPageChildren
      });

      // SEGUNDA PÁGINA: Tabla de contenidos (intercambiada con la tercera)
      const secondPageChildren: any[] = [
        // Salto de línea al inicio
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Table of Contents',
              size: 44,
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { before: 400, after: 600 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Basic Contest Data',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '3',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Economic Dashboard for the Contest',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Contest Configuration',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Economic Performance',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Revenue Analysis',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Prize and Sponsorship',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Schedule and Timeline',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Media Requirements',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Participation Progress',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Entry Management',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '4',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'Flyer Images',
              size: 28,
              bold: true
            }),
            new TextRun({
              text: ' ................................. ',
              size: 28
            }),
            new TextRun({
              text: '5',
              size: 28,
              bold: true
            })
          ],
          spacing: { before: 200, after: 100 }
        })
      ];

      sections.push({
        properties: {
          type: SectionType.NEXT_PAGE,
          page: {
            size: {
              width: 15840,
              height: 20160,
            },
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 1080,
            },
          },
        },
        children: secondPageChildren
      });

      // TERCERA PÁGINA: Información detallada del patrocinio (intercambiada con la segunda)
      // Aumentar el indentado para crear espacio desde el margen izquierdo
      const thirdPageIndent = 1440; // 1 pulgada (720 TWIPS = 0.5 pulgadas, 1440 = 1 pulgada)
      
      const thirdPageChildren: any[] = [
        // Salto de línea al inicio
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        // Título "Basic Contest Data"
        new Paragraph({
          children: [
            new TextRun({
              text: 'Basic Contest Data',
              size: 36,
              bold: true
            })
          ],
          heading: HeadingLevel.HEADING_2,
          alignment: AlignmentType.LEFT,
          spacing: { before: 400, after: 300 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [new TextRun({ text: '' })],
          spacing: { before: 0, after: 240 }
        }),
        // Social Links
        ...(formData.social_links ? [
          new Paragraph({
            children: [
              new TextRun({
                text: 'Social Media:',
                size: 28,
                bold: true
              })
            ],
            alignment: AlignmentType.LEFT,
            spacing: { before: 400, after: 100 },
            indent: { left: thirdPageIndent }
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: formData.social_links,
                size: 28
              })
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 300 },
            indent: { left: thirdPageIndent }
          })
        ] : []),

        // Fechas
        new Paragraph({
          children: [
            new TextRun({
              text: 'Start Date:',
              size: 28,
              bold: true
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { before: 200, after: 100 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: formatDate(formData.schedule.start),
              size: 28
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 200 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: 'End Date:',
              size: 28,
              bold: true
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { before: 200, after: 100 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: formatDate(formData.schedule.end),
              size: 28
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 300 },
          indent: { left: thirdPageIndent }
        }),

        // Costo Total
        new Paragraph({
          children: [
            new TextRun({
              text: 'Total Cost:',
              size: 32,
              bold: true
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { before: 200, after: 100 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `$${formData.total_sponsor_cost?.toFixed(2) || '0.00'}`,
              size: 32,
              bold: true,
              color: '2563EB' // Color azul
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 300 },
          indent: { left: thirdPageIndent }
        }),

        // Type of Sponsorship
        new Paragraph({
          children: [
            new TextRun({
              text: 'Type of Sponsorship:',
              size: 28,
              bold: true
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { before: 200, after: 100 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: formData.sponsorship_type?.replace(/_/g, ' ') || 'N/A',
              size: 28
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 300 },
          indent: { left: thirdPageIndent }
        }),

        // Entrance Type
        new Paragraph({
          children: [
            new TextRun({
              text: 'Entrance Type:',
              size: 28,
              bold: true
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { before: 200, after: 100 },
          indent: { left: thirdPageIndent }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: formData.entrance_type?.replace(/_/g, ' ') || 'N/A',
              size: 28
            })
          ],
          alignment: AlignmentType.LEFT,
          spacing: { after: 300 },
          indent: { left: thirdPageIndent }
        }),

        // Reward Contribution
        ...(formData.reward_contribution ? [
          new Paragraph({
            children: [
              new TextRun({
                text: 'Reward Contribution:',
                size: 28,
                bold: true
              })
            ],
            alignment: AlignmentType.LEFT,
            spacing: { before: 200, after: 100 },
            indent: { left: thirdPageIndent }
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `$${formData.reward_contribution.toFixed(2)}`,
                size: 28
              })
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 300 },
            indent: { left: thirdPageIndent }
          })
        ] : [])
      ];

      sections.push({
        properties: {
          type: SectionType.NEXT_PAGE,
          page: {
            size: {
              width: 15840,
              height: 20160,
            },
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 1080,
            },
          },
        },
        children: thirdPageChildren
      });

      // Determinar el ID del concurso (puede venir de selectedContest o existingSponsorship)
      let contestId = selectedContest?.id;
      let contestToUse = selectedContest;
      
      // Si no hay selectedContest pero hay existingSponsorship con contest_id, obtener el concurso
      if (!contestId && existingSponsorship?.contest_id) {
        contestId = existingSponsorship.contest_id;
        try {
          contestToUse = await contestService.getContestById(contestId);
          console.log('Concurso obtenido desde existingSponsorship:', contestToUse);
        } catch (error) {
          console.error('Error obteniendo concurso desde existingSponsorship:', error);
        }
      }

      // Si hay un concurso disponible, agregar la información de "view details" después de la primera página
      if (contestId && contestToUse) {
        try {
          console.log('Generando información del concurso para el documento Word...', { contestId });
          
          // Obtener datos económicos del concurso
          const economicsResponse = await contestService.getContestEconomics(contestId);
          const economicsData = economicsResponse.economics || {};
          const contestData = {
            ...contestToUse,
            ...economicsResponse.contest_info
          };

          console.log('Datos económicos obtenidos:', { economicsData, contestData });

          // Obtener patrocinio completo si existe
          let fullSponsorship = null;
          if (contestData?.sponsor_id || existingSponsorship?.id) {
            try {
              const sponsorshipId = contestData?.sponsor_id || existingSponsorship?.id;
              fullSponsorship = await sponsorshipService.getSponsorshipById(sponsorshipId);
              console.log('Patrocinio obtenido:', fullSponsorship);
            } catch (sponsorError) {
              console.error('Error loading sponsorship:', sponsorError);
              fullSponsorship = existingSponsorship || null;
            }
          }

          // Generar membership breakdown dinámico (similar a ContestDetailsModal)
          const entranceFee = contestData.entrance_fee || 0;
          const passCodesUsed = economicsData.total_pass_codes_used || 0;
          const couponpassesUsed = contestData.couponpasses_used || 0;
          const couponpassesAssigned = contestData.couponpasses_assigned ?? (contestData.max_coupass || 0);
          const maxAssigned = (contestData.max_pass ?? contestData.max_participants ?? contestData.min_participants) || 100;

          const dynamicMembershipBreakdown: any[] = [
            {
              membership_key: 'voucherFreePass',
              discount_type: 'Couponpasses',
              membership_level: '',
              participant_count: couponpassesUsed,
              actual_paid: 0,
              total_revenue: 0,
              percentage_of_total: couponpassesAssigned > 0 ? (couponpassesUsed / couponpassesAssigned) * 100 : 0
            },
            {
              membership_key: 'membersFullPrice',
              participant_count: passCodesUsed,
              actual_paid: entranceFee,
              total_revenue: passCodesUsed * entranceFee,
              percentage_of_total: maxAssigned > 0 ? (passCodesUsed / maxAssigned) * 100 : 0
            }
          ];

          console.log('Generando contenido de view details...', { dynamicMembershipBreakdown });

          // Generar contenido de "view details"
          const contestDetailsContent = generateContestDetailsContent(
            contestData,
            economicsData,
            fullSponsorship,
            dynamicMembershipBreakdown
          );

          console.log('Contenido de view details generado:', contestDetailsContent.length, 'elementos');

          // Agregar dos saltos de línea al inicio del contenido económico (página 4 - Economic Dashboard)
          const contestDetailsContentWithBreak = [
            new Paragraph({
              children: [new TextRun({ text: '' })],
              spacing: { before: 0, after: 240 }
            }),
            new Paragraph({
              children: [new TextRun({ text: '' })],
              spacing: { before: 0, after: 240 }
            }),
            new Paragraph({
              children: [new TextRun({ text: '' })],
              spacing: { before: 0, after: 240 }
            }),
            ...contestDetailsContent
          ];
          

          // Agregar el contenido de "view details" como una nueva sección
          sections.push({
            properties: {
              type: SectionType.NEXT_PAGE,
              page: {
                size: {
                  width: 15840,
                  height: 20160,
                },
                margin: {
                  top: 720,
                  right: 720,
                  bottom: 720,
                  left: 1080,
                },
              },
            },
            children: contestDetailsContentWithBreak
          });
          hasContestDetails = true;
          console.log('Información del concurso agregada exitosamente al documento');
        } catch (error) {
          console.error('Error loading contest details:', error);
          void appDialog.alert(`Error al cargar información del concurso: ${error instanceof Error ? error.message : 'Error desconocido'}`);
          // Continuar sin la información del concurso si hay error
        }
      } else {
        console.log('No hay concurso seleccionado, solo se generarán las plataformas');
      }

      // Procesar cada plataforma
      for (let i = 0; i < platforms.length; i++) {
        const platform = platforms[i];
        
        // Preparar datos de la plataforma para generar Word (igual que generateWordDocSimple)
        let platformData = { ...platform };
        
        // Si es voletaje, buscar los datos de la plataforma base
        if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
          const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
          if (voletajeBase) {
            platformData = {
              ...voletajeBase,
              name: t('preview_modal_info_sponsorship.voletaje'),
              image: voletajeBase.image_url || voletajeBase.image,
              logo_url: voletajeBase.logo_url || formData.media_assets.logo_url,
              custom_text: voletajeBase.custom_text || 'your ad goes here'
            };
          }
        } else if (platform.isBase && platform.id) {
          // Si es plataforma base, obtener los datos completos
          const basePlatform = basePlatforms.find((bp: any) => bp.id === platform.id);
          if (basePlatform) {
            platformData = {
              ...basePlatform,
              image: basePlatform.image_url || basePlatform.image,
              logo_url: basePlatform.logo_url || formData.media_assets.logo_url,
              custom_text: basePlatform.custom_text || 'your ad goes here'
            };
          }
        } else if (platform.isLegacy) {
          // Si es legacy, usar los datos del formulario
          platformData = {
            name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
            image: formData.custom_platform_image,
            logo_url: formData.media_assets.logo_url,
            custom_text: 'your ad goes here'
          };
        }

        if (!platformData.image && !platformData.image_url) {
          console.warn(`La plataforma ${platformData.name || 'sin nombre'} no tiene imagen, omitiéndola.`);
          continue;
        }

        // Obtener URLs normalizadas
        const imageUrl = platformData.image || platformData.image_url;
        let normalizedImageUrl = imageUrl;
        if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
          normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
        }

        const logoUrl = platformData.logo_url || formData.media_assets.logo_url;
        let normalizedLogoUrl = logoUrl;
        if (logoUrl && logoUrl.trim() !== '' && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
          normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
        }

        const customText = platformData.custom_text || 'your ad goes here';
        const contestName = selectedContest?.name || 'CONTEST NAME';
        const platformName = platformData.name || 'default';

        // Log para debugging
        if (logoUrl && logoUrl.trim() !== '') {
          console.log(`[Word Generation] Logo URL para plataforma ${platformName}:`, {
            original: logoUrl,
            normalized: normalizedLogoUrl,
            fromPlatform: !!platformData.logo_url,
            fromFormData: !!formData.media_assets.logo_url
          });
        } else {
          console.warn(`[Word Generation] No se encontró logoUrl para plataforma ${platformName}`, {
            platformDataLogoUrl: platformData.logo_url,
            formDataLogoUrl: formData.media_assets.logo_url
          });
        }

        // Llamar al endpoint del servidor para obtener la imagen compuesta
        // Usa el mismo endpoint que el botón Word individual, pero solo obtenemos la imagen
        const response = await fetch(`${environment.apiBaseUrl}/generar-preview-word`, {
          method: 'POST',
          headers: getApiHeaders(),
          body: JSON.stringify({ 
            imageUrl: normalizedImageUrl,
            logoUrl: (normalizedLogoUrl && normalizedLogoUrl.trim() !== '') ? normalizedLogoUrl : null,
            customText: customText || null,
            contestName: contestName || null,
            platformName: platformName
          }),
        });

        if (!response.ok) {
          let errorMessage = 'Error desconocido';
          let errorDetails = '';
          try {
            const errorBody = await response.json();
            errorMessage = errorBody.error || errorBody.message || `Error ${response.status}`;
            errorDetails = errorBody.details || '';
          } catch (e) {
            // Si no se puede parsear como JSON, usar el status text
            errorMessage = response.status === 404 
              ? `Ruta de API no encontrada (404): /api/generar-preview-word`
              : `Error ${response.status}: ${response.statusText}`;
          }
          
          console.error(`Error generando imagen para plataforma ${platformName}:`, errorMessage, errorDetails ? `- ${errorDetails}` : '');
          continue;
        }

        // Obtener la imagen como blob
        const imageBlob = await response.blob();
        const imageArrayBuffer = await imageBlob.arrayBuffer();
        const imageData = new Uint8Array(imageArrayBuffer);

        // Obtener dimensiones de la imagen
        const img = new Image();
        const imageUrlFromBlob = URL.createObjectURL(imageBlob);
        await new Promise((resolve, reject) => {
          img.onload = () => {
            URL.revokeObjectURL(imageUrlFromBlob);
            resolve(null);
          };
          img.onerror = () => {
            URL.revokeObjectURL(imageUrlFromBlob);
            reject(new Error('Failed to load image'));
          };
          img.src = imageUrlFromBlob;
        });

        const imageWidth = img.width;
        const imageHeight = img.height;

        // Agregar plataforma como una nueva sección (cada plataforma en su propia página)
        const platformSpacing = i === 0 ? 400 : 800;
        sections.push({
          properties: {
            type: SectionType.NEXT_PAGE,
            page: {
              size: {
                width: 15840,
                height: 20160,
              },
              margin: {
                top: 720,
                right: 720,
                bottom: 720,
                left: 1080,
              },
            },
          },
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: platformName,
                  size: 36,
                  bold: true
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: platformSpacing, after: 400, line: 240 }
            }),
            new Paragraph({
              children: [
                new ImageRun({
                  data: imageData,
                  transformation: {
                    width: imageWidth,
                    height: imageHeight,
                  },
                  type: 'png',
                } as any),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 300 }
            })
          ]
        });
      }

      // Crear documento Word con todas las secciones
      const doc = new Document({
        sections: sections
      });

      // Generar y descargar
      const blob = await Packer.toBlob(doc);
      const contestName = selectedContest?.name?.replace(/[^a-z0-9]/gi, '_') || 'Contest';
      const fileName = `All_Platforms_${contestName}_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, fileName);
      
      console.log(`Documento Word generado exitosamente con ${platforms.length} plataforma(s).`);
    } catch (error) {
      console.error('Error downloading all platforms Word document:', error);
      void appDialog.alert('Error al generar el documento. Por favor, inténtalo de nuevo.');
    }
  };

  // Función para obtener el valor del tier seleccionado
  const getTierValue = (tier?: 'gold' | 'silver' | 'bronze'): number => {
    switch (tier) {
      case 'gold':
        return 1500;
      case 'silver':
        return 1000;
      case 'bronze':
        return 500;
      default:
        return 0;
    }
  };

  // Función para mapear datos del patrocinio existente al formato del formulario
  const mapExistingSponsorshipToFormData = (sponsorship: any): SponsorshipPayload => {
    // Parsear custom_platforms si viene como string JSON
    let parsedPlatforms = [];
    if (sponsorship.custom_platforms) {
      if (typeof sponsorship.custom_platforms === 'string') {
        try {
          parsedPlatforms = JSON.parse(sponsorship.custom_platforms);
        } catch (e) {
          console.error('Error parseando custom_platforms:', e);
          parsedPlatforms = [];
        }
      } else if (Array.isArray(sponsorship.custom_platforms)) {
        parsedPlatforms = sponsorship.custom_platforms;
      }
    }
    
    return {
      sponsor_name: sponsorship.sponsor_name || "",
      sponsor_short_name: sponsorship.sponsor_short_name || "",
      industry: sponsorship.industry || "",
      contact_name: sponsorship.contact_name || "",
      contact_email: sponsorship.contact_email || "",
      sponsorship_type: sponsorship.sponsorship_type || "rewards_only",
      sponsor_tier: sponsorship.sponsor_tier || undefined,
      ambassador_id: sponsorship.ambassador_id || undefined,
      reward_contribution: Number(sponsorship.reward_contribution) || 250,
      entrance_coverage_percentage: Number(sponsorship.entrance_coverage_percentage) || 0,
      total_sponsor_cost: Number(sponsorship.total_sponsor_cost) || 250,
      impressions: Number(sponsorship.impressions) || 0,
      engagements: Number(sponsorship.engagements) || 0,
      placements: {
        header_logo: sponsorship.header_logo || true,
        listing_badge: sponsorship.listing_badge || true,
        modal_banner: sponsorship.modal_banner || false,
        email_mention: sponsorship.email_mention || true,
        social_posts: Number(sponsorship.social_posts) || 1,
      },
      media_assets: {
        logo_url: sponsorship.logo_url || "",
        banner_url: sponsorship.banner_url || "",
        brand_guidelines_url: sponsorship.brand_guidelines_url || "",
        landing_url: sponsorship.landing_url || "",
        slogan: sponsorship.slogan || "",
        website_url: sponsorship.website_url || "",
      },
      schedule: {
        start: sponsorship.start_date || "",
        end: sponsorship.end_date || "",
      },
      notes: sponsorship.notes || "",
      social_links: sponsorship.social_links || "",
      custom_platform_image: sponsorship.custom_platform_image || "",
      custom_platform_name: sponsorship.custom_platform_name || "",
      custom_platforms: parsedPlatforms,
      flyer_position: {
        x: sponsorship.flyer_position_x || 50,
        y: sponsorship.flyer_position_y || 50,
        width: sponsorship.flyer_width || 200,
        height: sponsorship.flyer_height || 150,
      },
      enabled_platforms: sponsorship.enabled_platforms || {
        facebook: false,
        instagram: false,
        voletaje: true,
      },
      enabled_base_platforms: sponsorship.enabled_base_platforms || [],
      platform_order: sponsorship.platform_order || [],
      entrance_type: sponsorship.sponsorship_type === 'full_entrance' ? 'full_entrance' :
                     sponsorship.sponsorship_type === 'partial_entrance' ? 'partial_entrance' : null,
    };
  };

  const [formData, setFormData] = useState<SponsorshipPayload>(() => {
    // Si hay un patrocinio existente, usar sus datos
    if (existingSponsorship) {
      return mapExistingSponsorshipToFormData(existingSponsorship);
    }
    
    // Si no, usar los datos del concurso seleccionado
    return {
    sponsor_name: "",
    sponsor_short_name: "",
    industry: "",
    contact_name: "",
    contact_email: "",
    sponsorship_type: "rewards_only",
    sponsor_tier: undefined,
    ambassador_id: undefined,
    reward_contribution: Number(selectedContest?.main_prize) || 250,
    entrance_coverage_percentage: 0,
    total_sponsor_cost: (Number(selectedContest?.main_prize) || 250) + getTierValue(undefined), // Se calculará automáticamente cuando cambien los valores

    impressions: 0,
    engagements: 0,

    placements: {
      header_logo: true,
      listing_badge: true,
      modal_banner: false,
      email_mention: true,
      social_posts: 1,
    },

    media_assets: {
      logo_url: "",
      banner_url: "",
      brand_guidelines_url: "",
      landing_url: "",
      slogan: selectedContest?.description || "",
      website_url: "",
    },

    schedule: {
      start: selectedContest?.start_date || "",
      end: selectedContest?.end_date || "",
    },

    notes: "",
    social_links: "",
    custom_platform_image: "",
    custom_platform_name: "",
    custom_platforms: [],
    flyer_position: {
      x: 50,
      y: 50,
      width: 200,
      height: 150,
    },
    enabled_platforms: {
      facebook: false,
      instagram: false,
      voletaje: true,
    },
    enabled_base_platforms: [],
    entrance_type: null,
    };
  });
  
  // Función para obtener plataformas por defecto según el tipo de patrocinio
  const getDefaultPlatformsForType = (type: SponsorshipType) => {
    const defaults: Record<SponsorshipType, { facebook: boolean; instagram: boolean; voletaje: boolean }> = {
      rewards_only: {
        facebook: false,
        instagram: false,
        voletaje: true,
      },
      branding_only: {
        facebook: false,
        instagram: false,
        voletaje: true,
      },
      full_entrance: {
        facebook: false,
        instagram: false,
        voletaje: true,
      },
      partial_entrance: {
        facebook: false,
        instagram: false,
        voletaje: true,
      },
      custom: {
        facebook: false,
        instagram: false,
        voletaje: true,
      },
    };
    return defaults[type];
  };
  
  // Estado para rastrear el tipo de patrocinio anterior
  const [previousSponsorshipType, setPreviousSponsorshipType] = useState<SponsorshipType | null>(formData.sponsorship_type || null);
  
  // Aplicar plataformas por defecto cuando cambia el tipo de patrocinio
  useEffect(() => {
    if (formData.sponsorship_type && !existingSponsorship) {
      // Si es la primera vez o si cambió el tipo
      if (previousSponsorshipType === null || previousSponsorshipType !== formData.sponsorship_type) {
        const defaultPlatforms = getDefaultPlatformsForType(formData.sponsorship_type);
        // Solo actualizar si no tiene enabled_platforms o si el tipo cambió
        if (!formData.enabled_platforms || previousSponsorshipType !== formData.sponsorship_type) {
          update("enabled_platforms", defaultPlatforms);
        }
      }
      setPreviousSponsorshipType(formData.sponsorship_type);
    }
  }, [formData.sponsorship_type, existingSponsorship]);
  
  // Cargar plataformas base al montar el componente
  useEffect(() => {
    const loadBasePlatforms = async () => {
      setLoadingBasePlatforms(true);
      try {
        const response = await fetch(`${environment.apiBaseUrl}/platforms`, {
          headers: getApiHeadersForGet()
        });
        if (response.ok) {
          const platforms = await response.json();
          // Filtrar solo las plataformas habilitadas y excluir la plataforma de configuración
          setBasePlatforms(platforms.filter((p: any) => p.enabled && p.id !== 'sponsorship_types_config'));
        }
      } catch (error) {
        console.error('Error loading base platforms:', error);
      } finally {
        setLoadingBasePlatforms(false);
      }
    };
    
    loadBasePlatforms();
  }, []);

  // Cargar embajadores al montar el componente
  useEffect(() => {
    const loadAmbassadors = async () => {
      setLoadingAmbassadors(true);
      try {
        const response = await fetch(`${environment.apiBaseUrl}/ambassadors`, {
          headers: getApiHeadersForGet()
        });
        if (response.ok) {
          const ambassadorsData = await response.json();
          setAmbassadors(ambassadorsData);
        }
      } catch (error) {
        console.error('Error loading ambassadors:', error);
      } finally {
        setLoadingAmbassadors(false);
      }
    };
    
    loadAmbassadors();
  }, []);

  // Cargar tipos de patrocinio configurados
  useEffect(() => {
    let cancelled = false;

    const loadSponsorshipTypes = async () => {
      setLoadingSponsorshipTypesConfig(true);
      try {
        const types = await fetchSponsorshipTypes(environment.apiBaseUrl, getApiHeadersForGet(), tI18n);
        if (!cancelled) {
          setSponsorshipTypesConfig(types);
        }
      } catch (error) {
        console.error('Error loading sponsorship types:', error);
        if (!cancelled) {
          setSponsorshipTypesConfig(getAvailableSponsorshipTypes([], false, tI18n));
        }
      } finally {
        if (!cancelled) {
          setLoadingSponsorshipTypesConfig(false);
        }
      }
    };
    
    loadSponsorshipTypes();
    return () => {
      cancelled = true;
    };
  }, [tI18n, locale]);

  // Sincronizar enabled_platforms con visiblePreviews cuando cambia enabled_platforms
  useEffect(() => {
    if (formData.enabled_platforms) {
      setVisiblePreviews(formData.enabled_platforms);
    }
  }, [formData.enabled_platforms?.facebook, formData.enabled_platforms?.instagram, formData.enabled_platforms?.voletaje]);

  // Sincronizar enabled_base_platforms con visibleBasePlatforms
  useEffect(() => {
    const enabledIds = formData.enabled_base_platforms || [];
    const newVisibleBasePlatforms: Record<string, boolean> = {};
    enabledIds.forEach((id) => {
      // Si la plataforma está habilitada, establecer como visible por defecto si no está en el estado
      if (!(id in visibleBasePlatforms)) {
        newVisibleBasePlatforms[id] = true;
      } else {
        newVisibleBasePlatforms[id] = visibleBasePlatforms[id];
      }
    });
    // Limpiar plataformas que ya no están habilitadas
    Object.keys(visibleBasePlatforms).forEach((id) => {
      if (enabledIds.includes(id)) {
        newVisibleBasePlatforms[id] = visibleBasePlatforms[id];
      }
    });
    if (Object.keys(newVisibleBasePlatforms).length !== Object.keys(visibleBasePlatforms).length || 
        Object.keys(newVisibleBasePlatforms).some(id => newVisibleBasePlatforms[id] !== visibleBasePlatforms[id])) {
      setVisibleBasePlatforms(newVisibleBasePlatforms);
    }
  }, [formData.enabled_base_platforms]);

  // Resetear platformOrder cuando cambian las plataformas para usar el orden de la base de datos
  useEffect(() => {
    // Resetear platformOrder cuando cambian las plataformas base, habilitadas o visibles
    // Esto asegura que siempre se use el orden de la base de datos (platform_order si existe)
    if (basePlatforms.length > 0 && !loadingBasePlatforms) {
      // Usar la función helper para construir el orden según platform_order de la BD
      const platforms = buildPlatformsFromDBOrder();
      
      // Actualizar platformOrder con el orden de la base de datos
      // Esto se hace para sincronizar platformOrder con el orden actual de la BD
      const newOrder = platforms.map((p, idx) => getPlatformId(p, idx));
      
      // Solo actualizar si el orden es diferente para evitar loops infinitos
      setPlatformOrder(prevOrder => {
        const orderChanged = prevOrder.length !== newOrder.length || 
          !prevOrder.every((id, idx) => id === newOrder[idx]);
        
        if (orderChanged) {
          console.log('🔄 Reset platformOrder con orden de BD:', newOrder);
          return newOrder;
        }
        return prevOrder;
      });
    }
  }, [
    basePlatforms.length, 
    formData.enabled_base_platforms?.length, 
    Object.keys(visibleBasePlatforms).length, 
    visiblePreviews.voletaje, 
    formData.custom_platforms?.length, 
    formData.custom_platform_image, 
    formData.platform_order?.length,
    loadingBasePlatforms,
    // Estas dependencias se usan dentro del efecto pero no se incluyen para evitar loops
    // basePlatforms, formData.enabled_base_platforms, visibleBasePlatforms, formData.custom_platforms
  ]);

  // Actualizar datos del formulario cuando se seleccione un concurso
  useEffect(() => {
    if (selectedContest) {
      setFormData(prev => {
        const newRewardContribution = Number(selectedContest.main_prize) || prev.reward_contribution || 0;
        const tierValue = getTierValue(prev.sponsor_tier);
        return {
          ...prev,
          sponsor_name: prev.sponsor_name, // No cambiar el nombre del patrocinador
          reward_contribution: newRewardContribution,
          total_sponsor_cost: newRewardContribution + tierValue,
          media_assets: {
            ...prev.media_assets,
            slogan: selectedContest.description || prev.media_assets.slogan,
          },
          schedule: {
            start: selectedContest.start_date || prev.schedule.start,
            end: selectedContest.end_date || prev.schedule.end,
          },
        };
      });
    }
  }, [selectedContest]);

  const update = <K extends keyof SponsorshipPayload>(
    key: K,
    value: SponsorshipPayload[K]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key as string]) {
      setErrors((e) => ({ ...e, [key as string]: null }));
    }
  };

  const handleFileSelect = (field: string, file: File) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => ({ ...prev, [field]: e.target?.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = (step = currentStep) => {
    const e: Record<string, string> = {};

    if (step === 1) {
      if (!formData.sponsor_name.trim())
        e.sponsor_name = t('basicinfosponsorship.sponsor_name_required');
    }

    if (step === 3) {
      if (!formData.schedule.start) e["schedule.start"] = "Fecha inicio requerida";
      if (!formData.schedule.end) e["schedule.end"] = "Fecha fin requerida";

      if (formData.schedule.start && formData.schedule.end) {
        if (new Date(formData.schedule.end) <= new Date(formData.schedule.start))
          e["schedule.end"] = "La fecha fin debe ser posterior al inicio";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validate()) {
      setCurrentStep((s) => Math.min(s + 1, steps.length));
      // Scroll al inicio del formulario (útil cuando está dentro de un modal con scroll)
      requestAnimationFrame(() => {
        formScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };
  const prev = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    // Scroll al inicio del formulario (útil cuando está dentro de un modal con scroll)
    requestAnimationFrame(() => {
      formScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const submit = () => {
    if (!validate(3)) {
      setCurrentStep(3);
      return;
    }
    
    // Obtener todas las plataformas disponibles (sin ordenar)
    const allPlatforms: any[] = [];
    
    // Voletaje
    if (visiblePreviews.voletaje) {
      allPlatforms.push({
        id: 'voletaje',
        name: t('preview_modal_info_sponsorship.voletaje'),
        type: 'voletaje',
        isBase: true,
        platform_type: 'voletaje'
      });
    }
    
    // Plataformas base
    const enabledBasePlatforms = basePlatforms
      .filter(platform => (formData.enabled_base_platforms || []).includes(platform.id))
      .filter(platform => visibleBasePlatforms[platform.id])
      .map((platform) => ({
        ...platform,
        isBase: true,
        id: platform.id
      }));
    allPlatforms.push(...enabledBasePlatforms);
    
    // Plataformas personalizadas
    const customPlatforms = getFilteredCustomPlatforms().map((plat, idx) => ({
      ...plat,
      isBase: false
    }));
    allPlatforms.push(...customPlatforms);
    
    // Si hay una plataforma personalizada antigua (formato legacy)
    if (formData.custom_platform_image && !getFilteredCustomPlatforms().length) {
      allPlatforms.push({
        id: 'custom-legacy',
        name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
        type: 'custom',
        isBase: false,
        isLegacy: true
      });
    }
    
    // Crear un mapa de platformId -> platform para búsqueda rápida
    const platformMap = new Map();
    allPlatforms.forEach((platform, idx) => {
      const platformId = getPlatformId(platform, idx);
      platformMap.set(platformId, platform);
    });
    
    // Usar el estado platformOrder actual para generar el array platform_order
    // Si platformOrder está vacío o no coincide con el número de plataformas, usar el orden por defecto
    console.log('💾 submit - Estado platformOrder actual:', platformOrder);
    console.log('💾 submit - Número de plataformas:', allPlatforms.length);
    const currentOrder = platformOrder.length > 0 && platformOrder.length === allPlatforms.length 
      ? platformOrder 
      : allPlatforms.map((p, idx) => getPlatformId(p, idx));
    console.log('💾 submit - Orden que se usará para guardar:', currentOrder);
    
    // Generar el array platform_order basado en el orden actual
    const platformOrderArray = currentOrder.map((platformId, index) => {
      const platform = platformMap.get(platformId);
      if (!platform) {
        // Si la plataforma no se encuentra, usar valores por defecto
        return {
          name: 'Unknown',
          position: index + 1,
          platform_id: platformId,
          type: 'unknown'
        };
      }
      
      // Obtener el nombre de la plataforma
      let platformName = '';
      if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
        platformName = t('preview_modal_info_sponsorship.voletaje');
      } else if (platform.isBase && platform.id) {
        const basePlatform = basePlatforms.find(bp => bp.id === platform.id);
        platformName = basePlatform ? basePlatform.name : platform.name || 'Unknown';
      } else {
        platformName = platform.name || customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform');
      }
      
      return {
        name: platformName,
        position: index + 1,
        platform_id: platformId,
        type: platform.type || platform.platform_type || (platform.isBase ? 'base' : 'custom')
      };
    });
    
    // Agregar platform_order al formData
    // Si hay un patrocinador existente, incluir su ID para que se pueda identificar como actualización
    const formDataWithOrder = {
      ...formData,
      platform_order: platformOrderArray,
      // Incluir el ID del patrocinador existente si existe
      ...(existingSponsorship?.id ? { id: existingSponsorship.id } : {})
    };
    
    console.log('💾 submit - platform_order que se guardará:', platformOrderArray);
    console.log('💾 submit - formDataWithOrder completo:', formDataWithOrder);
    console.log('💾 submit - existingSponsorship?.id:', existingSponsorship?.id);
    
    // Llamar a onCreated para pasar los datos al dashboard (el dashboard decide el siguiente paso)
    if (onCreated) {
      onCreated(formDataWithOrder);
    }
  };

  // Función para cargar patrocinadores
  const loadSponsors = async () => {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/sponsorships`, {
        headers: getApiHeadersForGet()
      });
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers.get('content-type'));
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          console.log('Loaded sponsors:', data);
          setSponsors(data);
        } else {
          console.error('Response is not JSON:', contentType);
          const text = await response.text();
          console.error('Response text:', text.substring(0, 200));
          setSponsors([]);
        }
      } else {
        console.error('Failed to load sponsors:', response.status, response.statusText);
        const text = await response.text();
        console.error('Error response:', text.substring(0, 200));
        setSponsors([]);
      }
    } catch (error) {
      console.error('Error loading sponsors:', error);
      setSponsors([]);
    }
  };

  // Función para seleccionar un patrocinador existente
  const selectSponsor = (sponsor: any) => {
    const mappedData = mapExistingSponsorshipToFormData(sponsor);
    setFormData(mappedData);
    setShowSponsorList(false);
    setShowSponsorSelection(true); // Cambiar a true para mostrar el formulario
  };

  // Función para crear nuevo patrocinador
  const createNewSponsor = () => {
    console.log('Creating new sponsor');
    setShowSponsorSelection(true); // Cambiar a true para mostrar el formulario
  };

  // Función para mostrar lista de patrocinadores
  const showSponsorListModal = () => {
    loadSponsors();
    setShowSponsorList(true);
  };

  // Calcular el costo total automáticamente: Reward Contribution + Tier value
  const calculatedTotalCost = (formData.reward_contribution || 0) + getTierValue(formData.sponsor_tier);

  const calcHint = calculatedTotalCost;


  const renderStepIndicator = () => (
    <Box sx={{ mb: 4 }}>
      <Stepper activeStep={currentStep - 1} alternativeLabel>
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;

          return (
            <Step key={step.id}>
              <StepLabel
                onClick={() => setCurrentStep(step.id)}
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                      color: completed || active ? 'white' : 'grey.600',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <StepIcon />
                  </Box>
                )}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
              >
                <Typography variant="caption" sx={{ fontWeight: isActive ? 600 : 400 }}>
                  {step.name}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );

  // Mostrar interfaz de selección inicial si no hay datos existentes
  if (!existingSponsorship && !selectedContest && !showSponsorSelection && !showSponsorList) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          p: 2,
          pt: 1
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: 'center',
              minHeight: '100vh'
            }}
          >
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
              {t('create_sponsorship_form.create_sponsorship')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              {t('create_sponsorship_form.how_do_you_want_to_proceed')}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={createNewSponsor}
                sx={{
                  backgroundColor: '#ff6b35',
                  '&:hover': {
                    backgroundColor: '#e55a2b'
                  },
                  px: 4,
                  py: 2
                }}
              >
                {t('create_sponsorship_form.new_sponsor')}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={showSponsorListModal}
                sx={{
                  borderColor: '#4caf50',
                  color: '#4caf50',
                  '&:hover': {
                    borderColor: '#388e3c',
                    backgroundColor: '#f1f8e9'
                  },
                  px: 4,
                  py: 2
                }}
              >
                {t('create_sponsorship_form.select_sponsor')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      ref={formScrollRef}
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: 2,
        pt: 1
      }}
    >
      <Container maxWidth="lg" sx={{ minHeight: '100vh' }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh - 32px)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
              {existingSponsorship ? 'Edit Sponsorship' : t('create_sponsorship_form.create_sponsorship')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {existingSponsorship ? 'Update the sponsorship details below' : t('create_sponsorship_form.create_sponsorship_description')}
            </Typography>
          </Box>

          {renderStepIndicator()}

          <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4, minHeight: '50vh', paddingBottom: 20 }}>
            {currentStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert
                  severity="info"
                  icon={<BuildingIcon />}
                  sx={{
                    backgroundColor: '#e3f2fd',
                    '& .MuiAlert-icon': { color: '#1976d2' }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {t('basicinfosponsorship.sponsor_information')}
                  </Typography>
                </Alert>

                <TextField
                  fullWidth
                  label={t('basicinfosponsorship.sponsor_name')}
                  value={formData.sponsor_name}
                  onChange={(e) => update("sponsor_name", e.target.value)}
                  placeholder="Ej: PetCorp"
                  variant="outlined"
                  error={!!errors.sponsor_name}
                  helperText={errors.sponsor_name}
                />

                <TextField
                  fullWidth
                  label={t('basicinfosponsorship.sponsor_short_name')}
                  value={formData.sponsor_short_name}
                  onChange={(e) => update("sponsor_short_name", e.target.value)}
                  placeholder="Ej: PC"
                  variant="outlined"
                />

                <TextField
                  fullWidth
                  label={t('basicinfosponsorship.industry')}
                  value={formData.industry}
                  onChange={(e) => update("industry", e.target.value)}
                  placeholder="Ej: Tecnología"
                  variant="outlined"
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('basicinfosponsorship.contact_name')}
                    value={formData.contact_name}
                    onChange={(e) => update("contact_name", e.target.value)}
                    placeholder="Nombre del contacto"
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => update("contact_email", e.target.value)}
                    placeholder="contacto@empresa.com"
                    variant="outlined"
                  />
                </Box>
              </Box>
            )}

            {currentStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert
                  severity="info"
                  icon={<DollarIcon />}
                  sx={{
                    backgroundColor: '#e8f5e8',
                    '& .MuiAlert-icon': { color: '#2e7d32' }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    {t('type_and_budget_info_sponsorship.type_and_budget')}
                  </Typography>
                </Alert>

                <Autocomplete
                  fullWidth
                  options={(() => {
                    // Función para verificar si el término de búsqueda es una subsecuencia del texto
                    const isSubsequence = (searchTerm: string, text: string): boolean => {
                      if (!text || !searchTerm) return false;
                      searchTerm = searchTerm.toLowerCase().trim();
                      text = text.toLowerCase().trim();
                      if (searchTerm.length === 0) return true;
                      if (searchTerm.length > text.length) return false;
                      
                      let searchIndex = 0;
                      for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                        if (text[i] === searchTerm[searchIndex]) {
                          searchIndex++;
                        }
                      }
                      return searchIndex === searchTerm.length;
                    };
                    
                    // Obtener lista de tipos disponibles
                    const availableTypes = loadingSponsorshipTypesConfig 
                      ? []
                      : sponsorshipTypesConfig.length > 0
                      ? sponsorshipTypesConfig
                      : [
                          { value: 'rewards_only', name: t('type_and_budget_info_sponsorship.rewards_only') },
                          { value: 'branding_only', name: t('type_and_budget_info_sponsorship.branding_only') },
                          { value: 'custom', name: t('type_and_budget_info_sponsorship.custom') }
                        ];
                    
                    return availableTypes.map((type: any) => ({
                      ...type,
                      label: type.name || (
                        type.value === 'rewards_only' ? t('type_and_budget_info_sponsorship.rewards_only') :
                        type.value === 'branding_only' ? t('type_and_budget_info_sponsorship.branding_only') :
                        type.value === 'custom' ? t('type_and_budget_info_sponsorship.custom') :
                        type.value === 'full_entrance' ? t('type_and_budget_info_sponsorship.full_entrance') :
                        type.value === 'partial_entrance' ? t('type_and_budget_info_sponsorship.partial_entrance') :
                        type.name || type.value
                      )
                    }));
                  })()}
                  value={(() => {
                    const currentValue = formData.sponsorship_type === "full_entrance" || formData.sponsorship_type === "partial_entrance"
                      ? sponsorshipTypesConfig.length > 0 ? sponsorshipTypesConfig[0].value : "rewards_only"
                      : formData.sponsorship_type || "";
                    
                    const availableTypes = loadingSponsorshipTypesConfig 
                      ? []
                      : sponsorshipTypesConfig.length > 0
                      ? sponsorshipTypesConfig
                      : [
                          { value: 'rewards_only', name: t('type_and_budget_info_sponsorship.rewards_only') },
                          { value: 'branding_only', name: t('type_and_budget_info_sponsorship.branding_only') },
                          { value: 'custom', name: t('type_and_budget_info_sponsorship.custom') }
                        ];
                    
                    const foundType = availableTypes.find((type: any) => type.value === currentValue);
                    if (!foundType) return null;
                    
                    return {
                      ...foundType,
                      label: foundType.name || (
                        foundType.value === 'rewards_only' ? t('type_and_budget_info_sponsorship.rewards_only') :
                        foundType.value === 'branding_only' ? t('type_and_budget_info_sponsorship.branding_only') :
                        foundType.value === 'custom' ? t('type_and_budget_info_sponsorship.custom') :
                        foundType.value === 'full_entrance' ? t('type_and_budget_info_sponsorship.full_entrance') :
                        foundType.value === 'partial_entrance' ? t('type_and_budget_info_sponsorship.partial_entrance') :
                        foundType.name || foundType.value
                      )
                    };
                  })()}
                  onChange={(event, newValue: any) => {
                    if (!newValue) return;
                    
                    const selectedValue = newValue.value;
                    const selectedTypeConfig = sponsorshipTypesConfig.find((t: any) => t.value === selectedValue);
                    
                    // Determinar el tipo base basado en el valor seleccionado
                    let newType: SponsorshipType;
                    let enabledPlatforms = {
                      facebook: false,
                      instagram: false,
                      voletaje: false
                    };
                    let basePlatformIds: string[] = [];
                    
                    if (selectedTypeConfig) {
                      // Aplicar las plataformas configuradas para este tipo
                      enabledPlatforms = {
                        facebook: false,
                        instagram: false,
                        voletaje: selectedTypeConfig.platforms?.includes('voletaje') || false
                      };
                      
                      newType = selectedTypeConfig.value as SponsorshipType;
                      
                      // Habilitar plataformas base configuradas
                      basePlatformIds = selectedTypeConfig.platforms?.filter((p: string) => 
                        p !== 'facebook' && p !== 'instagram' && p !== 'voletaje'
                      ) || [];
                    } else {
                      // Si no se encuentra en la configuración, usar valores por defecto según el valor
                      if (selectedValue === "rewards_only" || selectedValue === "branding_only" || selectedValue === "custom") {
                        newType = selectedValue as SponsorshipType;
                        // Aplicar valores por defecto
                        if (newType === "rewards_only") {
                          enabledPlatforms = { facebook: false, instagram: false, voletaje: true };
                        } else if (newType === "branding_only") {
                          enabledPlatforms = { facebook: false, instagram: false, voletaje: true };
                        } else if (newType === "custom") {
                          enabledPlatforms = { facebook: false, instagram: false, voletaje: true };
                        }
                      } else {
                        // Si es un valor desconocido, establecerlo de todos modos
                        newType = selectedValue as SponsorshipType;
                      }
                    }
                    
                    // Actualizar el estado
                    update("entrance_type", null);
                    update("sponsorship_type", newType);
                    update("enabled_platforms", enabledPlatforms);
                    update("enabled_base_platforms", basePlatformIds);
                    
                    if (newType === "custom") {
                      setBaseSponsorshipType(null);
                    } else if (newType === "rewards_only" || newType === "branding_only") {
                      setBaseSponsorshipType(newType);
                    }
                  }}
                  loading={loadingSponsorshipTypesConfig}
                  filterOptions={(options, params) => {
                    // Función para verificar si el término de búsqueda es una subsecuencia del texto
                    const isSubsequence = (searchTerm: string, text: string): boolean => {
                      if (!text || !searchTerm) return false;
                      searchTerm = searchTerm.toLowerCase().trim();
                      text = text.toLowerCase().trim();
                      if (searchTerm.length === 0) return true;
                      if (searchTerm.length > text.length) return false;
                      
                      let searchIndex = 0;
                      for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                        if (text[i] === searchTerm[searchIndex]) {
                          searchIndex++;
                        }
                      }
                      return searchIndex === searchTerm.length;
                    };
                    
                    const filtered = options.filter((option: any) => {
                      const searchTerm = params.inputValue.trim();
                      if (!searchTerm) return true;
                      
                      return isSubsequence(searchTerm, option.label || '');
                    });
                    
                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('type_and_budget_info_sponsorship.type_and_budget_description')}
                      variant="outlined"
                      placeholder="Buscar tipo de patrocinio..."
                    />
                  )}
                  getOptionLabel={(option: any) => option.label || option.value || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  noOptionsText="No se encontraron tipos que coincidan con la búsqueda"
                  loadingText={t('platforms.loading')}
                />

                {/* Selector de Tipo de Entrada - Solo visible cuando NO es "solo premio" (rewards_only) */}
                {formData.sponsorship_type && formData.sponsorship_type !== "rewards_only" && (
                  <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <InputLabel 
                        id="entrance-type-label"
                        shrink={true}
                      >
                        {t('type_and_budget_info_sponsorship.entrance_type')}
                      </InputLabel>
                      <Tooltip
                        title={t('type_and_budget_info_sponsorship.entrance_type_tooltip')}
                        arrow
                        placement="top"
                      >
                        <IconButton size="small" sx={{ p: 0.5 }}>
                          <InfoIcon fontSize="small" color="action" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Select
                      labelId="entrance-type-label"
                      value={
                        formData.sponsorship_type === "full_entrance" 
                          ? "full_entrance" 
                          : formData.sponsorship_type === "partial_entrance"
                          ? "partial_entrance"
                          : (formData.entrance_type || "full_entrance")
                      }
                      onChange={(e) => {
                        const value = e.target.value as string;
                        const entranceType = value as "full_entrance" | "partial_entrance";
                        update("entrance_type", entranceType);
                        
                        // Determinar el tipo base (Premio o Branding)
                        const currentBaseType = baseSponsorshipType || 
                          (formData.sponsorship_type === "full_entrance" || formData.sponsorship_type === "partial_entrance"
                            ? "rewards_only" // Si viene de una entrada y no hay base guardada, asumir Premio
                            : formData.sponsorship_type === "branding_only"
                            ? "branding_only"
                            : "rewards_only");
                        
                        // Actualizar sponsorship_type y guardar el tipo base
                        if (entranceType === "full_entrance") {
                          if (!baseSponsorshipType) {
                            setBaseSponsorshipType(currentBaseType);
                          }
                          update("sponsorship_type", "full_entrance");
                        } else if (entranceType === "partial_entrance") {
                          if (!baseSponsorshipType) {
                            setBaseSponsorshipType(currentBaseType);
                          }
                          update("sponsorship_type", "partial_entrance");
                        }
                      }}
                      label={t('type_and_budget_info_sponsorship.entrance_type')}
                    >
                      <MenuItem value="full_entrance">{t('type_and_budget_info_sponsorship.full_entrance')}</MenuItem>
                      <MenuItem value="partial_entrance">{t('type_and_budget_info_sponsorship.partial_entrance')}</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* Sección de Sponsor Tag Tiers con checkbox */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: tiersSectionActive ? 2 : 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tiersSectionActive}
                          onChange={(e) => setTiersSectionActive(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {t('type_and_budget_info_sponsorship.tiers')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('type_and_budget_info_sponsorship.optional')}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                  
                  {tiersSectionActive && (
                    <FormControl fullWidth variant="outlined">
                      <FormLabel 
                        id="sponsor-tier-label"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {t('type_and_budget_info_sponsorship.sponsorTagTiers')}
                        <Tooltip
                          title={t('type_and_budget_info_sponsorship.tiersTooltip')}
                          arrow
                          placement="top"
                        >
                          <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                            <InfoIcon fontSize="small" sx={{ color: '#1976d2' }} />
                          </IconButton>
                        </Tooltip>
                      </FormLabel>
                      <Select
                        labelId="sponsor-tier-label"
                        value={formData.sponsor_tier || 'none'}
                        onChange={(e) => {
                          const value = e.target.value as 'gold' | 'silver' | 'bronze' | 'none';
                          const newTier = value === 'none' ? undefined : value;
                          update("sponsor_tier", newTier);
                          // Actualizar total_cost automáticamente
                          if (!existingSponsorship) {
                            update("total_sponsor_cost", (formData.reward_contribution || 0) + getTierValue(newTier));
                          }
                        }}
                        native
                        variant="outlined"
                      >
                        <option value="none">{t('type_and_budget_info_sponsorship.none')}</option>
                        <option value="gold">{t('type_and_budget_info_sponsorship.goldTier')}</option>
                        <option value="silver">{t('type_and_budget_info_sponsorship.silverTier')}</option>
                        <option value="bronze">{t('type_and_budget_info_sponsorship.bronzeTier')}</option>
                      </Select>
                      <FormHelperText>{t('type_and_budget_info_sponsorship.sponsorTagTiersHelper')}</FormHelperText>
                    </FormControl>
                  )}
                </Box>

                {/* Campo de Ambassador ID */}
                <Autocomplete
                  fullWidth
                  options={(() => {
                    // Agregar opción "Ninguno" al inicio de la lista
                    const noneLabel = t('type_and_budget_info_sponsorship.none');
                    const noneOption = { id: 'none', name: noneLabel, email: '', label: noneLabel };
                    const ambassadorOptions = ambassadors.map((ambassador: any) => ({
                      ...ambassador,
                      label: `${ambassador.name} (${ambassador.email})`
                    }));
                    return [noneOption, ...ambassadorOptions];
                  })()}
                  value={(() => {
                    if (!formData.ambassador_id) {
                      const noneLabel = t('type_and_budget_info_sponsorship.none');
                      return { id: 'none', name: noneLabel, email: '', label: noneLabel };
                    }
                    const foundAmbassador = ambassadors.find((amb: any) => amb.id === formData.ambassador_id);
                    if (!foundAmbassador) return null;
                    return {
                      ...foundAmbassador,
                      label: `${foundAmbassador.name} (${foundAmbassador.email})`
                    };
                  })()}
                  onChange={(event, newValue: any) => {
                    if (!newValue || newValue.id === 'none') {
                      update("ambassador_id", undefined);
                    } else {
                      update("ambassador_id", newValue.id);
                    }
                  }}
                  loading={loadingAmbassadors}
                  filterOptions={(options, params) => {
                    // Función para verificar si el término de búsqueda es una subsecuencia del texto
                    const isSubsequence = (searchTerm: string, text: string): boolean => {
                      if (!text || !searchTerm) return false;
                      searchTerm = searchTerm.toLowerCase().trim();
                      text = text.toLowerCase().trim();
                      if (searchTerm.length === 0) return true;
                      if (searchTerm.length > text.length) return false;
                      
                      let searchIndex = 0;
                      for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                        if (text[i] === searchTerm[searchIndex]) {
                          searchIndex++;
                        }
                      }
                      return searchIndex === searchTerm.length;
                    };
                    
                    const filtered = options.filter((option: any) => {
                      const searchTerm = params.inputValue.trim();
                      if (!searchTerm) return true;
                      
                      // Buscar en nombre, email y en la label completa
                      const nameMatch = isSubsequence(searchTerm, option.name || '');
                      const emailMatch = isSubsequence(searchTerm, option.email || '');
                      const labelMatch = isSubsequence(searchTerm, option.label || '');
                      
                      return nameMatch || emailMatch || labelMatch;
                    });
                    
                    return filtered;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('type_and_budget_info_sponsorship.assignedAmbassador')}
                      variant="outlined"
                      placeholder={t('type_and_budget_info_sponsorship.searchAmbassadorPlaceholder')}
                      helperText={t('type_and_budget_info_sponsorship.assignedAmbassadorHelper')}
                      sx={{ mt: 2 }}
                    />
                  )}
                  getOptionLabel={(option: any) => option.label || option.name || option.value || ''}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText={t('type_and_budget_info_sponsorship.noAmbassadorsFound')}
                  loadingText={t('type_and_budget_info_sponsorship.loadingAmbassadors')}
                />

                {/* Configuración de Plataformas - Solo visible para tipos que no sean entrada */}
                {(formData.sponsorship_type !== "full_entrance" && formData.sponsorship_type !== "partial_entrance") && (
                <Card sx={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', mt: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {t('type_and_budget_info_sponsorship.platform_configuration')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('type_and_budget_info_sponsorship.platform_configuration_description')}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${(formData.enabled_platforms?.voletaje ? 1 : 0) + (formData.enabled_base_platforms?.length || 0)} ${t('type_and_budget_info_sponsorship.active_platforms_count')}`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                      {/* Voletaje */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 32, height: 32, backgroundColor: '#ff6b35', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>V</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {t('preview_modal_info_sponsorship.voletaje')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formData.enabled_platforms?.voletaje ? t('type_and_budget_info_sponsorship.platform_enabled') : t('type_and_budget_info_sponsorship.platform_disabled')}
                            </Typography>
                          </Box>
                        </Box>
                        <Switch
                          checked={formData.enabled_platforms?.voletaje || false}
                          onChange={(e) => {
                            update("enabled_platforms", {
                              facebook: false,
                              instagram: false,
                              voletaje: e.target.checked
                            });
                          }}
                          color="primary"
                        />
                      </Box>

                      {/* Plataformas Base Dinámicas */}
                      {loadingBasePlatforms ? (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('platforms.loading')}
                          </Typography>
                        </Box>
                      ) : (
                        basePlatforms.map((basePlatform) => (
                          <Box key={basePlatform.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ width: 32, height: 32, backgroundColor: '#7c3aed', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {basePlatform.image ? (
                                  <OptimizedImage
                                    src={basePlatform.image}
                                    alt={basePlatform.name}
                                    sx={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }}
                                  />
                                ) : (
                                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>P</Typography>
                                )}
                              </Box>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {basePlatform.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {(formData.enabled_base_platforms || []).includes(basePlatform.id) 
                                    ? t('type_and_budget_info_sponsorship.platform_enabled') 
                                    : t('type_and_budget_info_sponsorship.platform_disabled')}
                                </Typography>
                              </Box>
                            </Box>
                            <Switch
                              checked={(formData.enabled_base_platforms || []).includes(basePlatform.id)}
                              onChange={(e) => {
                                const currentEnabled = formData.enabled_base_platforms || [];
                                const newEnabled = e.target.checked
                                  ? [...currentEnabled, basePlatform.id]
                                  : currentEnabled.filter(id => id !== basePlatform.id);
                                update("enabled_base_platforms", newEnabled);
                              }}
                              color="primary"
                            />
                          </Box>
                        ))
                      )}
                    </Box>

                    {/* Botón para aplicar plataformas por defecto */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const defaultPlatforms = getDefaultPlatformsForType(formData.sponsorship_type);
                        update("enabled_platforms", defaultPlatforms);
                      }}
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          backgroundColor: '#e3f2fd'
                        }
                      }}
                    >
                      {t('type_and_budget_info_sponsorship.apply_default_platforms')}
                    </Button>
                  </CardContent>
                </Card>
                )}

                {(formData.sponsorship_type === "rewards_only" ||
                  formData.sponsorship_type === "custom" ||
                  formData.sponsorship_type === "branding_only") && (
                  <TextField
                    fullWidth
                    label={t('type_and_budget_info_sponsorship.reward_contribution')}
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    value={formData.reward_contribution ?? 0}
                    onChange={(e) => {
                      const newValue = parseFloat(e.target.value) || 0;
                      update("reward_contribution", newValue);
                      // Actualizar total_cost automáticamente
                      update("total_sponsor_cost", newValue + getTierValue(formData.sponsor_tier));
                    }}
                    variant="outlined"
                    error={!!errors.reward_contribution}
                    helperText={errors.reward_contribution}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={t('type_and_budget_info_sponsorship.reward_contribution_tooltip')}
                            arrow
                            placement="top"
                          >
                            <IconButton size="small" edge="end">
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {(formData.sponsorship_type === "partial_entrance" ||
                  formData.sponsorship_type === "full_entrance") && (
                  <TextField
                    fullWidth
                    label="% Cobertura de Entradas"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    value={formData.entrance_coverage_percentage ?? 0}
                    onChange={(e) => update("entrance_coverage_percentage", parseFloat(e.target.value) || 0)}
                    variant="outlined"
                    error={!!errors.entrance_coverage_percentage}
                    helperText={errors.entrance_coverage_percentage}
                  />
                )}

                {!existingSponsorship && (
                  <TextField
                    fullWidth
                    label={t('type_and_budget_info_sponsorship.total_cost')}
                    type="number"
                    inputProps={{ step: 0.01, min: 0, readOnly: true }}
                    value={calculatedTotalCost}
                    variant="outlined"
                    error={!!errors.total_sponsor_cost}
                    helperText={errors.total_sponsor_cost}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed',
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={t('type_and_budget_info_sponsorship.total_cost_tooltip')}
                            arrow
                            placement="top"
                          >
                            <IconButton size="small" edge="end" disabled>
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('type_and_budget_info_sponsorship.impressions_meta')}
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.impressions ?? 0}
                    onChange={(e) => update("impressions", parseInt(e.target.value) || 0)}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={t('type_and_budget_info_sponsorship.impressions_meta_tooltip')}
                            arrow
                            placement="top"
                          >
                            <IconButton size="small" edge="end">
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('type_and_budget_info_sponsorship.engagements_meta')}
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.engagements ?? 0}
                    onChange={(e) => update("engagements", parseInt(e.target.value) || 0)}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={t('type_and_budget_info_sponsorship.engagements_meta_tooltip')}
                            arrow
                            placement="top"
                          >
                            <IconButton size="small" edge="end">
                              <InfoIcon fontSize="small" color="action" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Card sx={{ backgroundColor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('type_and_budget_info_sponsorship.quick_summary')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${calcHint.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {currentStep === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert
                  severity="info"
                  icon={<MegaphoneIcon />}
                  sx={{
                    backgroundColor: '#fff3e0',
                    '& .MuiAlert-icon': { color: '#f57c00' }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#f57c00' }}>
                    {t('assets_and_visibility_info_sponsorship.assets_and_visibility')}
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Card sx={{ width: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon />
                        {t('assets_and_visibility_info_sponsorship.brand_assets')}
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label={t('assets_and_visibility_info_sponsorship.logo_url')}
                        value={formData.media_assets.logo_url || ''}
                        onChange={(e) => update("media_assets", { ...formData.media_assets, logo_url: e.target.value })}
                        placeholder="URL del logo"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      
                      <TextField
                        fullWidth
                        label={t('assets_and_visibility_info_sponsorship.banner_url')}
                        value={formData.media_assets.banner_url || ''}
                        onChange={(e) => update("media_assets", { ...formData.media_assets, banner_url: e.target.value })}
                        placeholder="URL del banner"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      
                      <TextField
                        fullWidth
                        label={t('assets_and_visibility_info_sponsorship.brand_guidelines_url')}
                        value={formData.media_assets.brand_guidelines_url || ''}
                        onChange={(e) => update("media_assets", { ...formData.media_assets, brand_guidelines_url: e.target.value })}
                        placeholder="URL de la guía de marca"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      
                      <TextField
                        fullWidth
                        label={t('assets_and_visibility_info_sponsorship.landing_url')}
                        value={formData.media_assets.landing_url || ''}
                        onChange={(e) => update("media_assets", { ...formData.media_assets, landing_url: e.target.value })}
                        placeholder="URL de landing"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      
                      <TextField
                        fullWidth
                        label={t('assets_and_visibility_info_sponsorship.slogan')}
                        value={formData.media_assets.slogan || ''}
                        onChange={(e) => update("media_assets", { ...formData.media_assets, slogan: e.target.value })}
                        placeholder="Ej: Innovación para el futuro"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      
                      <TextField
                        fullWidth
                        label={t('assets_and_visibility_info_sponsorship.website_url')}
                        type="url"
                        value={formData.media_assets.website_url || ''}
                        onChange={(e) => update("media_assets", { ...formData.media_assets, website_url: e.target.value })}
                        placeholder="https://www.ejemplo.com"
                        variant="outlined"
                      />
                    </CardContent>
                  </Card>

                  <Card sx={{ width: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon />
                        {t('assets_and_visibility_info_sponsorship.social_links')}
                      </Typography>
                      
                      <TextField
                        fullWidth
                        label="Social Links"
                        value={formData.social_links || ''}
                        onChange={(e) => update("social_links", e.target.value)}
                        placeholder="https://facebook.com/empresa, https://twitter.com/empresa"
                        variant="outlined"
                        multiline
                        rows={3}
                      />
                    </CardContent>
                  </Card>
                </Box>

                <TextField
                  fullWidth
                  label={t('assets_and_visibility_info_sponsorship.notes')}
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Lineamientos especiales, copy sugerido, etc."
                  variant="outlined"
                />

              </Box>
            )}

            {currentStep === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Alert
                  severity="info"
                  icon={<CalendarIcon />}
                  sx={{
                    backgroundColor: '#f3e5f5',
                    '& .MuiAlert-icon': { color: '#7b1fa2' }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                    {t('dates_and_summary_info_sponsorship.dates_and_summary')}
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('dates_and_summary_info_sponsorship.start_date')}
                    type="datetime-local"
                    value={formData.schedule.start}
                    onChange={(e) => update("schedule", { ...formData.schedule, start: e.target.value })}
                    variant="outlined"
                    error={!!errors["schedule.start"]}
                    helperText={errors["schedule.start"]}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label={t('dates_and_summary_info_sponsorship.end_date')}
                    type="datetime-local"
                    value={formData.schedule.end}
                    onChange={(e) => update("schedule", { ...formData.schedule, end: e.target.value })}
                    variant="outlined"
                    error={!!errors["schedule.end"]}
                    helperText={errors["schedule.end"]}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Card sx={{ backgroundColor: '#e3f2fd' }}>
                  <CardContent>
                    <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                      {t('dates_and_summary_info_sponsorship.total_cost')}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      ${(formData.total_sponsor_cost || 0).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('dates_and_summary_info_sponsorship.sponsor')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {formData.sponsor_name || "—"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formData.contact_name || t('dates_and_summary_info_sponsorship.no_contact')} · {formData.contact_email || "—"}
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('dates_and_summary_info_sponsorship.type_and_meta')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                        {formData.sponsorship_type.replace("_", " ")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('dates_and_summary_info_sponsorship.rewards')}: ${formData.reward_contribution?.toFixed(2) || "0.00"} · {t('dates_and_summary_info_sponsorship.entrace_coverage')}: {formData.entrance_coverage_percentage ?? 0}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowPreviewModal(true)}
                    startIcon={<EyeIcon />}
                    sx={{ 
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  >
                    {t('preview_modal_info_sponsorship.previewButton')}
                  </Button>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Box 
              ref={buttonsContainerRef}
              sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              position: 'sticky',
              bottom: -25,
              backgroundColor: 'white',
              paddingTop: 4,
              paddingBottom: 4,
              paddingLeft: 4,
              paddingRight: 4,
              zIndex: 1000,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 2,
              boxShadow: isButtonsAtBottom ? '0 -2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
              marginTop: 12,
              mb: 0
            }}>
              <Button
                onClick={prev}
                disabled={currentStep === 1}
                variant="contained"
                color="success"
                sx={{
                  minWidth: 120,
                  opacity: currentStep === 1 ? 0.5 : 1,
                  marginLeft: 2
                }}
              >
                {t('contestformbuttons.previous')}
              </Button>

              {currentStep < steps.length ? (
                <Button
                  onClick={next}
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: 120, marginRight: 2 }}
                >
                  {t('contestformbuttons.next')}
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                  sx={{ minWidth: 160, marginRight: 2 }}
                >
                  {existingSponsorship ? 'Edit Sponsorship' : t('create_sponsorship_form.create_sponsorship')}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Preview Modal */}
      <Dialog
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewPlatformsSearchTerm('');
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EyeIcon />
            {t('preview_modal_info_sponsorship.preview')}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Botón Download Contest Information - Comentado */}
            {/* <Button
              variant="contained"
              startIcon={<FileTextIcon />}
              onClick={async () => {
                try {
                  // Obtener datos del concurso si está disponible
                  let contestData: any = null;
                  let economicsData: any = null;
                  let fullSponsorship: any = null;
                  let dynamicMembershipBreakdown: any[] = [];
                  
                  if (selectedContest?.id) {
                    try {
                      // Obtener datos económicos del concurso
                      const economicsResponse = await contestService.getContestEconomics(selectedContest.id);
                      economicsData = economicsResponse.economics || {};
                      contestData = {
                        ...selectedContest,
                        ...economicsResponse.contest_info
                      };

                      // Obtener patrocinio completo si existe
                      if (contestData?.sponsor_id || existingSponsorship?.id) {
                        try {
                          const sponsorshipId = contestData?.sponsor_id || existingSponsorship?.id;
                          fullSponsorship = await sponsorshipService.getSponsorshipById(sponsorshipId);
                        } catch (sponsorError) {
                          console.error('Error loading sponsorship:', sponsorError);
                          fullSponsorship = existingSponsorship || null;
                        }
                      }

                      // Generar membership breakdown dinámico
                      const generateDynamicMembershipBreakdown = () => {
                        const entranceFee = contestData.entrance_fee || 0;
                        const totalParticipants = contestData.current_participants || 0;
                        const passCodesUsed = economicsData.total_pass_codes_used || 0;
                        
                        const couponpassesUsed = contestData.couponpasses_used || 0;
                        const couponpassesAssigned = contestData.couponpasses_assigned ?? (contestData.max_coupass || 0);
                        const couponpassesProgress = couponpassesAssigned > 0 ? (couponpassesUsed / couponpassesAssigned) * 100 : 0;

                        const originalBreakdown = contestData.membership_breakdown || [];
                        
                        const updatedBreakdown = originalBreakdown.map((memberType: any) => {
                          if (memberType.membership_key === 'voucherFreePass') {
                            return {
                              ...memberType,
                              discount_type: 'Couponpasses',
                              membership_level: '',
                              participant_count: couponpassesUsed,
                              actual_paid: 0,
                              total_revenue: 0,
                              percentage_of_total: couponpassesProgress
                            };
                          }
                          
                          if (memberType.membership_key === 'membersFullPrice') {
                            const maxAssigned = (contestData.max_pass ?? contestData.max_participants ?? contestData.min_participants) || 100;
                            const percentage = maxAssigned > 0 ? (passCodesUsed / maxAssigned) * 100 : 0;
                            return {
                              ...memberType,
                              participant_count: passCodesUsed,
                              actual_paid: entranceFee,
                              total_revenue: passCodesUsed * entranceFee,
                              percentage_of_total: percentage,
                              total_assigned: maxAssigned,
                              show_percentage: true
                            };
                          }
                          
                          if (memberType.membership_key === 'subscribersDiscounted') {
                            return {
                              ...memberType,
                              participant_count: 0,
                              actual_paid: 0,
                              total_revenue: 0,
                              percentage_of_total: 0
                            };
                          }
                          
                          if (memberType.membership_key === 'nonMembersFull') {
                            return {
                              ...memberType,
                              participant_count: 0,
                              actual_paid: 0,
                              total_revenue: 0,
                              percentage_of_total: 0
                            };
                          }
                          
                          return memberType;
                        });

                        const noahsArkSubscribers100 = {
                          membership_key: 'noahsArkSubscribers100',
                          discount_type: "Noah's Ark Subscribers (100% off)",
                          membership_level: 'Paid Subscriber',
                          discount_amount: entranceFee,
                          discount_percentage: 100,
                          participant_count: 0,
                          actual_paid: 0,
                          total_revenue: 0,
                          percentage_of_total: 0
                        };

                        const result = [];
                        let couponpassesSection = null;
                        let membersFullPriceSection = null;
                        let nonMembersFullSection = null;
                        let noahsArk50Section = null;
                        let otherSections = [];
                        
                        for (let i = 0; i < updatedBreakdown.length; i++) {
                          const section = updatedBreakdown[i];
                          
                          if (section.membership_key === 'voucherFreePass') {
                            couponpassesSection = section;
                          } else if (section.membership_key === 'membersFullPrice') {
                            membersFullPriceSection = section;
                          } else if (section.membership_key === 'nonMembersFull') {
                            nonMembersFullSection = section;
                          } else if (section.discount_type && section.discount_type.includes("Noah's Ark Subscribers (50% off)")) {
                            noahsArk50Section = section;
                          } else {
                            otherSections.push(section);
                          }
                        }
                        
                        if (couponpassesSection) result.push(couponpassesSection);
                        if (membersFullPriceSection) result.push(membersFullPriceSection);
                        if (nonMembersFullSection) result.push(nonMembersFullSection);
                        if (noahsArk50Section) result.push(noahsArk50Section);
                        result.push(noahsArkSubscribers100);
                        result.push(...otherSections);

                        return result;
                      };
                      
                      dynamicMembershipBreakdown = generateDynamicMembershipBreakdown();
                    } catch (contestError) {
                      console.error('Error loading contest data:', contestError);
                    }
                  }

                  // Obtener plataformas ordenadas según el orden guardado
                  const orderedPlatforms = getOrderedPlatforms();
                  
                  // Mapear las plataformas ordenadas al formato necesario para el documento
                  const allPlatforms = orderedPlatforms.map((platform: any) => {
                    // Si es voletaje
                    if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
                      // Buscar la plataforma base de voletaje
                      const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
                      return {
                        ...voletajeBase,
                        name: t('preview_modal_info_sponsorship.voletaje'),
                        isBase: true,
                        image: voletajeBase?.image_url || voletajeBase?.image,
                        x: voletajeBase?.x || 50,
                        y: voletajeBase?.y || 50,
                        width: voletajeBase?.width || 200,
                        height: voletajeBase?.height || 150,
                        logo_url: voletajeBase?.logo_url || formData.media_assets.logo_url,
                        logo_width: voletajeBase?.logo_width || 60,
                        logo_height: voletajeBase?.logo_height || 60,
                        Image_form: voletajeBase?.Image_form || 'circular',
                        custom_text: voletajeBase?.custom_text || 'your ad goes here',
                        Text_size: voletajeBase?.Text_size || 0.9,
                        contest_text: voletajeBase?.contest_text || 'Nombre del concurso',
                        distancia_x: voletajeBase?.distancia_x || 60,
                        Fix_width: voletajeBase?.Fix_width,
                        Fix_height: voletajeBase?.Fix_height,
                        general_width: voletajeBase?.general_width,
                        general_height: voletajeBase?.general_height
                      };
                    }
                    
                    // Si es plataforma base
                    if (platform.isBase && platform.id) {
                      const basePlatform = basePlatforms.find((bp: any) => bp.id === platform.id);
                      if (basePlatform) {
                        return {
                          ...basePlatform,
                          isBase: true,
                          image: basePlatform.image_url || basePlatform.image,
                          x: basePlatform.x || 50,
                          y: basePlatform.y || 50,
                          width: basePlatform.width || 200,
                          height: basePlatform.height || 150,
                          logo_url: basePlatform.logo_url || formData.media_assets.logo_url,
                          logo_width: basePlatform.logo_width || 60,
                          logo_height: basePlatform.logo_height || 60,
                          Image_form: basePlatform.Image_form || 'circular',
                          custom_text: basePlatform.custom_text || 'your ad goes here',
                          Text_size: basePlatform.Text_size || 0.9,
                          contest_text: basePlatform.contest_text || 'Nombre del concurso',
                          distancia_x: basePlatform.distancia_x || 60,
                          Fix_width: basePlatform.Fix_width,
                          Fix_height: basePlatform.Fix_height,
                          general_width: basePlatform.general_width,
                          general_height: basePlatform.general_height
                        };
                      }
                    }
                    
                    // Si es plataforma personalizada
                    if (!platform.isBase && !platform.isLegacy) {
                      return {
                        ...platform,
                        isBase: false
                      };
                    }
                    
                    // Si es legacy
                    if (platform.isLegacy) {
                      return {
                        name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
                        image: formData.custom_platform_image,
                        x: formData.flyer_position?.x || 50,
                        y: formData.flyer_position?.y || 50,
                        width: formData.flyer_position?.width || 200,
                        height: formData.flyer_position?.height || 150,
                        isBase: false
                      };
                    }
                    
                    return null;
                  }).filter((p: any) => p !== null);
                  
                  if (allPlatforms.length === 0) {
                    void appDialog.alert('No hay plataformas para descargar');
                    return;
                  }

                  // Función para crear imagen compuesta con volante (igual que download contest information)
                  const createCompositeImage = async (platform: any): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
                    const logoUrl = formData.media_assets?.logo_url || '';
                    const contestName = contestData?.name || selectedContest?.name || 'NOMBRE DEL CONCURSO';
                    try {
                      // Para "Download Platforms", siempre usar el mismo tamaño que el botón Word del preview
                      // El servidor siempre genera imágenes de 816 x 1056 px (tamaño de página estándar)
                      const finalCanvasWidth = 816;  // 8.5" x 96 DPI
                      const finalCanvasHeight = 1056; // 11" x 96 DPI
                      
                      // Crear canvas con el tamaño fijo desde el inicio
                      const canvas = document.createElement('canvas');
                      canvas.width = finalCanvasWidth;
                      canvas.height = finalCanvasHeight;
                      
                      const ctx = canvas.getContext('2d');
                      if (!ctx) return null;

                      // Cargar imagen de fondo
                      const imageUrl = platform.image || platform.image_url;
                      if (!imageUrl) {
                        throw new Error('No image URL provided');
                      }
                      
                      // Normalizar URL si es necesario
                      let normalizedImageUrl = imageUrl;
                      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                        normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
                      }
                      
                      // Usar loadImageForWord para manejar CORS automáticamente
                      const imageData = await loadImageForWord(normalizedImageUrl);
                      if (!imageData) {
                        throw new Error(`Failed to load background image: ${normalizedImageUrl}`);
                      }
                      
                      // Crear un elemento Image desde los datos cargados
                      const blob = new Blob([imageData.data as any], { type: 'image/png' });
                      const imageUrlFromBlob = URL.createObjectURL(blob);
                      const bgImage = new window.Image();
                      
                      await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                          URL.revokeObjectURL(imageUrlFromBlob);
                          reject(new Error('Background image load timeout'));
                        }, 30000);
                        
                        bgImage.onload = () => {
                          clearTimeout(timeout);
                          URL.revokeObjectURL(imageUrlFromBlob);
                          resolve(null);
                        };
                        bgImage.onerror = (error) => {
                          clearTimeout(timeout);
                          URL.revokeObjectURL(imageUrlFromBlob);
                          reject(new Error(`Failed to load background image from blob`));
                        };
                        bgImage.src = imageUrlFromBlob;
                      });
                      
                      // Calcular el escalado para mantener la proporción de la imagen (igual que el servidor)
                      const imageAspectRatio = imageData.width / imageData.height;
                      const canvasAspectRatio = finalCanvasWidth / finalCanvasHeight;
                      
                      let drawWidth = finalCanvasWidth;
                      let drawHeight = finalCanvasHeight;
                      let drawX = 0;
                      let drawY = 0;
                      
                      if (imageAspectRatio > canvasAspectRatio) {
                        // La imagen es más ancha, ajustar al ancho del canvas
                        drawWidth = finalCanvasWidth;
                        drawHeight = finalCanvasWidth / imageAspectRatio;
                        drawY = (finalCanvasHeight - drawHeight) / 2;
                      } else {
                        // La imagen es más alta, ajustar al alto del canvas
                        drawHeight = finalCanvasHeight;
                        drawWidth = finalCanvasHeight * imageAspectRatio;
                        drawX = (finalCanvasWidth - drawWidth) / 2;
                      }
                      
                      // Rellenar el fondo con blanco (como hace el servidor)
                      ctx.fillStyle = '#FFFFFF';
                      ctx.fillRect(0, 0, finalCanvasWidth, finalCanvasHeight);
                      
                      // Dibujar la imagen de fondo escalada y centrada
                      ctx.drawImage(bgImage, drawX, drawY, drawWidth, drawHeight);
                      
                      // Calcular escalado para el volante
                      let scaleX = drawWidth / imageData.width;
                      let scaleY = drawHeight / imageData.height;
                      
                      console.log(`📐 Download Platforms - Canvas: ${canvas.width}x${canvas.height}, Image: ${imageData.width}x${imageData.height}, Draw: ${drawWidth.toFixed(0)}x${drawHeight.toFixed(0)} at (${drawX.toFixed(0)}, ${drawY.toFixed(0)})`);

                      let flyerWidth: number;
                      let flyerHeight: number;
                      let tallImageScaleFactor = 1.0;
                      
                      // Si existen Fix_width y Fix_height en la plataforma, usarlos
                      if (platform.Fix_width && platform.Fix_height) {
                        flyerWidth = platform.Fix_width;
                        flyerHeight = platform.Fix_height;
                      } else {
                        // Calcular tamaño basado en el tamaño original de la imagen (para mantener proporciones correctas)
                        const previewMaxWidth = 800;
                        const previewDisplayWidth = Math.min(bgImage.width, previewMaxWidth);
                        const previewDisplayHeight = (bgImage.height / bgImage.width) * previewDisplayWidth;
                        
                        const flyerWidthPercent = ((platform.width || 200) / previewDisplayWidth) * 100;
                        const flyerHeightPercent = ((platform.height || 150) / previewDisplayHeight) * 100;
                        
                        flyerWidth = (flyerWidthPercent / 100) * bgImage.width;
                        flyerHeight = (flyerHeightPercent / 100) * bgImage.height;
                        
                        const maxDimension = Math.max(bgImage.width, bgImage.height);
                        const minDimension = Math.min(bgImage.width, bgImage.height);
                        const differencePercent = (minDimension / maxDimension) * 100;
                        
                        if (bgImage.height > bgImage.width && differencePercent < 100) {
                          if (differencePercent <= 40.18) {
                            tallImageScaleFactor = 0.4;
                          } else if (differencePercent <= 50) {
                            tallImageScaleFactor = 0.4 + ((differencePercent - 40.18) / (50 - 40.18)) * (0.65 - 0.5);
                          } else {
                            tallImageScaleFactor = 0.677 + ((differencePercent - 50) / 50) * 0.35;
                          }
                          flyerWidth = flyerWidth * tallImageScaleFactor;
                          flyerHeight = flyerHeight * tallImageScaleFactor;
                        }
                      }
                      
                      // Aplicar el escalado a las posiciones y tamaños del volante
                      const flyerX = ((platform.x || 50) / 100) * finalCanvasWidth;
                      const flyerY = ((platform.y || 50) / 100) * finalCanvasHeight;
                      
                      // Aplicar el escalado a los tamaños del volante para que coincidan con el canvas final
                      flyerWidth = flyerWidth * scaleX;
                      flyerHeight = flyerHeight * scaleY;

                      // Dibujar fondo del volante
                      ctx.fillStyle = '#f5f5dc';
                      ctx.strokeStyle = '#ff6b35';
                      ctx.lineWidth = 2;
                      ctx.beginPath();
                      // Dibujar rectángulo redondeado (compatible con todos los navegadores)
                      const radius = 8;
                      ctx.moveTo(flyerX + radius, flyerY);
                      ctx.lineTo(flyerX + flyerWidth - radius, flyerY);
                      ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY, flyerX + flyerWidth, flyerY + radius);
                      ctx.lineTo(flyerX + flyerWidth, flyerY + flyerHeight - radius);
                      ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY + flyerHeight, flyerX + flyerWidth - radius, flyerY + flyerHeight);
                      ctx.lineTo(flyerX + radius, flyerY + flyerHeight);
                      ctx.quadraticCurveTo(flyerX, flyerY + flyerHeight, flyerX, flyerY + flyerHeight - radius);
                      ctx.lineTo(flyerX, flyerY + radius);
                      ctx.quadraticCurveTo(flyerX, flyerY, flyerX + radius, flyerY);
                      ctx.closePath();
                      ctx.fill();
                      ctx.stroke();

                      // Cargar y dibujar logo
                      const logoUrl = formData.media_assets.logo_url;
                      if (logoUrl) {
                        try {
                          // Normalizar URL del logo si es necesario
                          let normalizedLogoUrl = logoUrl;
                          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
                            normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
                          }
                          
                          const logoImage = new Image();
                          logoImage.crossOrigin = 'anonymous';
                          await new Promise((resolve, reject) => {
                            logoImage.onload = resolve;
                            logoImage.onerror = () => {
                              console.error('Error loading logo image:', normalizedLogoUrl);
                              reject(new Error(`Failed to load logo image: ${normalizedLogoUrl}`));
                            };
                            logoImage.src = normalizedLogoUrl;
                          });

                          // Reducir el tamaño del logo (aplicar factor de escala de 0.7 para hacerlo más pequeño)
                          const logoScaleFactor = 0.7;
                          const maxLogoWidth = ((platform.logo_width / 100) * flyerWidth) * logoScaleFactor;
                          const maxLogoHeight = ((platform.logo_height / 100) * flyerHeight) * logoScaleFactor;
                          console.log('🖼️ Logo size reduced - Scale factor:', logoScaleFactor, 'Max size:', maxLogoWidth, 'x', maxLogoHeight);
                          
                          const imageAspectRatio = logoImage.width / logoImage.height;
                          const maxAspectRatio = maxLogoWidth / maxLogoHeight;
                          
                          let logoWidth, logoHeight;
                          if (imageAspectRatio > maxAspectRatio) {
                            logoWidth = maxLogoWidth;
                            logoHeight = maxLogoWidth / imageAspectRatio;
                          } else {
                            logoHeight = maxLogoHeight;
                            logoWidth = maxLogoHeight * imageAspectRatio;
                          }
                          
                          const logoX = flyerX + (flyerWidth - logoWidth) / 2;
                          const logoY = flyerY + (platform.distancia_x || 60); // Espacio desde la parte superior

                          ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
                          
                          // Guardar la posición donde termina el logo para posicionar el texto justo debajo
                          const logoBottom = logoY + logoHeight;
                          
                          // Dibujar texto del concurso justo debajo del logo con salto de línea automático
                          const contestNameText = (contestName || platform.contest_text || 'NOMBRE DEL CONCURSO').toUpperCase();
                          const fontSize = (platform.Text_size || 0.9) * 16 * tallImageScaleFactor;
                          ctx.fillStyle = '#7c3aed';
                          ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                          ctx.textAlign = 'center';
                          ctx.textBaseline = 'top';
                          // Espacio entre logo y primer texto
                          const textMargin = Math.max(40, 40 * tallImageScaleFactor);
                          let contestY = logoBottom + textMargin;
                          
                          // Función helper para dividir texto en líneas
                          const drawMultilineText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number) => {
                            const words = text.split(' ');
                            const lines: string[] = [];
                            let currentLine = words[0];
                            
                            for (let i = 1; i < words.length; i++) {
                              const word = words[i];
                              const testLine = currentLine + ' ' + word;
                              const metrics = ctx.measureText(testLine);
                              if (metrics.width > maxWidth && currentLine.length > 0) {
                                lines.push(currentLine);
                                currentLine = word;
                              } else {
                                currentLine = testLine;
                              }
                            }
                            lines.push(currentLine);
                            
                            // Dibujar cada línea
                            lines.forEach((line, index) => {
                              ctx.fillText(line, x, y + (index * lineHeight));
                            });
                            
                            return lines.length;
                          };
                          
                          // Ancho disponible para el texto (con márgenes laterales)
                          const textMaxWidth = flyerWidth * 0.9; // 90% del ancho del voleto
                          const lineHeight = fontSize * 1.2;
                          const lineCount = drawMultilineText(contestNameText, flyerX + flyerWidth / 2, contestY, textMaxWidth, lineHeight);
                          contestY += (lineCount - 1) * lineHeight; // Ajustar posición Y si hay múltiples líneas

                          // Dibujar texto personalizado debajo del nombre del concurso
                          const customText = platform.custom_text || 'your ad goes here';
                          // Espacio entre los dos textos (aumentado)
                          const customTextY = contestY + fontSize * 1.8;
                          ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
                        } catch (logoError) {
                          console.error('Error loading logo:', logoError);
                        }
                      } else {
                        // Si no hay logo, posicionar el texto desde arriba
                        const contestNameText = (contestName || platform.contest_text || 'NOMBRE DEL CONCURSO').toUpperCase();
                        const fontSize = (platform.Text_size || 0.9) * 16 * tallImageScaleFactor;
                        ctx.fillStyle = '#7c3aed';
                        ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        const contestY = flyerY + 20;
                        ctx.fillText(contestNameText, flyerX + flyerWidth / 2, contestY);

                        const customText = platform.custom_text || 'your ad goes here';
                        const customTextY = contestY + fontSize * 1.2;
                        ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
                      }

                      // VERIFICAR que el canvas tenga el tamaño correcto antes de convertirlo
                      if (canvas.width !== 816 || canvas.height !== 1056) {
                        console.error(`❌ ERROR: Canvas size is ${canvas.width}x${canvas.height}, expected 816x1056. Fixing...`);
                        canvas.width = 816;
                        canvas.height = 1056;
                      }
                      
                      return new Promise((resolve) => {
                        canvas.toBlob((blob) => {
                          if (!blob) {
                            resolve(null);
                            return;
                          }
                          blob.arrayBuffer().then((buffer) => {
                            // FORZAR dimensiones a 816 x 1056 (tamaño estándar del servidor)
                            const finalWidth = 816;
                            const finalHeight = 1056;
                            console.log(`📐 Download Platforms - Final image dimensions: ${finalWidth}x${finalHeight}, canvas verified: ${canvas.width}x${canvas.height}, buffer size: ${buffer.byteLength} bytes`);
                            resolve({
                              data: new Uint8Array(buffer),
                              width: finalWidth,
                              height: finalHeight
                            });
                          });
                        }, 'image/png', 1.0); // Calidad máxima
                      });
                    } catch (error) {
                      console.error('Error creating composite image:', error);
                      return null;
                    }
                  };

                  // Crear secciones para cada plataforma (cada una en su propia página)
                  const sections = await Promise.all(allPlatforms.map(async (platform: any, index: number) => {
                    const children: any[] = [];
                    
                    // Título de la plataforma
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${index + 1}. ${platform.name || platform.platform_type || 'Platform'}${platform.isBase ? ' (Base)' : ''}`,
                            size: 36,
                            bold: true
                          })
                        ],
                        spacing: { before: 400, after: 0, line: 240 } // Sin espaciado entre título e imagen
                      })
                    );

                    // Crear imagen compuesta con volante
                    if (platform.image || platform.image_url) {
                      try {
                        const compositeImage = await createCompositeImage(platform);
                        
                        if (compositeImage) {
                          // Forzar dimensiones a 816 x 1056 px (igual que el servidor) para mantener consistencia con el preview
                          const imageWidth = 816;  // 8.5" x 96 DPI
                          const imageHeight = 1056; // 11" x 96 DPI
                          children.push(
                            new Paragraph({
                              children: [
                                new ImageRun({
                                  data: compositeImage.data,
                                  transformation: {
                                    width: imageWidth,
                                    height: imageHeight,
                                  },
                                  type: 'png',
                                } as any),
                              ],
                              alignment: AlignmentType.CENTER,
                              spacing: { before: 0, after: 300 }
                            })
                          );
                        } else {
                          // Si falla la creación compuesta, intentar solo la imagen de fondo usando la función helper
                          const imageData = await loadImageForWord(platform.image || platform.image_url);
                          
                          if (imageData) {
                            // Usar general_width y general_height si están definidos, sino usar el tamaño original
                            const imageWidth = (platform.general_width && platform.general_height) 
                              ? platform.general_width 
                              : (imageData.width || 600);
                            const imageHeight = (platform.general_width && platform.general_height) 
                              ? platform.general_height 
                              : (imageData.height || 400);
                            
                            children.push(
                              new Paragraph({
                                children: [
                                  new ImageRun({
                                    data: imageData.data,
                                    transformation: {
                                      width: imageWidth,
                                      height: imageHeight,
                                    },
                                    type: 'png',
                                  } as any),
                                ],
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 300 }
                              })
                            );
                          } else {
                            // Si no se puede cargar la imagen, omitirla en lugar de mostrar error
                            console.error('Failed to load fallback image for platform:', platform.name);
                          }
                        }
                      } catch (imgError) {
                        console.error('Error loading image:', imgError);
                        // No agregar mensaje de error al documento, simplemente omitir la imagen
                      }
                    } else {
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'No hay imagen disponible',
                              size: 24,
                              color: '666666'
                            })
                          ],
                          spacing: { after: 200 }
                        })
                      );
                    }

                    return {
                      properties: {
                        type: SectionType.NEXT_PAGE
                      },
                      children: children
                    };
                  }));

                  // Función para cargar imagen del logo con límite de altura
                  let logoImageData: Uint8Array | null = null;
                  let logoWidth = 2000;  // Ancho en TWIPS (default)
                  let logoHeight = 2000; // Alto en TWIPS (default)
                  
                  if (formData.media_assets.logo_url) {
                    try {
                      const logoResponse = await fetch(formData.media_assets.logo_url);
                      const logoArrayBuffer = await logoResponse.arrayBuffer();
                      logoImageData = new Uint8Array(logoArrayBuffer);
                      
                      // Cargar la imagen para obtener sus dimensiones reales
                      const img = new Image();
                      const blob = new Blob([logoArrayBuffer], { type: 'image/png' });
                      const imgUrl = URL.createObjectURL(blob);
                      
                      await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = imgUrl;
                      });
                      
                      // Calcular dimensiones manteniendo proporción y limitando altura a 18.75px (8 veces más pequeño que 150px original)
                      const maxHeightPx = 18.75;
                      let finalWidthPx = img.width;
                      let finalHeightPx = img.height;
                      
                      if (finalHeightPx > maxHeightPx) {
                        const scale = maxHeightPx / finalHeightPx;
                        finalWidthPx = finalWidthPx * scale;
                        finalHeightPx = maxHeightPx;
                      }
                      
                      // Convertir píxeles a TWIPS
                      // Para Word: 1 pulgada = 1440 TWIPS
                      // Asumiendo 96 DPI (pantalla estándar): 1 pulgada = 96 píxeles
                      // Por lo tanto: 1 píxel = 1440/96 = 15 TWIPS
                      // Pero para imágenes grandes, usamos un factor más conservador
                      logoWidth = Math.round(finalWidthPx * 15);
                      logoHeight = Math.round(finalHeightPx * 15);
                      
                      // Si la imagen es muy ancha (aspect ratio >= 2:1), reducir a la mitad en la portada
                      const aspectRatio = img.width / img.height;
                      if (aspectRatio >= 2) {
                        logoWidth = Math.round(logoWidth * 0.5);
                        logoHeight = Math.round(logoHeight * 0.5);
                      }
                      
                      URL.revokeObjectURL(imgUrl);
                    } catch (logoError) {
                      console.error('Error loading logo:', logoError);
                    }
                  }

                  // Formatear fechas
                  const formatDate = (dateString: string) => {
                    if (!dateString) return 'N/A';
                    try {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('es-ES', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      });
                    } catch {
                      return dateString;
                    }
                  };

                  // Cargar logo de Noah's Ark (PNG)
                  let noahsArkLogoData: Uint8Array | null = null;
                  let noahsArkLogoWidth = 375;  // 25px * 15 TWIPS/px (8 veces más pequeño)
                  let noahsArkLogoHeight = 375; // 25px * 15 TWIPS/px
                  
                  try {
                    // Usar la imagen PNG local desde public
                    const noahsArkLogoUrl = '/Noah.logo.color5.png';
                    
                    // Cargar el PNG directamente
                    const noahsArkLogoResponse = await fetch(noahsArkLogoUrl);
                    if (!noahsArkLogoResponse.ok) {
                      throw new Error(`Failed to fetch PNG: ${noahsArkLogoResponse.status}`);
                    }
                    
                    const arrayBuffer = await noahsArkLogoResponse.arrayBuffer();
                    noahsArkLogoData = new Uint8Array(arrayBuffer);
                    
                    // Opcional: Cargar la imagen para obtener dimensiones reales y escalar a 25px
                    const img = new Image();
                    await new Promise<void>((resolve, reject) => {
                      img.onload = () => {
                        // Calcular dimensiones manteniendo proporción y limitando a 12.5px (16 veces más pequeño que el original)
                        const maxDimensionPx = 12.5;
                        let finalWidthPx = img.width;
                        let finalHeightPx = img.height;
                        
                        if (finalWidthPx > maxDimensionPx || finalHeightPx > maxDimensionPx) {
                          const scale = Math.min(maxDimensionPx / finalWidthPx, maxDimensionPx / finalHeightPx);
                          finalWidthPx = finalWidthPx * scale;
                          finalHeightPx = finalHeightPx * scale;
                        }
                        
                        // Convertir píxeles a TWIPS (1 píxel = 15 TWIPS)
                        noahsArkLogoWidth = Math.round(finalWidthPx * 15);
                        noahsArkLogoHeight = Math.round(finalHeightPx * 15);
                        resolve();
                      };
                      img.onerror = reject;
                      img.src = noahsArkLogoUrl;
                    });
                    
                    console.log('Noah\'s Ark logo loaded successfully (PNG):', {
                      width: noahsArkLogoWidth,
                      height: noahsArkLogoHeight,
                      dataLength: noahsArkLogoData.length,
                      hasData: !!noahsArkLogoData
                    });
                  } catch (noahsArkLogoError) {
                    console.error('Error loading Noah\'s Ark logo (PNG):', noahsArkLogoError);
                    noahsArkLogoData = null;
                  }

                  // Debug: verificar que el logo se haya cargado
                  console.log('Noah\'s Ark logo check:', {
                    hasData: !!noahsArkLogoData,
                    dataLength: noahsArkLogoData?.length || 0,
                    width: noahsArkLogoWidth,
                    height: noahsArkLogoHeight
                  });

                  const firstSectionChildren: any[] = [
                    // Logo de Noah's Ark al inicio
                    ...(noahsArkLogoData && noahsArkLogoData.length > 0 ? [
                      new Paragraph({
                        children: [
                          new ImageRun({
                            data: noahsArkLogoData,
                            transformation: {
                              width: noahsArkLogoWidth,
                              height: noahsArkLogoHeight,
                            },
                            type: 'png',
                          } as any),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 600 }
                      })
                    ] : []),
                    
                    // Título principal
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Contest Information - ${selectedContest?.name || 'Contest'}`,
                          size: 44,
                          bold: true
                        })
                      ],
                      heading: HeadingLevel.HEADING_1,
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 600 }
                    }),
                    
                    // Nombre del patrocinador
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Sponsorship:',
                          size: 32,
                          bold: true
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 100 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: formData.sponsor_name || 'N/A',
                          size: 32
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 300 }
                    }),

                    // Logo de la empresa
                    ...(logoImageData ? [
                      new Paragraph({
                        children: [
                          new ImageRun({
                            data: logoImageData,
                            transformation: {
                              width: logoWidth,
                              height: logoHeight,
                            },
                            type: 'png',
                          } as any),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 300 }
                      })
                    ] : []),

                    // Slogan
                    ...(formData.media_assets.slogan ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formData.media_assets.slogan,
                            size: 28,
                            italics: true
                          })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 400 }
                      })
                    ] : [])
                  ];

                  // Función helper para generar contenido de datos del concurso
                  const generateContestDataSection = (contest: any, economics: any, sponsorship: any, membershipBreakdown: any[]) => {
                    const totalRevenue = Number(economics?.total_revenue) || 0;
                    const totalCosts = Number(economics?.total_costs) || 0;
                    const economicsObj = economics || {};

                    
                    
                    return [


                      // Título
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `Economic Dashboard for the Contest ${contest?.name || 'Contest'}`,
                            size: 44,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 400 }
                      }),
                      
                      // Información General del Concurso
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.ContestConfiguration'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Status')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest?.status || 'N/A', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Phase')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest?.phase?.replace(/_/g, ' ') || 'N/A', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.AccessLevel')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest?.subscribers_only ? 'Subscribers Only' : contest?.members_only ? 'Members Only' : 'Public', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.PhotoRewardsMode')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest?.photo_rewards_mode || 'N/A', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.PrizeType')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest?.prize_type || 'N/A', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Couponpasses: ', bold: true, size: 28 }),
                          new TextRun({ text: String(contest?.coupon_quantity || contest?.max_coupass || 0), size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Pases Normales: ', bold: true, size: 28 }),
                          new TextRun({ text: String(contest?.max_participants || contest?.max_pass || 0), size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Rendimiento Económico
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.economic_performance'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.total_revenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${totalRevenue.toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.participant')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economicsObj.participant_revenue) || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Sponsor')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(contest?.total_sponsor_cost) || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.total_costs')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${totalCosts.toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Main_Prize')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economicsObj.prize_costs) || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Secondary_Prize')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economicsObj.ops_costs) || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.net_profit')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economicsObj.noahs_ark_profit) || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Margin')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${(Number(economicsObj.profit_margin) || 0).toFixed(1)}%`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `ROI: `, bold: true, size: 28 }),
                          new TextRun({ text: `${(Number(economicsObj.total_roi) || 0).toFixed(1)}%`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.Per_Participant')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(Number(economicsObj.revenue_per_participant) || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Análisis de Ingresos
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.RevenueAnalysis'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.FullPriceRevenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economicsObj.full_price_revenue || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.DiscountedRevenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economicsObj.discounted_revenue || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.VoucherValueUsed')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economicsObj.voucher_redemption_value || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.TotalDiscountsGiven')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economicsObj.total_discount_given || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.RevenueEfficiency')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${(economicsObj.revenue_efficiency_percentage || 0).toFixed(1)}%`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.PotentialRevenue')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${(economicsObj.potential_revenue || 0).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Premios y Patrocinio
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.PrizeandSponsorship'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.PrizePoolBreakdown'),
                            size: 32,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_3,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 200, after: 100 }
                      }),
                      ...(contest?.prize_first_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Frist_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_first_place.toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      ...(contest?.prize_second_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Second_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_second_place.toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      ...(contest?.prize_third_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Third_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_third_place.toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      ...(contest?.prize_fourth_place > 0 ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Fourth_Place')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `$${contest.prize_fourth_place.toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Total: ', bold: true, size: 28 }),
                          new TextRun({ text: `$${((Number(contest?.prize_first_place) || 0) + 
                              (Number(contest?.prize_second_place) || 0) + 
                              (Number(contest?.prize_third_place) || 0) + 
                              (Number(contest?.prize_fourth_place) || 0) + 
                              (Number(contest?.prize_fifth_place) || 0) + 
                              (Number(contest?.prize_sixth_place) || 0) + 
                              (Number(contest?.prize_seventh_place) || 0) + 
                              (Number(contest?.prize_eighth_place) || 0)).toFixed(2)}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Secondary_Prize')}: `, bold: true, size: 28 }),
                          new TextRun({ 
                            text: `$${(() => {
                              // Calcular total de premios secundarios desde dynamic_prizes
                              const dynamicPrizes = contest?.dynamic_prizes || {};
                              let secondaryTotal = 0;
                              if (typeof dynamicPrizes === 'object' && dynamicPrizes !== null) {
                                Object.values(dynamicPrizes).forEach((prize: any) => {
                                  secondaryTotal += Number(prize) || 0;
                                });
                              }
                              return secondaryTotal.toFixed(2);
                            })()}`, 
                            size: 28 
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      
                      // Detalles de Patrocinio
                      ...((sponsorship || contest?.sponsor_name) ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: t('details_contest.Sponsorship_Details'),
                              size: 32,
                              bold: true
                            })
                          ],
                          heading: HeadingLevel.HEADING_3,
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 200, after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Sponsor')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship?.sponsor_name || contest?.sponsor_name || 'Noah\'s Ark', size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.type')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship?.sponsorship_type?.replace(/_/g, ' ') || contest?.sponsorship_type?.replace(/_/g, ' ') || 'N/A', size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.contribution')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `$${(sponsorship?.total_sponsor_cost || contest?.total_sponsor_cost || 0).toFixed(2)}`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        ...(sponsorship?.impressions > 0 ? [new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.impressions')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.impressions.toLocaleString(), size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        })] : []),
                        ...(sponsorship?.engagements > 0 ? [new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.engagements')}: `, bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.engagements.toLocaleString(), size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        })] : []),
                        ...(sponsorship?.contact_name ? [new Paragraph({
                          children: [
                            new TextRun({ text: 'Contact: ', bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.contact_name, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        })] : []),
                        ...(sponsorship?.contact_email ? [new Paragraph({
                          children: [
                            new TextRun({ text: 'Email: ', bold: true, size: 28 }),
                            new TextRun({ text: sponsorship.contact_email, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        })] : [])
                      ] : []),
                      
                      // Photo Rewards Split
                      ...(contest?.photo_rewards_mode !== 'disabled' ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: t('details_contest.PhotoRewardsSplit'),
                              size: 32,
                              bold: true
                            })
                          ],
                          heading: HeadingLevel.HEADING_3,
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 200, after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Photo_Rewards_User_Percentage')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${contest.photo_rewards_user_percentage || 0}%`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Photo_Rewards_Organizer_Percentage')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${contest.photo_rewards_organizer_percentage || 0}%`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${t('details_contest.Photo_Rewards_Noahs_Ark_Percentage')}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${contest.photo_rewards_noahs_ark_percentage || 0}%`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        })
                      ] : []),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Cronograma
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.ScheduleandTimeline'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.SubmissionPeriod')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${new Date(contest?.submission_start || Date.now()).toLocaleDateString()} - ${new Date(contest?.submission_deadline || Date.now()).toLocaleDateString()}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      ...(contest?.voting_start ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.VotingPeriod')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${new Date(contest.voting_start).toLocaleDateString()} - ${new Date(contest.voting_end).toLocaleDateString()}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.WinnerAnnouncement')}: `, bold: true, size: 28 }),
                          new TextRun({ text: new Date(contest?.winner_announcement || Date.now()).toLocaleDateString(), size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Requisitos de Medios
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.MediaRequirements'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Allowed_Types')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest?.allowed_media_types || 'both', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      ...(contest?.allowed_media_types === 'image' || contest?.allowed_media_types === 'both' ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Max_Images')}: `, bold: true, size: 28 }),
                          new TextRun({ text: String(contest?.max_images || 0), size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      ...(contest?.allowed_media_types === 'video' || contest?.allowed_media_types === 'both' ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Max_Videos')}: `, bold: true, size: 28 }),
                          new TextRun({ text: String(contest?.max_videos || 0), size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Imagesize_limit')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${contest?.max_file_size || 0}MB`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Videosize_limit')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${contest?.max_file_size || 0}MB`, size: 28 })
                        ],
                        alignment: AlignmentType.CENTER
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Video_duration')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${contest?.max_video_duration_seconds || 0}s`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      ...(contest?.media_requirements ? [new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Media_Requirements')}: `, bold: true, size: 28 }),
                          new TextRun({ text: contest.media_requirements, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      })] : []),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Progreso de Participación
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.ParticipationProgress'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${t('details_contest.Current_Progress')}: `, bold: true, size: 28 }),
                          new TextRun({ text: `${economicsObj.total_pass_codes_used || 0} / ${contest?.max_participants || contest?.min_participants} ${t('details_contest.participants')}`, size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 }
                      }),
                      
                      // Línea divisoria
                      new Paragraph({
                        border: {
                          bottom: {
                            color: '000000',
                            size: 6,
                            style: BorderStyle.SINGLE
                          }
                        },
                        spacing: { after: 300, before: 200 }
                      }),
                      
                      // Membership Breakdown
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t('details_contest.MembershipAndPaymentBreakdown'),
                            size: 36,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_2,
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 400, after: 200 }
                      }),
                      ...(membershipBreakdown.map((member: any) => [
                        new Paragraph({
                          children: [
                            new TextRun({ text: `${member.membership_level || member.discount_type}: `, bold: true, size: 28 }),
                            new TextRun({ text: `${member.participant_count || 0} participantes`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `  Ingresos: `, bold: true, size: 28 }),
                            new TextRun({ text: `$${(member.total_revenue || 0).toFixed(2)}`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({ text: `  Porcentaje: `, bold: true, size: 28 }),
                            new TextRun({ text: `${(member.percentage_of_total || 0).toFixed(1)}%`, size: 28 })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 }
                        })
                      ]).flat())
                    ];
                  };

                  // Función para generar tabla de contenido
                  const generateTableOfContents = () => {
                    return [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Table of Contents',
                            size: 44,
                            bold: true
                          })
                        ],
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 3000 } // 5 saltos de línea (240 TWIPS cada uno = 1200 TWIPS, pero usamos 3000 para más espacio)
                      }),
                      // Primer título principal: Basic Contest Data
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Basic Contest Data', size: 32, bold: true }),
                          new TextRun({ text: ' ................................. ', size: 32 }),
                          new TextRun({ text: '3', size: 32 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 300 }
                      }),
                      // Segundo título principal: Economic Dashboard for the Contest
                      new Paragraph({
                        children: [
                          new TextRun({ text: `Economic Dashboard for the Contest ${selectedContest?.name || 'Contest'}`, size: 32, bold: true }),
                          new TextRun({ text: ' ................................. ', size: 32 }),
                          new TextRun({ text: '4', size: 32 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      // Títulos secundarios de Economic Dashboard
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.ContestConfiguration')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.economic_performance')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.RevenueAnalysis')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.PrizeandSponsorship')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.ScheduleandTimeline')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.MediaRequirements')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.ParticipationProgress')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({ text: `  ${t('details_contest.MembershipAndPaymentBreakdown')}`, size: 28 }),
                          new TextRun({ text: ' ................................. ', size: 28 }),
                          new TextRun({ text: '4', size: 28 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 300 }
                      }),
                      // Tercer título principal: Flyer Images
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Flyer Images', size: 32, bold: true }),
                          new TextRun({ text: ' ................................. ', size: 32 }),
                          new TextRun({ text: '5', size: 32 })
                        ],
                        alignment: AlignmentType.LEFT,
                        spacing: { after: 200 }
                      })
                    ];
                  };

                  // Función para generar la tercera hoja con datos adicionales
                  const generateAdditionalInfoSection = () => {
                    const formatDate = (dateString: string) => {
                      if (!dateString) return 'N/A';
                      try {
                        const date = new Date(dateString);
                        return date.toLocaleDateString('es-ES', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        });
                      } catch {
                        return dateString;
                      }
                    };

                    return [
                      // 5 saltos de línea al inicio
                      new Paragraph({
                        children: [],
                        spacing: { before: 0, after: 3000 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Social Media:',
                            size: 32,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formData.social_links || 'facebook.com instagram.com',
                            size: 32
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 300 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Start Date:',
                            size: 32,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formatDate(formData.schedule.start),
                            size: 32
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 300 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'End Date:',
                            size: 32,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formatDate(formData.schedule.end),
                            size: 32
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 300 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Total Cost:',
                            size: 32,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `$${formData.total_sponsor_cost?.toFixed(2) || '0.00'}`,
                            size: 32,
                            bold: true,
                            color: '2563EB'
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 300 }
                      }),
                      // Type of Sponsorship
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Type of Sponsorship:',
                            size: 32,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: t(`type_and_budget_info_sponsorship.${formData.sponsorship_type}`) || formData.sponsorship_type || 'N/A',
                            size: 32
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 300 }
                      }),
                      // Entrance Type
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Entrance Type:',
                            size: 32,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formData.sponsorship_type === 'full_entrance' 
                              ? t('type_and_budget_info_sponsorship.full_entrance')
                              : formData.sponsorship_type === 'partial_entrance'
                              ? `${t('type_and_budget_info_sponsorship.partial_entrance')} (${formData.entrance_coverage_percentage || 0}%)`
                              : formData.entrance_type === 'full_entrance'
                              ? t('type_and_budget_info_sponsorship.full_entrance')
                              : formData.entrance_type === 'partial_entrance'
                              ? `${t('type_and_budget_info_sponsorship.partial_entrance')} (${formData.entrance_coverage_percentage || 0}%)`
                              : (formData.entrance_coverage_percentage && formData.entrance_coverage_percentage > 0)
                              ? `${formData.entrance_coverage_percentage}%`
                              : 'N/A',
                            size: 32
                          })
                        ],
                        alignment: AlignmentType.LEFT,
                        indent: { left: 720 },
                        spacing: { after: 300 }
                      }),
                      // Reward Contribution
                      ...((formData.reward_contribution && formData.reward_contribution > 0) ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'Reward Contribution:',
                              size: 32,
                              bold: true
                            })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 },
                          spacing: { after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: `$${formData.reward_contribution.toFixed(2)}`,
                              size: 32
                            })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 },
                          spacing: { after: 300 }
                        })
                      ] : []),
                      // Impressions Meta
                      ...((formData.impressions && formData.impressions > 0) ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'Impressions Meta:',
                              size: 32,
                              bold: true
                            })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 },
                          spacing: { after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: formData.impressions.toLocaleString(),
                              size: 32
                            })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 },
                          spacing: { after: 300 }
                        })
                      ] : []),
                      // Engagements Meta
                      ...((formData.engagements && formData.engagements > 0) ? [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'Engagements Meta:',
                              size: 32,
                              bold: true
                            })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 },
                          spacing: { after: 100 }
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: formData.engagements.toLocaleString(),
                              size: 32
                            })
                          ],
                          alignment: AlignmentType.LEFT,
                          indent: { left: 720 },
                          spacing: { after: 400 }
                        })
                      ] : [])
                    ];
                  };

                  const doc = new Document({
                    sections: [
                      // Primera sección con el título general e información del patrocinador
                      {
                        properties: {
                          page: {
                            size: {
                              width: 15840, // 11 pulgadas (ancho aumentado desde 8.5")
                              height: 15000, // 14 pulgadas (alto aumentado - tamaño Legal)
                            },
                            margins: {
                              top: 720,    // 0.5 pulgadas (margen superior reducido)
                              right: 720,  // 0.5 pulgadas (margen derecho reducido)
                              bottom: 720, // 0.5 pulgadas (margen inferior reducido)
                              left: 360,   // 0.25 pulgadas (margen izquierdo muy reducido - imágenes más cerca del borde)
                            },
                          },
                        },
                        children: firstSectionChildren
                      },
                      // Segunda sección: Tabla de contenido
                      {
                        properties: {
                          type: SectionType.NEXT_PAGE,
                          page: {
                            size: {
                              width: 15840,
                              height: 20160,
                            },
                            margins: {
                              top: 720,
                              right: 720,
                              bottom: 720,
                              left: 360,
                            },
                          },
                        },
                        children: generateTableOfContents()
                      },
                      // Tercera sección: Información adicional (Redes Sociales, Fechas, Costo Total)
                      {
                        properties: {
                          type: SectionType.NEXT_PAGE,
                          page: {
                            size: {
                              width: 15840,
                              height: 20160,
                            },
                            margins: {
                              top: 720,
                              right: 720,
                              bottom: 720,
                              left: 360,
                            },
                          },
                        },
                        children: generateAdditionalInfoSection()
                      },
                      // Cuarta sección con datos del concurso (solo si hay datos disponibles)
                      ...(contestData && economicsData ? [{
                        properties: {
                          type: SectionType.NEXT_PAGE,
                          page: {
                            size: {
                              width: 15840,
                              height: 20160,
                            },
                            margins: {
                              top: 720,
                              right: 720,
                              bottom: 720,
                              left: 360,
                            },
                          },
                        },
                        children: generateContestDataSection(contestData, economicsData, fullSponsorship, dynamicMembershipBreakdown)
                      }] : []),
                      // Secciones para cada plataforma (cada una en su propia página)
                      ...sections.map((section: any) => ({
                        ...section,
                        properties: {
                          ...section.properties,
                          page: {
                            size: {
                              width: 15840, // 11 pulgadas (ancho aumentado desde 8.5")
                              height: 20160, // 14 pulgadas (alto aumentado - tamaño Legal)
                            },
                            margins: {
                              top: 720,    // 0.5 pulgadas (margen superior reducido)
                              right: 720,  // 0.5 pulgadas (margen derecho reducido)
                              bottom: 720, // 0.5 pulgadas (margen inferior reducido)
                              left: 360,   // 0.25 pulgadas (margen izquierdo muy reducido - imágenes más cerca del borde)
                            },
                          },
                        },
                      }))
                    ]
                  });

                  // Generar el blob y descargar
                  const blob = await Packer.toBlob(doc);
                  const fileName = `Platform_Images_${selectedContest?.name?.replace(/[^a-z0-9]/gi, '_') || 'Contest'}_${new Date().toISOString().split('T')[0]}.docx`;
                  saveAs(blob, fileName);
                } catch (docError) {
                  console.error('Error generating Word document:', docError);
                  void appDialog.alert('Error al generar el documento. Por favor, inténtalo de nuevo.');
                }
              }}
              sx={{
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' },
                fontSize: '0.875rem',
                padding: '6px 16px'
              }}
              size="small"
            >
              {t('preview_modal_info_sponsorship.downloadImages') || 'Descargar Información del Concurso'}
            </Button> */}
            <Button
              onClick={downloadAllPlatformsWord}
              variant="contained"
              startIcon={<FileTextIcon />}
              sx={{
                backgroundColor: '#9c27b0',
                '&:hover': { backgroundColor: '#7b1fa2' },
                fontSize: '0.875rem',
                padding: '6px 16px',
                ml: 1
              }}
              size="small"
            >
              {t('preview_modal_info_sponsorship.downloadAllPlatformsWord') || 'Descargar Todas las Plataformas (Word)'}
            </Button>
          <IconButton
            onClick={() => setShowPreviewConfigModal(true)}
            size="small"
              sx={{ ml: 1 }}
            title={t('preview_modal_info_sponsorship.configurePreviews')}
          >
            <SettingsIcon />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <IconButton
              size="small"
              onClick={() => setPreviewViewMode('list')}
              sx={{
                backgroundColor: previewViewMode === 'list' ? '#e3f2fd' : 'transparent',
                color: previewViewMode === 'list' ? '#1976d2' : '#666',
                '&:hover': {
                  backgroundColor: previewViewMode === 'list' ? '#bbdefb' : '#f5f5f5'
                }
              }}
              title="Vista de lista"
            >
              <ViewListIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPreviewViewMode('gallery')}
              sx={{
                backgroundColor: previewViewMode === 'gallery' ? '#e3f2fd' : 'transparent',
                color: previewViewMode === 'gallery' ? '#1976d2' : '#666',
                '&:hover': {
                  backgroundColor: previewViewMode === 'gallery' ? '#bbdefb' : '#f5f5f5'
                }
              }}
              title="Vista de galería"
            >
              <ViewModuleIcon />
            </IconButton>
          </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewViewMode === 'list' ? (
          <Box>
            {/* Buscador de plataformas */}
            <Box sx={{ mb: 2, mt: 2 }}>
              <TextField
                fullWidth
                placeholder="Buscar plataforma por nombre..."
                value={previewPlatformsSearchTerm}
                onChange={(e) => setPreviewPlatformsSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Filter className="w-5 h-5" style={{ color: '#6b7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#ff6b35',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#ff6b35',
                    },
                  },
                }}
              />
            </Box>
            
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>Orden</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('preview_modal_info_sponsorship.platform')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('preview_modal_info_sponsorship.available')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('preview_modal_info_sponsorship.description')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Función para verificar si el término de búsqueda es una subsecuencia del texto
                    const isSubsequence = (searchTerm: string, text: string): boolean => {
                      if (!text || !searchTerm) return false;
                      searchTerm = searchTerm.toLowerCase().trim();
                      text = text.toLowerCase().trim();
                      if (searchTerm.length === 0) return true;
                      if (searchTerm.length > text.length) return false;
                      
                      let searchIndex = 0;
                      for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                        if (text[i] === searchTerm[searchIndex]) {
                          searchIndex++;
                        }
                      }
                      return searchIndex === searchTerm.length;
                    };
                    
                    // Filtrar plataformas según el término de búsqueda
                    const filteredPlatforms = getOrderedPlatforms().filter((platform: any) => {
                      const searchTerm = previewPlatformsSearchTerm.trim();
                      if (!searchTerm) return true;
                      
                      let platformName = '';
                      
                      // Obtener el nombre de la plataforma según su tipo
                      if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
                        platformName = t('preview_modal_info_sponsorship.voletaje');
                      } else if (platform.isBase && platform.id) {
                        const basePlatform = basePlatforms.find(bp => bp.id === platform.id);
                        platformName = basePlatform?.name || '';
                      } else if (!platform.isBase && !platform.isLegacy) {
                        platformName = platform.name || `${t('preview_modal_info_sponsorship.customPlatform')}`;
                      } else if (platform.isLegacy) {
                        platformName = customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform');
                      }
                      
                      return isSubsequence(searchTerm, platformName);
                    });
                    
                    if (filteredPlatforms.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
                              {getOrderedPlatforms().length === 0
                                ? 'No hay plataformas disponibles'
                                : 'No se encontraron plataformas que coincidan con la búsqueda'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return filteredPlatforms.map((platform, filteredIndex) => {
                      // Encontrar el índice original en la lista completa
                      const orderedPlatforms = getOrderedPlatforms();
                      const originalIndex = orderedPlatforms.findIndex((p: any, i: number) => {
                        const pId = getPlatformId(p, i);
                        const platformId = getPlatformId(platform, filteredIndex);
                        return pId === platformId;
                      });
                      const actualIndex = originalIndex >= 0 ? originalIndex : filteredIndex;
                      const platformId = getPlatformId(platform, actualIndex);
                      const canMoveUp = actualIndex > 0;
                      const canMoveDown = actualIndex < orderedPlatforms.length - 1;
                  
                  // Renderizar Voletaje
                  if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
                    return (
                      <TableRow 
                        key={platformId}
                        draggable
                        onDragStart={(e) => handleDragStart(e, platformId)}
                        onDragOver={(e) => handleDragOver(e, platformId)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, platformId)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          cursor: 'move',
                          opacity: draggedPlatformId === platformId ? 0.5 : 1,
                          backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : 'transparent',
                          '&:hover': {
                            backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : '#f5f5f5'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformUp(platformId)}
                              disabled={!canMoveUp}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveUp ? 1 : 0.3
                              }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformDown(platformId)}
                              disabled={!canMoveDown}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveDown ? 1 : 0.3
                              }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, backgroundColor: '#ff6b35', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>V</Typography>
                            </Box>
                            {t('preview_modal_info_sponsorship.voletaje')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={t('preview_modal_info_sponsorship.yes')} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography>{t('preview_modal_info_sponsorship.voletaje_description')}</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowImagePreview(true)}
                              sx={{ 
                                borderColor: '#ff6b35',
                                color: '#ff6b35',
                                '&:hover': {
                                  borderColor: '#e55a2b',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.preview1')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowSmallFlyerPreview(true)}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': {
                                  borderColor: '#388e3c',
                                  backgroundColor: '#f1f8e9'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.preview2')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowInstagramPreviewV3(true)}
                              sx={{ 
                                borderColor: '#2196f3',
                                color: '#2196f3',
                                '&:hover': {
                                  borderColor: '#1976d2',
                                  backgroundColor: '#e3f2fd'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.preview3')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => generateWordDocSimple(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openWordConfigModal(platform)}
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                ml: 1,
                                '&:hover': {
                                  borderColor: '#f57c00',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              Config
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  // Renderizar Plataformas Base
                  if (platform.isBase && platform.id && platform.id !== 'voletaje') {
                    const basePlatform = basePlatforms.find(bp => bp.id === platform.id);
                    if (!basePlatform) return null;
                    
                    return (
                      <TableRow 
                        key={platformId}
                        draggable
                        onDragStart={(e) => handleDragStart(e, platformId)}
                        onDragOver={(e) => handleDragOver(e, platformId)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, platformId)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          cursor: 'move',
                          opacity: draggedPlatformId === platformId ? 0.5 : 1,
                          backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : 'transparent',
                          '&:hover': {
                            backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : '#f5f5f5'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformUp(platformId)}
                              disabled={!canMoveUp}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveUp ? 1 : 0.3
                              }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformDown(platformId)}
                              disabled={!canMoveDown}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveDown ? 1 : 0.3
                              }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, backgroundColor: '#7c3aed', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {basePlatform.image ? (
                                <img 
                                  src={basePlatform.image} 
                                  alt={basePlatform.name}
                                  style={{ width: '24px', height: '24px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                              ) : (
                                <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>P</Typography>
                              )}
                            </Box>
                            {basePlatform.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={t('preview_modal_info_sponsorship.yes')} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography>{t('preview_modal_info_sponsorship.basePlatformDescription')}</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSelectedBasePlatformForPreview(basePlatform);
                                setShowBasePlatformPreview(true);
                              }}
                              sx={{ 
                                borderColor: '#7c3aed',
                                color: '#7c3aed',
                                '&:hover': {
                                  borderColor: '#6d28d9',
                                  backgroundColor: '#f3e8ff'
                                }
                              }}
                            >
                              {t('platforms.thumbnail')} {basePlatform.name}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => generateWordDocSimple(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openWordConfigModal(platform)}
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                ml: 1,
                                '&:hover': {
                                  borderColor: '#f57c00',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              Config
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  // Renderizar Plataformas Personalizadas
                  if (!platform.isBase && !platform.isLegacy) {
                    const plat = platform;
                    return (
                      <TableRow 
                        key={platformId}
                        draggable
                        onDragStart={(e) => handleDragStart(e, platformId)}
                        onDragOver={(e) => handleDragOver(e, platformId)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, platformId)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          cursor: 'move',
                          opacity: draggedPlatformId === platformId ? 0.5 : 1,
                          backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : 'transparent',
                          '&:hover': {
                            backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : '#f5f5f5'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformUp(platformId)}
                              disabled={!canMoveUp}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveUp ? 1 : 0.3
                              }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformDown(platformId)}
                              disabled={!canMoveDown}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveDown ? 1 : 0.3
                              }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, backgroundColor: '#9c27b0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>C</Typography>
                            </Box>
                            {plat.name || `${t('preview_modal_info_sponsorship.customPlatform')} ${actualIndex+1}`}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={t('preview_modal_info_sponsorship.yes')} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography>{t('preview_modal_info_sponsorship.customPlatformDescription')}</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const platformData = plat as any;
                                setCustomPlatformImage(platformData.image || null);
                                setFlyerPosition({
                                  x: platformData.x || 50,
                                  y: platformData.y || 50,
                                  width: platformData.width || 200,
                                  height: platformData.height || 150
                                });
                                setCustomPlatformName(platformData.name || t('preview_modal_info_sponsorship.customPlatform'));
                                setPlatformShape(((platformData as any).Image_form || (platformData as any).shape || 'circular') as 'circular' | 'square');
                                setPlatformLogoUrl((platformData as any).logo_url || '');
                                setPlatformCustomText((platformData as any).custom_text || 'your ad goes here');
                                setPlatformFontSize((platformData as any).Text_size || 0.9);
                                setLogoSize({
                                  width: (platformData as any).logo_width || 60,
                                  height: (platformData as any).logo_height || 60
                                });
                                setTempWidth(String(platformData.width || 200));
                                setTempHeight(String(platformData.height || 150));
                                setTempLogoWidth(String((platformData as any).logo_width || 60));
                                setTempLogoHeight(String((platformData as any).logo_height || 60));
                                if (formData.custom_platforms) {
                                  const customPlatformIndex = formData.custom_platforms.findIndex(p => 
                                    p.name === platformData.name && 
                                    p.image === platformData.image &&
                                    (p as any).contest_id === platformData.contest_id
                                  );
                                  setEditingPlatformIndex(customPlatformIndex >= 0 ? customPlatformIndex : actualIndex);
                                } else {
                                  setEditingPlatformIndex(actualIndex);
                                }
                                setShowAddPlatformModal(true);
                              }}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.adjustPreview')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setTempPreviewPlatform(null);
                                const customPlatforms = getFilteredCustomPlatforms();
                                const actualIndex = customPlatforms.findIndex(p => 
                                  p.name === plat.name && 
                                  p.image === plat.image
                                );
                                setSelectedPlatformIndex(actualIndex >= 0 ? actualIndex : index);
                                setShowCustomPlatformPreview(true);
                              }}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': {
                                  borderColor: '#388e3c',
                                  backgroundColor: '#f1f8e9'
                                }
                              }}
                            >
                              {t('platforms.thumbnail')} {plat.name || `${t('preview_modal_info_sponsorship.customPlatform')} ${actualIndex+1}`}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSinglePlatformWord(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openWordConfigModal(platform)}
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                ml: 1,
                                '&:hover': {
                                  borderColor: '#f57c00',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              Config
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={async () => {
                                if (await appDialog.confirm(
                                  t('preview_modal_info_sponsorship.deleteConfirm') || `¿Estás seguro de que deseas eliminar "${plat.name || t('preview_modal_info_sponsorship.customPlatform')}"?`,
                                  { destructive: true }
                                )) {
                                  const platformData = plat as any;
                                  if (formData.custom_platforms) {
                                    const actualIndex = formData.custom_platforms.findIndex(p => 
                                      p.name === platformData.name && 
                                      p.image === platformData.image &&
                                      (p as any).contest_id === platformData.contest_id
                                    );
                                    if (actualIndex >= 0) {
                                      setFormData(prev => {
                                        const updatedPlatforms = [...(prev.custom_platforms || [])];
                                        updatedPlatforms.splice(actualIndex, 1);
                                        return {
                                          ...prev,
                                          custom_platforms: updatedPlatforms
                                        };
                                      });
                                      // Actualizar el orden removiendo la plataforma eliminada
                                      setPlatformOrder(prev => prev.filter(id => id !== platformId));
                                    }
                                  }
                                }
                              }}
                              sx={{ 
                                borderColor: '#f44336',
                                color: '#f44336',
                                '&:hover': {
                                  borderColor: '#d32f2f',
                                  backgroundColor: '#ffebee'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.delete') || 'Eliminar'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  // Renderizar Plataforma Legacy
                  if (platform.isLegacy) {
                    return (
                      <TableRow 
                        key={platformId}
                        draggable
                        onDragStart={(e) => handleDragStart(e, platformId)}
                        onDragOver={(e) => handleDragOver(e, platformId)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, platformId)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          cursor: 'move',
                          opacity: draggedPlatformId === platformId ? 0.5 : 1,
                          backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : 'transparent',
                          '&:hover': {
                            backgroundColor: dragOverPlatformId === platformId ? '#e3f2fd' : '#f5f5f5'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformUp(platformId)}
                              disabled={!canMoveUp}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveUp ? 1 : 0.3
                              }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => movePlatformDown(platformId)}
                              disabled={!canMoveDown}
                              sx={{ 
                                padding: '4px',
                                opacity: canMoveDown ? 1 : 0.3
                              }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, backgroundColor: '#9c27b0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>C</Typography>
                            </Box>
                            {customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={t('preview_modal_info_sponsorship.yes')} color="success" size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography>{t('preview_modal_info_sponsorship.customPlatformDescription')}</Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setCustomPlatformImage(formData.custom_platform_image || null);
                                setFlyerPosition({
                                  x: formData.flyer_position?.x || 50,
                                  y: formData.flyer_position?.y || 50,
                                  width: formData.flyer_position?.width || 200,
                                  height: formData.flyer_position?.height || 150
                                });
                                setCustomPlatformName(formData.custom_platform_name || t('preview_modal_info_sponsorship.customPlatform'));
                                setPlatformShape('circular');
                                setPlatformLogoUrl('');
                                setPlatformCustomText('your ad goes here');
                                setPlatformFontSize(0.9);
                                setLogoSize({ width: 60, height: 60 });
                                setTempWidth(String(formData.flyer_position?.width || 200));
                                setTempHeight(String(formData.flyer_position?.height || 150));
                                setTempLogoWidth('60');
                                setTempLogoHeight('60');
                                setEditingPlatformIndex(null);
                                setShowAddPlatformModal(true);
                              }}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.adjustPreview')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowCustomPlatformPreview(true)}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': {
                                  borderColor: '#388e3c',
                                  backgroundColor: '#f1f8e9'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.viewPreview')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSinglePlatformWord(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openWordConfigModal(platform)}
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                ml: 1,
                                '&:hover': {
                                  borderColor: '#f57c00',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              Config
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  return null;
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          ) : (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 3,
              alignItems: 'stretch'
            }}>
              {getOrderedPlatforms().map((platform, index) => {
                const platformId = getPlatformId(platform, index);
                const orderedPlatforms = getOrderedPlatforms();
                const canMoveUp = index > 0;
                const canMoveDown = index < orderedPlatforms.length - 1;
                
                // Renderizar Voletaje
                if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
                  return (
                    <Box 
                      key={platformId}
                      draggable
                      onDragStart={(e) => handleDragStart(e, platformId)}
                      onDragOver={(e) => handleDragOver(e, platformId)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, platformId)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        cursor: 'move',
                        opacity: draggedPlatformId === platformId ? 0.5 : 1,
                        transform: dragOverPlatformId === platformId ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.2s',
                        border: dragOverPlatformId === platformId ? '2px dashed #1976d2' : '2px solid transparent',
                        borderRadius: 1
                      }}
                    >
                      <Card sx={{ 
                        height: '100%',
                        minHeight: '300px',
                        borderLeft: '4px solid #1e3a8a',
                        backgroundColor: '#e8f5e9',
                        display: 'flex',
                        flexDirection: platformImageOrientations[platformId] === 'horizontal' ? 'column' : 'row',
                        alignItems: 'stretch',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                      }}>
                        {/* Vista previa de la imagen */}
                        <Box sx={{ 
                          width: platformImageOrientations[platformId] === 'horizontal' ? '100%' : '60%', 
                          height: platformImageOrientations[platformId] === 'horizontal' ? '250px' : '100%',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: platformImageOrientations[platformId] === 'horizontal' ? 0.5 : 0,
                          backgroundColor: '#EBF9FF',
                          borderRight: platformImageOrientations[platformId] === 'horizontal' ? 'none' : '1px solid #e0e0e0',
                          borderBottom: platformImageOrientations[platformId] === 'horizontal' ? '1px solid #e0e0e0' : 'none',
                          overflow: 'hidden'
                        }}>
                          {(() => {
                            const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
                            if (!voletajeBase) return null;
                            
                            const previewImage = voletajeBase.image_url || voletajeBase.image;
                            if (!previewImage) return null;
                            
                            return (
                              <Box
                                component="img"
                                src={previewImage}
                                alt="Vista previa"
                                onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                  const img = e.currentTarget;
                                  if (img && img.naturalWidth && img.naturalHeight) {
                                    const isHorizontal = img.naturalWidth > img.naturalHeight;
                                    setPlatformImageOrientations(prev => ({
                                      ...prev,
                                      [platformId]: isHorizontal ? 'horizontal' : 'vertical'
                                    }));
                                  }
                                }}
                                sx={{
                                  width: platformImageOrientations[platformId] === 'vertical' ? '120%' : '100%',
                                  height: platformImageOrientations[platformId] === 'vertical' ? '120%' : '100%',
                                  objectFit: platformImageOrientations[platformId] === 'horizontal' ? 'cover' : 'contain',
                                  objectPosition: platformImageOrientations[platformId] === 'horizontal' ? 'top center' : 'center center',
                                  borderRadius: 1
                                }}
                              />
                            );
                          })()}
                        </Box>
                        
                        {/* Datos y botones */}
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          width: platformImageOrientations[platformId] === 'horizontal' ? '100%' : '20%', 
                          display: 'flex', 
                          flexDirection: 'column' 
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              {t('preview_modal_info_sponsorship.voletaje')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformUp(platformId)}
                                disabled={!canMoveUp}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveUp ? 1 : 0.3
                                }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformDown(platformId)}
                                disabled={!canMoveDown}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveDown ? 1 : 0.3
                                }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('preview_modal_info_sponsorship.voletaje_description')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowImagePreview(true)}
                              sx={{ 
                                borderColor: '#ff6b35',
                                color: '#ff6b35',
                                '&:hover': {
                                  borderColor: '#e55a2b',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.preview1')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowSmallFlyerPreview(true)}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': {
                                  borderColor: '#388e3c',
                                  backgroundColor: '#f1f8e9'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.preview2')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowInstagramPreviewV3(true)}
                              sx={{ 
                                borderColor: '#2196f3',
                                color: '#2196f3',
                                '&:hover': {
                                  borderColor: '#1976d2',
                                  backgroundColor: '#e3f2fd'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.preview3')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSinglePlatformWord(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }
                
                // Renderizar Plataformas Base
                if (platform.isBase && platform.id && platform.id !== 'voletaje') {
                  const basePlatform = basePlatforms.find(bp => bp.id === platform.id);
                  if (!basePlatform) return null;
                  
                  return (
                    <Box 
                      key={platformId}
                      draggable
                      onDragStart={(e) => handleDragStart(e, platformId)}
                      onDragOver={(e) => handleDragOver(e, platformId)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, platformId)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        cursor: 'move',
                        opacity: draggedPlatformId === platformId ? 0.5 : 1,
                        transform: dragOverPlatformId === platformId ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.2s',
                        border: dragOverPlatformId === platformId ? '2px dashed #1976d2' : '2px solid transparent',
                        borderRadius: 1
                      }}
                    >
                      <Card sx={{ 
                        height: '100%',
                        minHeight: '300px',
                        borderLeft: '4px solid #1e3a8a',
                        backgroundColor: '#e8f5e9',
                        display: 'flex',
                        flexDirection: platformImageOrientations[platformId] === 'horizontal' ? 'column' : 'row',
                        alignItems: 'stretch',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                      }}>
                        {/* Vista previa de la imagen */}
                        <Box sx={{ 
                          width: platformImageOrientations[platformId] === 'horizontal' ? '100%' : '60%', 
                          height: platformImageOrientations[platformId] === 'horizontal' ? '250px' : '100%',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: platformImageOrientations[platformId] === 'horizontal' ? 0.5 : 0,
                          backgroundColor: '#EBF9FF',
                          borderRight: platformImageOrientations[platformId] === 'horizontal' ? 'none' : '1px solid #e0e0e0',
                          borderBottom: platformImageOrientations[platformId] === 'horizontal' ? '1px solid #e0e0e0' : 'none',
                          overflow: 'hidden'
                        }}>
                          {basePlatform.image_url || basePlatform.image ? (
                            <OptimizedImage
                              src={basePlatform.image_url || basePlatform.image}
                              alt={basePlatform.name}
                              onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                const img = e.currentTarget;
                                if (img && img.naturalWidth && img.naturalHeight) {
                                  const isHorizontal = img.naturalWidth > img.naturalHeight;
                                  setPlatformImageOrientations(prev => ({
                                    ...prev,
                                    [platformId]: isHorizontal ? 'horizontal' : 'vertical'
                                  }));
                                }
                              }}
                              sx={{
                                width: platformImageOrientations[platformId] === 'vertical' ? '120%' : '100%',
                                height: platformImageOrientations[platformId] === 'vertical' ? '120%' : '100%',
                                objectFit: platformImageOrientations[platformId] === 'horizontal' ? 'cover' : 'contain',
                                objectPosition: platformImageOrientations[platformId] === 'horizontal' ? 'top center' : 'center center',
                                borderRadius: 1
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sin imagen
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Datos y botones */}
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          width: platformImageOrientations[platformId] === 'horizontal' ? '100%' : '40%', 
                          display: 'flex', 
                          flexDirection: 'column' 
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              {basePlatform.name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformUp(platformId)}
                                disabled={!canMoveUp}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveUp ? 1 : 0.3
                                }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformDown(platformId)}
                                disabled={!canMoveDown}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveDown ? 1 : 0.3
                                }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('preview_modal_info_sponsorship.basePlatformDescription')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSelectedBasePlatformForPreview(basePlatform);
                                setShowBasePlatformPreview(true);
                              }}
                              sx={{ 
                                borderColor: '#7c3aed',
                                color: '#7c3aed',
                                '&:hover': {
                                  borderColor: '#6d28d9',
                                  backgroundColor: '#f3e8ff'
                                }
                              }}
                            >
                              {t('platforms.thumbnail')} {basePlatform.name}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSinglePlatformWord(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openWordConfigModal(platform)}
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                ml: 1,
                                '&:hover': {
                                  borderColor: '#f57c00',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              Config
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }
                
                // Renderizar Plataformas Personalizadas
                if (!platform.isBase && !platform.isLegacy) {
                  const plat = platform;
                  return (
                    <Box 
                      key={platformId}
                      draggable
                      onDragStart={(e) => handleDragStart(e, platformId)}
                      onDragOver={(e) => handleDragOver(e, platformId)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, platformId)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        cursor: 'move',
                        opacity: draggedPlatformId === platformId ? 0.5 : 1,
                        transform: dragOverPlatformId === platformId ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.2s',
                        border: dragOverPlatformId === platformId ? '2px dashed #1976d2' : '2px solid transparent',
                        borderRadius: 1
                      }}
                    >
                      <Card sx={{ 
                        height: '100%',
                        minHeight: '300px',
                        borderLeft: '4px solid #1e3a8a',
                        backgroundColor: '#e8f5e9',
                        display: 'flex',
                        flexDirection: platformImageOrientations[platformId] === 'horizontal' ? 'column' : 'row',
                        alignItems: 'stretch',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)'
                      }}>
                        {/* Vista previa de la imagen */}
                        <Box sx={{ 
                          width: platformImageOrientations[platformId] === 'horizontal' ? '100%' : '60%', 
                          height: platformImageOrientations[platformId] === 'horizontal' ? '250px' : '100%',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          p: platformImageOrientations[platformId] === 'horizontal' ? 0.5 : 0,
                          backgroundColor: '#EBF9FF',
                          borderRight: platformImageOrientations[platformId] === 'horizontal' ? 'none' : '1px solid #e0e0e0',
                          borderBottom: platformImageOrientations[platformId] === 'horizontal' ? '1px solid #e0e0e0' : 'none',
                          overflow: 'hidden'
                        }}>
                          {plat.image ? (
                            <Box
                              component="img"
                              src={plat.image}
                              alt={plat.name || 'Plataforma personalizada'}
                              onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                const img = e.currentTarget;
                                if (img && img.naturalWidth && img.naturalHeight) {
                                  const isHorizontal = img.naturalWidth > img.naturalHeight;
                                  setPlatformImageOrientations(prev => ({
                                    ...prev,
                                    [platformId]: isHorizontal ? 'horizontal' : 'vertical'
                                  }));
                                }
                              }}
                              sx={{
                                width: platformImageOrientations[platformId] === 'vertical' ? '120%' : '100%',
                                height: platformImageOrientations[platformId] === 'vertical' ? '120%' : '100%',
                                objectFit: platformImageOrientations[platformId] === 'horizontal' ? 'cover' : 'contain',
                                objectPosition: platformImageOrientations[platformId] === 'horizontal' ? 'top center' : 'center center',
                                borderRadius: 1
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sin imagen
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Datos y botones */}
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          width: platformImageOrientations[platformId] === 'horizontal' ? '100%' : '40%', 
                          display: 'flex', 
                          flexDirection: 'column' 
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              {plat.name || `${t('preview_modal_info_sponsorship.customPlatform')} ${index+1}`}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformUp(platformId)}
                                disabled={!canMoveUp}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveUp ? 1 : 0.3
                                }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformDown(platformId)}
                                disabled={!canMoveDown}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveDown ? 1 : 0.3
                                }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('preview_modal_info_sponsorship.customPlatformDescription')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                const platformData = plat as any;
                                setCustomPlatformImage(platformData.image || null);
                                setFlyerPosition({
                                  x: platformData.x || 50,
                                  y: platformData.y || 50,
                                  width: platformData.width || 200,
                                  height: platformData.height || 150
                                });
                                setCustomPlatformName(platformData.name || t('preview_modal_info_sponsorship.customPlatform'));
                                setPlatformShape(((platformData as any).Image_form || (platformData as any).shape || 'circular') as 'circular' | 'square');
                                setPlatformLogoUrl((platformData as any).logo_url || '');
                                setPlatformCustomText((platformData as any).custom_text || 'your ad goes here');
                                setPlatformFontSize((platformData as any).Text_size || 0.9);
                                setLogoSize({
                                  width: (platformData as any).logo_width || 60,
                                  height: (platformData as any).logo_height || 60
                                });
                                setTempWidth(String(platformData.width || 200));
                                setTempHeight(String(platformData.height || 150));
                                setTempLogoWidth(String((platformData as any).logo_width || 60));
                                setTempLogoHeight(String((platformData as any).logo_height || 60));
                                if (formData.custom_platforms) {
                                  const customPlatformIndex = formData.custom_platforms.findIndex(p => 
                                    p.name === platformData.name && 
                                    p.image === platformData.image &&
                                    (p as any).contest_id === platformData.contest_id
                                  );
                                  setEditingPlatformIndex(customPlatformIndex >= 0 ? customPlatformIndex : actualIndex);
                                } else {
                                  setEditingPlatformIndex(actualIndex);
                                }
                                setShowAddPlatformModal(true);
                              }}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.adjustPreview')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setTempPreviewPlatform(null);
                                const customPlatforms = getFilteredCustomPlatforms();
                                const actualIndex = customPlatforms.findIndex(p => 
                                  p.name === plat.name && 
                                  p.image === plat.image
                                );
                                setSelectedPlatformIndex(actualIndex >= 0 ? actualIndex : index);
                                setShowCustomPlatformPreview(true);
                              }}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': {
                                  borderColor: '#388e3c',
                                  backgroundColor: '#f1f8e9'
                                }
                              }}
                            >
                              {t('platforms.thumbnail')} {plat.name || `${t('preview_modal_info_sponsorship.customPlatform')} ${actualIndex+1}`}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSinglePlatformWord(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openWordConfigModal(platform)}
                              sx={{ 
                                borderColor: '#ff9800',
                                color: '#ff9800',
                                ml: 1,
                                '&:hover': {
                                  borderColor: '#f57c00',
                                  backgroundColor: '#fff3e0'
                                }
                              }}
                            >
                              Config
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={async () => {
                                if (await appDialog.confirm(
                                  t('preview_modal_info_sponsorship.deleteConfirm') || `¿Estás seguro de que deseas eliminar "${plat.name || t('preview_modal_info_sponsorship.customPlatform')}"?`,
                                  { destructive: true }
                                )) {
                                  const platformData = plat as any;
                                  if (formData.custom_platforms) {
                                    const actualIndex = formData.custom_platforms.findIndex(p => 
                                      p.name === platformData.name && 
                                      p.image === platformData.image &&
                                      (p as any).contest_id === platformData.contest_id
                                    );
                                    if (actualIndex >= 0) {
                                      setFormData(prev => {
                                        const updatedPlatforms = [...(prev.custom_platforms || [])];
                                        updatedPlatforms.splice(actualIndex, 1);
                                        return {
                                          ...prev,
                                          custom_platforms: updatedPlatforms
                                        };
                                      });
                                      setPlatformOrder(prev => prev.filter(id => id !== platformId));
                                    }
                                  }
                                }
                              }}
                              sx={{ 
                                borderColor: '#f44336',
                                color: '#f44336',
                                '&:hover': {
                                  borderColor: '#d32f2f',
                                  backgroundColor: '#ffebee'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.delete') || 'Eliminar'}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }
                
                // Renderizar Plataforma Legacy
                if (platform.isLegacy) {
                  return (
                    <Box 
                      key={platformId}
                      draggable
                      onDragStart={(e) => handleDragStart(e, platformId)}
                      onDragOver={(e) => handleDragOver(e, platformId)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, platformId)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        cursor: 'move',
                        opacity: draggedPlatformId === platformId ? 0.5 : 1,
                        transform: dragOverPlatformId === platformId ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.2s',
                        border: dragOverPlatformId === platformId ? '2px dashed #1976d2' : '2px solid transparent',
                        borderRadius: 1
                      }}
                    >
                      <Card sx={{ 
                        height: '100%',
                        borderLeft: '4px solid #1e3a8a',
                        backgroundColor: '#e8f5e9',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 32, height: 32, backgroundColor: '#9c27b0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>C</Typography>
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                {customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform')}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformUp(platformId)}
                                disabled={!canMoveUp}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveUp ? 1 : 0.3
                                }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => movePlatformDown(platformId)}
                                disabled={!canMoveDown}
                                sx={{ 
                                  padding: '4px',
                                  opacity: canMoveDown ? 1 : 0.3
                                }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('preview_modal_info_sponsorship.customPlatformDescription')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setCustomPlatformImage(formData.custom_platform_image || null);
                                setFlyerPosition({
                                  x: formData.flyer_position?.x || 50,
                                  y: formData.flyer_position?.y || 50,
                                  width: formData.flyer_position?.width || 200,
                                  height: formData.flyer_position?.height || 150
                                });
                                setCustomPlatformName(formData.custom_platform_name || t('preview_modal_info_sponsorship.customPlatform'));
                                setPlatformShape('circular');
                                setPlatformLogoUrl('');
                                setPlatformCustomText('your ad goes here');
                                setPlatformFontSize(0.9);
                                setLogoSize({ width: 60, height: 60 });
                                setTempWidth(String(formData.flyer_position?.width || 200));
                                setTempHeight(String(formData.flyer_position?.height || 150));
                                setTempLogoWidth('60');
                                setTempLogoHeight('60');
                                setEditingPlatformIndex(null);
                                setShowAddPlatformModal(true);
                              }}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.adjustPreview')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setShowCustomPlatformPreview(true)}
                              sx={{ 
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': {
                                  borderColor: '#388e3c',
                                  backgroundColor: '#f1f8e9'
                                }
                              }}
                            >
                              {t('preview_modal_info_sponsorship.viewPreview')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSinglePlatformWord(platform)}
                              sx={{ 
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                  borderColor: '#7b1fa2',
                                  backgroundColor: '#f3e5f5'
                                }
                              }}
                            >
                              Word
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                }
                
                return null;
              })}
            </Box>
          </Box>
          )}
          
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('preview_modal_info_sponsorship.sponsorshipType')}</strong> {formData.sponsorship_type === 'rewards_only' ? t('preview_modal_info_sponsorship.rewardsOnly') : t('preview_modal_info_sponsorship.branding')}
            </Typography>
            {formData.sponsorship_type === 'rewards_only' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('preview_modal_info_sponsorship.rewards_only_description')}
              </Typography>
            )}
            {formData.sponsorship_type === 'branding_only' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('preview_modal_info_sponsorship.branding_only_description')}
              </Typography>
            )}
          </Box>

          {/* Botón Añadir Plataforma */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                // Limpiar estados para nueva plataforma
                setCustomPlatformImage(null);
                setCustomPlatformName(t('preview_modal_info_sponsorship.customPlatform'));
                setFlyerPosition({ x: 50, y: 50, width: 200, height: 150 });
                setEditingPlatformIndex(null);
                setPreviewImageDimensions(null);
                setShowAddPlatformModal(true);
              }}
              sx={{
                borderColor: '#ff6b35',
                color: '#ff6b35',
                '&:hover': {
                  borderColor: '#e55a2b',
                  backgroundColor: '#fff3e0'
                },
                px: 3,
                py: 1.5
              }}
            >
              {t('preview_modal_info_sponsorship.addPlatform')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewModal(false)}>
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Configuración de Previews */}
      <Dialog
        open={showPreviewConfigModal}
        onClose={() => setShowPreviewConfigModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          {t('preview_modal_info_sponsorship.configurePreviews')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('preview_modal_info_sponsorship.selectPreviewsDescription')}
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={visiblePreviews.voletaje}
                  onChange={(e) => setVisiblePreviews({ ...visiblePreviews, voletaje: e.target.checked })}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 24, height: 24, backgroundColor: '#ff6b35', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>V</Typography>
                  </Box>
                  <Typography>{t('preview_modal_info_sponsorship.voletaje')}</Typography>
                </Box>
              }
              sx={{ display: 'flex', mb: 2 }}
            />

            {/* Plataformas Base Dinámicas */}
            {basePlatforms
              .filter(platform => (formData.enabled_base_platforms || []).includes(platform.id))
              .map((platform) => (
                <FormControlLabel
                  key={platform.id}
                  control={
                    <Checkbox
                      checked={visibleBasePlatforms[platform.id] || false}
                      onChange={(e) => setVisibleBasePlatforms({ ...visibleBasePlatforms, [platform.id]: e.target.checked })}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, backgroundColor: '#7c3aed', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {platform.image ? (
                          <img 
                            src={platform.image} 
                            alt={platform.name}
                            style={{ width: '24px', height: '24px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ) : (
                          <Typography sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>P</Typography>
                        )}
                      </Box>
                      <Typography>{platform.name}</Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', mb: 2 }}
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewConfigModal(false)}>
            {t('preview_modal_info_sponsorship.close')}
          </Button>
          <Button 
            onClick={() => {
              setVisiblePreviews({ facebook: false, instagram: false, voletaje: true });
              // Restaurar todas las plataformas base habilitadas
              const allEnabledBasePlatforms: Record<string, boolean> = {};
              basePlatforms
                .filter(platform => (formData.enabled_base_platforms || []).includes(platform.id))
                .forEach(platform => {
                  allEnabledBasePlatforms[platform.id] = true;
                });
              setVisibleBasePlatforms(allEnabledBasePlatforms);
            }}
            variant="outlined"
          >
            {t('preview_modal_info_sponsorship.restoreDefault')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog
        open={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EyeIcon />
          {t('preview_modal_info_sponsorship.previewVoletajeAd')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            backgroundColor: '#f5f5dc', // Beige background
            p: 3,
            borderRadius: 2,
            position: 'relative',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {/* Top Right Corner */}
            <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
              <Typography variant="caption" sx={{ color: 'black', fontWeight: 'bold' }}>
                {t('preview_modal_info_sponsorship.availableInAppleStore')}
              </Typography>
            </Box>


            {/* Logo Section - Top Center */}
            {formData.media_assets.logo_url && (
              <Box sx={{ 
                textAlign: 'center', 
                mt: 0, 
                mb: 0.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  backgroundColor: '#ff69b4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '3px solid black'
                }}>
                  <img 
                    src={formData.media_assets.logo_url} 
                    alt="Logo" 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Main Title with Black Frame */}
            <Box sx={{ 
              textAlign: 'center', 
              mt: 0, 
              mb: 1,
              position: 'relative',
              display: 'inline-block',
              mx: 'auto',
              p: 2
            }}>
              {/* Black Frame */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '4px solid black',
                borderRadius: 0,
                zIndex: 1
              }} />
              
              {/* Title Content */}
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    color: '#ff6b35', 
                    fontWeight: 'bold', 
                    fontSize: '2.5rem',
                    mb: 1,
                    textTransform: 'uppercase',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()}
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: 'black', 
                    fontWeight: 'bold', 
                    fontSize: '1.8rem',
                    textTransform: 'uppercase'
                  }}
                >
                  CAPTURE THE PURRFECT SPOOKY
                </Typography>
              </Box>
            </Box>

            {/* Prize Information */}
            <Box sx={{ 
              position: 'absolute', 
              top: 110, 
              right: 10, 
              backgroundColor: '#ff6b35', 
              p: 2, 
              borderRadius: 1,
              minWidth: '250px',
              maxWidth: '250px'
            }}>
              <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold', mb: 0.2, fontSize: '1.2rem' }}>
                1ST PLACE PRIZE ${formData.reward_contribution || '50.00'}
              </Typography>
              <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold', mb: 0.2, fontSize: '1.2rem' }}>
                2ND PLACE PRIZE ${Math.round((formData.reward_contribution || 50) * 0.5)}.00
              </Typography>
              <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold', mb: 0.2, fontSize: '1.2rem' }}>
                3RD PLACE PRIZE ${Math.round((formData.reward_contribution || 50) * 0.3)}.00
              </Typography>
              <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold', fontSize: '1.2rem' }}>
                4TH PLACE PRIZE ${Math.round((formData.reward_contribution || 50) * 0.2)}.00
              </Typography>
            </Box>

            {/* Slogan - Compact with Large Text */}
            <Box sx={{ 
              width: '80%',
              textAlign: 'center', 
              my: 1.5,
              backgroundColor: 'rgba(0,0,0,0.05)',
              py: 1.5,
              px: 2,
              borderRadius: 1,
              mx: 'auto'
            }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  color: 'black', 
                  fontWeight: 'bold',
                  fontSize: '2.8rem',
                  textTransform: 'uppercase',
                  lineHeight: 1.0,
                  mb: 0.3
                }}
              >
                CAPTURE THE
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ 
                  color: 'black', 
                  fontWeight: 'bold',
                  fontSize: '2.8rem',
                  textTransform: 'uppercase',
                  lineHeight: 1.0
                }}
              >
                PURRFECT SPOOKY MOMENT
              </Typography>
            </Box>

            {/* How to Enter Section */}
            <Box sx={{ 
              backgroundColor: '#ff6b35', 
              p: 2, 
              borderRadius: 1,
              mt: 'auto'
            }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                How to enter:
              </Typography>
              <Box sx={{ color: 'white' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  1. Download our Noah's Ark Pet Connect App (BETA stage)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  2. Upload a photo of your pet in a costume
                </Typography>
                <Typography variant="body1">
                  3. Before you post, select the tag: "Halloween Photo Contest"
                </Typography>
              </Box>
            </Box>

            {/* Contest Closing Date */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'black', 
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}
              >
                CONTEST CLOSES {formData.schedule.end ? new Date(formData.schedule.end).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '/') : '11/1/2025'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImagePreview(false)}>
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instagram Preview Modal */}
      <Dialog
        open={showInstagramPreview}
        onClose={() => setShowInstagramPreview(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {t('preview_modal_info_sponsorship.previewInstagram')}
          </Typography>
          <Button
            onClick={() => setShowInstagramPreview(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, backgroundColor: '#000' }}>
          {/* Phone Frame */}
          <Box sx={{
            width: '100%',
            maxWidth: '375px',
            margin: '0 auto',
            backgroundColor: '#000',
            borderRadius: '25px',
            padding: '20px',
            position: 'relative'
          }}>
            {/* Phone Screen */}
            <Box sx={{
              backgroundColor: '#fff',
              borderRadius: '20px',
              overflow: 'hidden',
              position: 'relative',
              minHeight: '600px'
            }}>
              {/* Status Bar */}
              <Box sx={{
                backgroundColor: '#fff',
                padding: '8px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>10:49</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography sx={{ fontSize: '12px' }}>📶</Typography>
                  <Typography sx={{ fontSize: '12px' }}>📶</Typography>
                  <Typography sx={{ fontSize: '12px' }}>🔋</Typography>
                </Box>
              </Box>

              {/* Instagram Header */}
              <Box sx={{
                padding: '12px 16px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>←</Typography>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>Posts</Typography>
                  <Typography sx={{ fontSize: '12px', color: '#666' }}>noahsarkpc</Typography>
                </Box>
                <Typography sx={{ fontSize: '14px', color: '#0095f6' }}>Follow</Typography>
                <Typography sx={{ fontSize: '18px' }}>⋯</Typography>
              </Box>

              {/* User Info */}
              <Box sx={{
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#ff6b35',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  <img 
                    src="/logo_ark.png" 
                    alt="Noah's Ark Logo" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      console.log('Error loading logo:', e);
                      // Fallback to N if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div style="width: 100%; height: 100%; background-color: #ff6b35; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 14px;">N</div>';
                      }
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>noahsarkpc</Typography>
                <Typography sx={{ fontSize: '18px', marginLeft: 'auto' }}>⋯</Typography>
              </Box>

              {/* Main Content Area - This is where the Voletaje flyer will appear */}
              <Box sx={{
                width: '100%',
                height: '300px',
                backgroundColor: '#f5f5dc', // Light beige color from the phone
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {/* Voletaje Flyer Content */}
                <Box sx={{
                  width: '90%',
                  height: '90%',
                  backgroundColor: '#f5f5dc',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 2
                }}>
                  {/* Logo Section */}
                  {formData.media_assets.logo_url && (
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      border: '2px solid black',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={formData.media_assets.logo_url} 
                        alt="Logo" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }}
                        onError={(e) => {
                          // Fallback to N if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div style="width: 100%; height: 100%; background-color: #ff6b35; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 20px; border-radius: 50%;">N</div>';
                          }
                        }}
                      />
                    </Box>
                  )}

                  {/* Title with Black Frame */}
                  <Box sx={{
                    position: 'relative',
                    display: 'inline-block',
                    mb: 2
                  }}>
                    {/* Black Frame */}
                    <Box sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      right: -4,
                      bottom: -4,
                      border: '3px solid black',
                      zIndex: 1
                    }} />
                    
                    {/* Title Content */}
                    <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                      <Typography sx={{
                        color: '#ff6b35',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        mb: 0.5,
                        textTransform: 'uppercase',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()}
                      </Typography>
                      <Typography sx={{
                        color: 'black',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        textTransform: 'uppercase'
                      }}>
                        CAPTURE THE PURRFECT SPOOKY
                      </Typography>
                    </Box>
                  </Box>

                  {/* Prize Information */}
                  <Box sx={{
                    backgroundColor: '#ff6b35',
                    p: 1.5,
                    borderRadius: 1,
                    minWidth: '200px',
                    textAlign: 'center'
                  }}>
                    <Typography sx={{ color: 'black', fontWeight: 'bold', mb: 0.5, fontSize: '0.9rem' }}>
                      1ST PLACE ${formData.reward_contribution || '50.00'}
                    </Typography>
                    <Typography sx={{ color: 'black', fontWeight: 'bold', mb: 0.5, fontSize: '0.9rem' }}>
                      2ND PLACE ${Math.round((formData.reward_contribution || 50) * 0.5)}.00
                    </Typography>
                    <Typography sx={{ color: 'black', fontWeight: 'bold', mb: 0.5, fontSize: '0.9rem' }}>
                      3RD PLACE ${Math.round((formData.reward_contribution || 50) * 0.25)}.00
                    </Typography>
                    <Typography sx={{ color: 'black', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      4TH PLACE ${Math.round((formData.reward_contribution || 50) * 0.2)}.00
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Engagement Icons */}
              <Box sx={{
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography sx={{ fontSize: '20px' }}>❤️</Typography>
                <Typography sx={{ fontSize: '20px' }}>💬</Typography>
                <Typography sx={{ fontSize: '20px' }}>📤</Typography>
                <Typography sx={{ fontSize: '20px', marginLeft: 'auto' }}>🔖</Typography>
              </Box>

              {/* Caption */}
              <Box sx={{ padding: '0 16px 12px' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>noahsarkpc</Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()} Photo Contest! 🎃
                  <br />
                  Win amazing prizes for your pet's best costume! 
                  <br />
                  Contest closes {formData.schedule.end ? new Date(formData.schedule.end).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '/') : '11/1/2025'}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#666', mt: 1 }}>3 days ago</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowInstagramPreview(false)}
            variant="outlined"
            sx={{ borderColor: '#e6683c', color: '#e6683c' }}
          >
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instagram Preview Modal V2 - Con Phone.png */}
      <Dialog
        open={showInstagramPreviewV2}
        onClose={() => setShowInstagramPreviewV2(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {t('preview_modal_info_sponsorship.previewInstagramFlyer')}
          </Typography>
          <Button
            onClick={() => setShowInstagramPreviewV2(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, backgroundColor: '#000' }}>
          {/* Phone Frame con Phone2.png como fondo */}
          <Box sx={{
            width: '100%',
            maxWidth: '350px', // Reducido de 500px a 350px
            margin: '0 auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '500px' // Reducido de 700px a 500px
          }}>
            {/* Imagen de fondo del teléfono */}
            <img 
              src="/Phone2.png" 
              alt="Phone Frame" 
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '320px', // Reducido de 450px a 320px
                position: 'relative',
                zIndex: 1
              }}
              onError={(e) => {
                console.log('Error loading Phone2.png:', e);
              }}
            />
            
            {/* Volante superpuesto en la sección de color café claro */}
            <Box sx={{
              position: 'absolute',
              top: '17%', // Movido más arriba de 18% a 12%
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80%', // Reducido de 100% a 80%
              maxWidth: '255px', // Reducido de 350px a 220px
              zIndex: 2,
              backgroundColor: '#f5f5dc', // Color café claro
              borderRadius: 1.5, // Reducido de 2 a 1.5
              padding: 1.5, // Reducido de 2 a 1.5
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px' // Reducido de 280px a 160px
            }}>
              {/* Logo Section */}
              {formData.media_assets.logo_url && (
                <Box sx={{
                  width: 120, // Aumentado de 40 a 60
                  height: 120, // Aumentado de 40 a 60
                  borderRadius: '50%',
                  border: '2px solid black', // Aumentado de 1.5px a 2px
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1, // Reducido de 1.5 a 1
                  overflow: 'hidden'
                }}>
                  <img 
                    src={formData.media_assets.logo_url} 
                    alt="Logo" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div style="width: 100%; height: 100%; background-color: #ff6b35; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 16px; border-radius: 50%;">N</div>';
                      }
                    }}
                  />
                </Box>
              )}

              {/* Title with Black Frame */}
              <Box sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 1.5,
                minWidth: '200px', // Hacer el cuadro más ancho
                padding: '8px 16px' // Agregar padding interno
              }}>
                {/* Black Frame */}
                <Box sx={{
                  position: 'absolute',
                  top: -3,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  border: '2px solid black',
                  zIndex: 1
                }} />
                
                {/* Title Content */}
                <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                  <Typography sx={{
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    fontSize: '1.2rem', // Aumentado de 0.9rem a 1.2rem
                    mb: 0.3, // Aumentado de 0.2 a 0.3
                    textTransform: 'uppercase',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()}
                  </Typography>
                  <Typography sx={{
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: '0.9rem', // Aumentado de 0.7rem a 0.9rem
                    textTransform: 'uppercase'
                  }}>
                    {t('preview_modal_info_sponsorship.yourAdGoesHere')}
                  </Typography>
                </Box>
              </Box>

            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowInstagramPreviewV2(false)}
            variant="outlined"
            sx={{ borderColor: '#e6683c', color: '#e6683c' }}
          >
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instagram Preview Modal V3 - Con Voleto_Big.png */}
      <Dialog
        open={showInstagramPreviewV3}
        onClose={() => setShowInstagramPreviewV3(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {t('preview_modal_info_sponsorship.previewInstagramBigFlyer')}
          </Typography>
          <Button
            onClick={() => setShowInstagramPreviewV3(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, backgroundColor: '#f0f0f0' }}>
          {/* Imagen Voleto_Big.png sin marco */}
          <Box sx={{
            width: '100%',
            maxWidth: '600px',
            margin: '0 auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 1
          }}>
            {/* Imagen Voleto_Big.png completa */}
            <img 
              src="/Voleto_Big.png" 
              alt="Volante Grande" 
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '100%',
                position: 'relative',
                zIndex: 1,
                objectFit: 'contain',
                objectPosition: 'center'
              }}
              onError={(e) => {
                console.log('Error loading Voleto_Big.png:', e);
              }}
            />
            
            {/* Volante superpuesto en la sección de color beige */}
            <Box sx={{
              position: 'absolute',
              top: '21%',
              left: '15%',
              transform: 'translateX(-50%)',
              width: '100%',
              maxWidth: '120px',
              zIndex: 2,
              backgroundColor: '#f5f5dc', // Color beige igual al Preview 1
              borderRadius: 2,
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              minHeight: '113px',
              maxHeight: '113px'
            }}>
              {/* Logo Section */}
              {formData.media_assets.logo_url && (
                <Box sx={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  border: '2px solid black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  zIndex: 3
                }}>
                  <img 
                    src={formData.media_assets.logo_url} 
                    alt="Logo" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div style="width: 100%; height: 100%; background-color: #ff6b35; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 20px; border-radius: 50%;">N</div>';
                      }
                    }}
                  />
                </Box>
              )}

              {/* Title with Black Frame */}
              <Box sx={{
                position: 'absolute',
                bottom: '5px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'inline-block',
                minWidth: '100px',
                padding: '8px 12px',
                zIndex: 3
              }}>
                {/* Black Frame */}
                <Box sx={{
                  position: 'absolute',
                  top: -1,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  border: '2px solid black',
                  zIndex: 1
                }} />
                
                {/* Title Content */}
                <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                  <Typography sx={{
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    mb: 0.3,
                    textTransform: 'uppercase',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()}
                  </Typography>
                  <Typography sx={{
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase'
                  }}>
                    {t('preview_modal_info_sponsorship.yourAdGoesHere')}
                  </Typography>
                </Box>
              </Box>

            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowInstagramPreviewV3(false)}
            variant="outlined"
            sx={{ borderColor: '#2196f3', color: '#2196f3' }}
          >
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facebook Preview Modal */}
      <Dialog
        open={showFacebookPreview}
        onClose={() => setShowFacebookPreview(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {t('preview_modal_info_sponsorship.previewFacebook')}
          </Typography>
          <Button
            onClick={() => setShowFacebookPreview(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, backgroundColor: '#f0f2f5' }}>
          {/* Facebook Frame con Facebook.png como fondo */}
          <Box sx={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '600px'
          }}>
            {/* Imagen de fondo de Facebook */}
            <img 
              src="/Facebook.png" 
              alt="Facebook Frame" 
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '800px',
                position: 'relative',
                zIndex: 1
              }}
              onError={(e) => {
                console.log('Error loading Facebook.png:', e);
              }}
            />
            
            {/* Anuncio superpuesto en la mitad superior */}
            <Box sx={{
              position: 'absolute',
              top: '55%', // Posición en la mitad superior
              left: '57.5%',
              transform: 'translateX(-50%)',
              width: '17%', // Ocupa la mitad de la pantalla
              maxWidth: '400px',
              zIndex: 2,
              backgroundColor: '#f5f5dc',
              borderRadius: 1.5,
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '120px',
              maxHeight: '120px'
            }}>
              {/* Logo Section (from form data) */}
              {formData.media_assets.logo_url && (
                <Box sx={{
                  width: 80, // Aumentado de 50 a 80
                  height: 80, // Aumentado de 50 a 80
                  borderRadius: '50%',
                  border: '3px solid black', // Aumentado de 2px a 3px
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                  overflow: 'hidden'
                }}>
                  <img 
                    src={formData.media_assets.logo_url} 
                    alt="Logo" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div style="width: 100%; height: 100%; background-color: #ff6b35; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 20px; border-radius: 50%;">N</div>';
                      }
                    }}
                  />
                </Box>
              )}
              
              {/* Title with Black Frame (dynamic from form data) */}
              <Box sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 1.5
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: -3,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  border: '2px solid black',
                  zIndex: 1
                }} />
                <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                  <Typography sx={{
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    fontSize: '0.8rem', // Reducido de 1.1rem a 0.8rem
                    mb: 0.2, // Reducido de 0.3 a 0.2
                    textTransform: 'uppercase',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()}
                  </Typography>
                  <Typography sx={{
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: '0.9rem', // Aumentado de 0.7rem a 0.9rem
                    textTransform: 'uppercase'
                  }}>
                    {t('preview_modal_info_sponsorship.yourAdGoesHere')}
                  </Typography>
                </Box>
              </Box>
              
            </Box>
            
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowFacebookPreview(false)}
            variant="outlined"
            sx={{ borderColor: '#1877f2', color: '#1877f2' }}
          >
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Small Flyer Preview Modal */}
      <Dialog
        open={showSmallFlyerPreview}
        onClose={() => setShowSmallFlyerPreview(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {t('preview_modal_info_sponsorship.previewVoletajeSmall')}
          </Typography>
          <Button
            onClick={() => setShowSmallFlyerPreview(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, backgroundColor: '#f0f2f5' }}>
          {/* Small Flyer Frame con Voleto_Small.png como fondo */}
          <Box sx={{
            width: '100%',
            maxWidth: '400px',
            margin: '0 auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '500px'
          }}>
            {/* Imagen de fondo del volante pequeño */}
            <img 
              src="/Voleto_Small.png" 
              alt="Small Flyer Template"
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '400px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                console.log('Error loading Voleto_Small.png:', e);
              }}
            />
            
            {/* Contenido superpuesto en el volante pequeño */}
            <Box sx={{
              position: 'absolute',
              top: '34%',
              left: '69%',
              transform: 'translateX(-50%)',
              width: '70%',
              maxWidth: '200px',
              zIndex: 2,
              backgroundColor: '#f5f5dc', // Mismo color que los otros volantes
              borderRadius: 1,
              padding: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '165px'
            }}>
              {/* Logo Section */}
              {formData.media_assets.logo_url && (
                <Box sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: '2px solid black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  overflow: 'hidden'
                }}>
                  <img
                    src={formData.media_assets.logo_url}
                    alt="Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target.parentElement) {
                        target.parentElement.innerHTML = '<div style="width: 100%; height: 100%; background-color: #ff6b35; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 16px; border-radius: 50%;">N</div>';
                      }
                    }}
                  />
                </Box>
              )}

              {/* Title with Black Frame */}
              <Box sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 1,
                minWidth: '180px',
                padding: '6px 12px'
              }}>
                {/* Black Frame */}
                <Box sx={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  border: '1px solid black',
                  zIndex: 1
                }} />
                
                {/* Title Content */}
                <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                  <Typography sx={{
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    mb: 0.3,
                    textTransform: 'uppercase',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {(() => {
                    return selectedContest?.name?.toUpperCase() || formData.media_assets.slogan?.toUpperCase() || 'HALLOWEEN';
                  })()}
                  </Typography>
                  <Typography sx={{
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    textTransform: 'uppercase'
                  }}>
                    {t('preview_modal_info_sponsorship.yourAdGoesHere')}
                  </Typography>
                </Box>
              </Box>
              
            </Box>
            
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowSmallFlyerPreview(false)}
            variant="outlined"
            sx={{ borderColor: '#4caf50', color: '#4caf50' }}
          >
            {t('preview_modal_info_sponsorship.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Añadir Plataforma - Misma estructura que Add Platform de Manage Base Platforms */}
        <Dialog
        open={showAddPlatformModal}
        onClose={() => {
          setShowAddPlatformModal(false);
          if (editingPlatformIndex === null) {
            setCustomPlatformImage(null);
            setCustomPlatformName(t('preview_modal_info_sponsorship.customPlatform'));
            setFlyerPosition({ x: 50, y: 50, width: 200, height: 150 });
            setTempWidth('200');
            setTempHeight('150');
            setPlatformShape('circular');
            setPlatformLogoUrl('');
            setPlatformCustomText('your ad goes here');
            setPlatformFontSize(0.9);
            setLogoSize({ width: 60, height: 60 });
            setTempLogoWidth('60');
            setTempLogoHeight('60');
            setPreviewImageDimensions(null);
          }
          setEditingPlatformIndex(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingPlatformIndex !== null ? t('platforms.editPlatform') : t('preview_modal_info_sponsorship.addNewPlatform')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Columna izquierda: Formulario de datos de imagen */}
            <Box sx={{ flex: '0 0 400px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <TextField
                fullWidth
                label={t('platforms.name')}
                value={customPlatformName}
                onChange={(e) => setCustomPlatformName(e.target.value)}
                margin="normal"
                required
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('platforms.customText')}
                  value={platformCustomText}
                  onChange={(e) => setPlatformCustomText(e.target.value)}
                  margin="normal"
                  placeholder="your ad goes here"
                />
                <TextField
                  fullWidth
                  label={t('platforms.fontSize')}
                  type="number"
                  value={platformFontSize}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value > 0) {
                      setPlatformFontSize(value);
                    }
                  }}
                  margin="normal"
                  inputProps={{ step: 0.1, min: 0.1, max: 5 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">rem</InputAdornment>
                  }}
                />
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('platforms.uploadImage')}
          </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setCustomPlatformImage(reader.result as string);
                        setPreviewImageDimensions(null);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </Box>

              {/* Controles de tamaño */}
              {customPlatformImage && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Columna de títulos */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', pt: 1, minWidth: '80px' }}>
                      <Typography variant="body2">
                        {t('platforms.manual')}:
                      </Typography>
                      <Typography variant="body2">
                        {t('platforms.logoSize')}:
                      </Typography>
                    </Box>
                    
                    {/* Columna de controles */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Controles manuales */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          label={t('platforms.width')}
                          type="number"
                          value={tempWidth}
                          onChange={(e) => {
                            setTempWidth(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = parseInt(tempWidth) || 200;
                              const validatedValue = Math.max(100, Math.min(1000, value));
                              setFlyerPosition(prev => ({
                                ...prev,
                                width: validatedValue
                              }));
                              setTempWidth(String(validatedValue));
                            }
                          }}
                          sx={{ width: '100px' }}
                        />
                        <Typography variant="body2">x</Typography>
                        <TextField
                          size="small"
                          label={t('platforms.height')}
                          type="number"
                          value={tempHeight}
                          onChange={(e) => {
                            setTempHeight(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = parseInt(tempHeight) || 150;
                              const validatedValue = Math.max(30, Math.min(1000, value));
                              setFlyerPosition(prev => ({
                                ...prev,
                                height: validatedValue
                              }));
                              setTempHeight(String(validatedValue));
                            }
                          }}
                          sx={{ width: '100px' }}
                        />
          <Button
                          size="small"
                          variant="contained"
            onClick={() => {
                            const widthValue = parseInt(tempWidth) || 200;
                            const heightValue = parseInt(tempHeight) || 150;
                            const validatedWidth = Math.max(100, Math.min(1000, widthValue));
                            const validatedHeight = Math.max(30, Math.min(1000, heightValue));
                            setFlyerPosition(prev => ({
                              ...prev,
                              width: validatedWidth,
                              height: validatedHeight
                            }));
                            setTempWidth(String(validatedWidth));
                            setTempHeight(String(validatedHeight));
                          }}
                          sx={{ ml: 1, minWidth: 'auto', px: 2 }}
                        >
                          {t('platforms.applySize')}
                        </Button>
                      </Box>

                      {/* Controles de tamaño del logo */}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          label={t('platforms.logoWidth')}
                          type="number"
                          value={tempLogoWidth}
                          onChange={(e) => {
                            setTempLogoWidth(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = parseInt(tempLogoWidth) || 60;
                              const validatedValue = Math.max(10, Math.min(1000, value));
                              setLogoSize(prev => ({
                                ...prev,
                                width: validatedValue
                              }));
                              setTempLogoWidth(String(validatedValue));
                            }
                          }}
                          sx={{ width: '100px' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                        <Typography variant="body2">x</Typography>
                        <TextField
                          size="small"
                          label={t('platforms.logoHeight')}
                          type="number"
                          value={tempLogoHeight}
                          onChange={(e) => {
                            setTempLogoHeight(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = parseInt(tempLogoHeight) || 60;
                              const validatedValue = Math.max(10, Math.min(1000, value));
                              setLogoSize(prev => ({
                                ...prev,
                                height: validatedValue
                              }));
                              setTempLogoHeight(String(validatedValue));
                            }
                          }}
                          sx={{ width: '100px' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            const widthValue = parseInt(tempLogoWidth) || 60;
                            const heightValue = parseInt(tempLogoHeight) || 60;
                            const validatedWidth = Math.max(10, Math.min(1000, widthValue));
                            const validatedHeight = Math.max(10, Math.min(1000, heightValue));
                            setLogoSize({
                              width: validatedWidth,
                              height: validatedHeight
                            });
                            setTempLogoWidth(String(validatedWidth));
                            setTempLogoHeight(String(validatedHeight));
                          }}
                          sx={{ ml: 1, minWidth: 'auto', px: 2 }}
                        >
                          {t('platforms.applySize')}
          </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Columna derecha: Imagen con voleto */}
            {customPlatformImage && (
              <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                justifyContent: 'flex-start', 
                alignItems: 'flex-start',
                maxHeight: '100%',
                border: '2px solid #d1d5db',
                borderRadius: 2,
                backgroundColor: '#fafafa',
                p: 1.5,
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Scrollbar superior sincronizada - fija en la parte superior */}
                <Box 
                  id={`scrollbar-top-sponsorship-${editingPlatformIndex || 'new'}`}
                  sx={{
                    width: 'calc(100% - 16px)',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    height: '16px',
                    mb: 1.5,
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#fafafa',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    marginTop: '-8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&::-webkit-scrollbar': {
                      height: '16px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#e5e7eb',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#6b7280',
                      borderRadius: '8px',
                      border: '1px solid #4b5563',
                      '&:hover': {
                        background: '#4b5563'
                      }
                    }
                  }}
                  onScroll={(e) => {
                    const bottomScroll = document.getElementById(`scrollbar-bottom-sponsorship-${editingPlatformIndex || 'new'}`);
                    if (bottomScroll) {
                      bottomScroll.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                >
                  <Box sx={{ width: '800px', height: '1px' }} />
            </Box>

                {/* Contenedor principal con imagen - scrollbar oculta */}
                <Box 
                  id={`scrollbar-bottom-sponsorship-${editingPlatformIndex || 'new'}`}
                sx={{
                  flex: 1,
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    width: '100%',
                  position: 'relative',
                    paddingBottom: '0px',
                    marginBottom: '0px'
                  }}
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  } as React.CSSProperties}
                  onScroll={(e) => {
                    const topScroll = document.getElementById(`scrollbar-top-sponsorship-${editingPlatformIndex || 'new'}`);
                    if (topScroll) {
                      topScroll.scrollLeft = e.currentTarget.scrollLeft;
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      overflow: 'visible',
                      backgroundColor: '#f9f9f9',
                      display: 'inline-block',
                      minWidth: '800px'
                    }}
                  >
                    {/* Imagen de fondo - Usando img con estilos inline directos */}
                    <img
                      src={customPlatformImage}
                      alt="Platform background"
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth && img.naturalHeight) {
                          const aspectRatio = img.naturalWidth / img.naturalHeight;
                          let displayWidth = img.naturalWidth;
                          let displayHeight = img.naturalHeight;
                          
                          // Si es más ancha que alta, limitar a 2000px pero mantener proporción
                          if (aspectRatio > 1 && img.naturalWidth > 2000) {
                            displayWidth = 2000;
                            displayHeight = 2000 / aspectRatio;
                          } else if (aspectRatio <= 1 && img.naturalWidth > 800) {
                            displayWidth = 800;
                            displayHeight = 800 / aspectRatio;
                          }
                          
                          setPreviewImageDimensions({
                            width: displayWidth,
                            height: displayHeight
                          });
                          
                          // Aplicar tamaño directamente al elemento
                          img.style.width = `${displayWidth}px`;
                          img.style.height = `${displayHeight}px`;
                          img.style.maxWidth = 'none';
                          img.style.minWidth = 'none';
                          img.style.objectFit = 'none';
                        }
                      }}
                      onError={() => {
                        setPreviewImageDimensions(null);
                      }}
                      style={{
                        display: 'block',
                        width: previewImageDimensions ? `${previewImageDimensions.width}px` : '800px',
                        height: previewImageDimensions ? `${previewImageDimensions.height}px` : 'auto',
                        maxWidth: 'none',
                        minWidth: 'none',
                        objectFit: 'none'
                      }}
                    />
                    
                    {/* Volante arrastrable */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${flyerPosition.x}%`,
                        top: `${flyerPosition.y}%`,
                        width: `${flyerPosition.width}px`,
                        height: `${flyerPosition.height}px`,
                        backgroundColor: '#f5f5dc',
                        border: '2px solid #7c3aed',
                        borderRadius: 1,
                        cursor: 'move',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: 1,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        '&:hover': {
                          borderColor: '#6d28d9',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startLeft = flyerPosition.x;
                        const startTop = flyerPosition.y;
                        
                        const container = e.currentTarget?.parentElement;
                        if (!container) return;

                        const containerRect = container.getBoundingClientRect();

                        const handleMouseMove = (e: MouseEvent) => {
                          e.preventDefault();
                          const deltaX = e.clientX - startX;
                          const deltaY = e.clientY - startY;
                          
                          const newX = Math.max(0, Math.min(100, startLeft + (deltaX / containerRect.width) * 100));
                          const newY = Math.max(0, Math.min(100, startTop + (deltaY / containerRect.height) * 100));
                          
                          setFlyerPosition(prev => ({ ...prev, x: newX, y: newY }));
                        };

                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                    >
                      {/* Logo con forma configurable */}
                      {platformLogoUrl ? (
                        <Box
                          component="img"
                          src={platformLogoUrl}
                          alt="Logo"
                          sx={{
                            width: `${logoSize.width}%`,
                            height: `${logoSize.height}%`,
                            objectFit: 'contain',
                            borderRadius: platformShape === 'circular' ? '50%' : 0,
                            mb: 1
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : formData.media_assets.logo_url ? (
                        <Box
                          component="img"
                          src={formData.media_assets.logo_url}
                          alt="Logo"
                          sx={{
                            width: `${logoSize.width}%`,
                            height: `${logoSize.height}%`,
                            objectFit: 'contain',
                            borderRadius: platformShape === 'circular' ? '50%' : 0,
                            mb: 1
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Box sx={{
                          width: `${logoSize.width}%`,
                          height: `${logoSize.height}%`,
                          borderRadius: platformShape === 'circular' ? '50%' : 0,
                          border: '2px solid black',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          LOGO
                        </Box>
                      )}

                      {/* Texto del concurso */}
                      <Typography
                        sx={{
                          color: '#7c3aed',
                          fontWeight: 'bold',
                          fontSize: `${platformFontSize}rem`,
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          lineHeight: 1.2,
                          mb: 1.5
                        }}
                      >
                        {(selectedContest?.name || 'NOMBRE DEL CONCURSO').toUpperCase()}
                      </Typography>

                      {/* Texto personalizado */}
                      <Typography
                        sx={{
                          color: '#7c3aed',
                          fontWeight: 'bold',
                          fontSize: `${platformFontSize}rem`,
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          lineHeight: 1.2,
                          mt: 1.5
                        }}
                      >
                        {platformCustomText || 'your ad goes here'}
                    </Typography>
                  </Box>
                  </Box>
                </Box>
                  </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {customPlatformImage && (
              <Button
                variant="outlined"
                onClick={() => {
                  // Crear un objeto con los datos actuales del estado para preview
                  const currentPlatformForPreview = {
                    name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
                    image: customPlatformImage,
                    x: flyerPosition.x, // Usar valores actuales del estado
                    y: flyerPosition.y, // Usar valores actuales del estado
                    width: flyerPosition.width, // Usar valores actuales del estado
                    height: flyerPosition.height, // Usar valores actuales del estado
                    Image_form: platformShape,
                    logo_url: platformLogoUrl || formData.media_assets.logo_url || '',
                    custom_text: platformCustomText,
                    logo_width: logoSize.width,
                    logo_height: logoSize.height,
                    Text_size: platformFontSize,
                    contest_text: 'Nombre del concurso'
                  };
                  
                  // Guardar en estado temporal para preview
                  setTempPreviewPlatform(currentPlatformForPreview);
                  setSelectedPlatformIndex(null); // Usar null para indicar que es preview temporal
                  setShowCustomPlatformPreview(true);
                }}
                sx={{ mr: 1 }}
              >
                {t('platforms.viewPreview')}
              </Button>
            )}
          </Box>
          <Box>
          <Button
            onClick={() => {
              setShowAddPlatformModal(false);
                if (!editingPlatformIndex) {
                setCustomPlatformImage(null);
                setCustomPlatformName(t('preview_modal_info_sponsorship.customPlatform'));
                setFlyerPosition({ x: 50, y: 50, width: 200, height: 150 });
                  setTempWidth('200');
                  setTempHeight('150');
                  setPlatformShape('circular');
                  setPlatformLogoUrl('');
                  setPlatformCustomText('your ad goes here');
                  setPreviewImageDimensions(null);
                  setPlatformFontSize(0.9);
                  setLogoSize({ width: 60, height: 60 });
                  setTempLogoWidth('60');
                  setTempLogoHeight('60');
              }
              setEditingPlatformIndex(null);
            }}
            variant="outlined"
              sx={{ mr: 1 }}
          >
              {t('buttonsforms.Cancel')}
          </Button>
          <Button 
            onClick={() => {
              if (!customPlatformImage) {
                void appDialog.alert('Por favor sube una imagen primero');
                return;
              }

              // Actualizar el formData con los datos de la plataforma personalizada
              const updatedPlatform = {
                name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
                  image: customPlatformImage,
                x: flyerPosition.x,
                y: flyerPosition.y,
                width: flyerPosition.width,
                height: flyerPosition.height,
                  Image_form: platformShape,
                  logo_url: platformLogoUrl || formData.media_assets.logo_url || '',
                  custom_text: platformCustomText,
                  logo_width: logoSize.width,
                  logo_height: logoSize.height,
                  Text_size: platformFontSize,
                  contest_text: 'Nombre del concurso'
              };

              setFormData(prev => {
                const currentPlatforms = [...(prev.custom_platforms || [])];
                
                const contestId = existingSponsorship?.contest_id || selectedContest?.id || null;
                
                if (editingPlatformIndex !== null && editingPlatformIndex >= 0 && editingPlatformIndex < currentPlatforms.length) {
                  currentPlatforms[editingPlatformIndex] = {
                    ...updatedPlatform,
                    contest_id: currentPlatforms[editingPlatformIndex].contest_id || contestId
                  };
                } else {
                  currentPlatforms.push({
                    ...updatedPlatform,
                      contest_id: contestId || undefined
                  });
                }
                
                return {
                  ...prev,
                  custom_platforms: currentPlatforms
                };
              });

              // Limpiar campos del modal
              setCustomPlatformImage(null);
                setCustomPlatformName(t('preview_modal_info_sponsorship.customPlatform'));
              setFlyerPosition({ x: 50, y: 50, width: 200, height: 150 });
                setTempWidth('200');
                setTempHeight('150');
                setPlatformShape('circular');
                setPlatformLogoUrl('');
                setPlatformCustomText('your ad goes here');
                setPlatformFontSize(0.9);
                setLogoSize({ width: 60, height: 60 });
                setPreviewImageDimensions(null);
                setTempLogoWidth('60');
                setTempLogoHeight('60');
              setEditingPlatformIndex(null);
              
              setShowAddPlatformModal(false);
            }}
            variant="contained"
              sx={{ backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
          >
              {t('platforms.save')}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Modal Preview de Plataforma Personalizada */}
      <Dialog
        open={showCustomPlatformPreview}
        onClose={() => {
          setShowCustomPlatformPreview(false);
          setTempPreviewPlatform(null); // Limpiar preview temporal al cerrar
          setSelectedPlatformIndex(null); // Limpiar índice seleccionado al cerrar
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" component="span" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {(() => {
              let platform = null;
              
              // Si hay una preview temporal, usarla primero (viene del botón "View Preview" en el modal de edición)
              if (tempPreviewPlatform) {
                platform = tempPreviewPlatform;
              } else {
                // Si no hay preview temporal, buscar en el array usando el índice seleccionado
              const filteredPlatforms = getFilteredCustomPlatforms();
                if (selectedPlatformIndex !== null && selectedPlatformIndex >= 0 && selectedPlatformIndex < filteredPlatforms.length) {
                  platform = filteredPlatforms[selectedPlatformIndex];
                } else if (filteredPlatforms.length > 0) {
                  // Si no hay índice seleccionado o es inválido, usar la primera plataforma
                  platform = filteredPlatforms[0];
                }
              }
              return `${t('preview_modal_info_sponsorship.preview')} - ${platform?.name || t('preview_modal_info_sponsorship.customPlatform')}`;
            })()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<FileTextIcon />}
              onClick={async () => {
                try {
                  // Obtener plataformas ordenadas según el orden guardado
                  const orderedPlatforms = getOrderedPlatforms();
                  
                  // Mapear las plataformas ordenadas al formato necesario para el documento
                  const allPlatforms = orderedPlatforms.map((platform: any) => {
                    // Si es voletaje
                    if (platform.type === 'voletaje' || platform.platform_type === 'voletaje') {
                      // Buscar la plataforma base de voletaje
                      const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
                      return {
                        ...voletajeBase,
                        name: t('preview_modal_info_sponsorship.voletaje'),
                        isBase: true,
                        image: voletajeBase?.image_url || voletajeBase?.image,
                        x: voletajeBase?.x || 50,
                        y: voletajeBase?.y || 50,
                        width: voletajeBase?.width || 200,
                        height: voletajeBase?.height || 150,
                        logo_url: voletajeBase?.logo_url || formData.media_assets.logo_url,
                        logo_width: voletajeBase?.logo_width || 60,
                        logo_height: voletajeBase?.logo_height || 60,
                        Image_form: voletajeBase?.Image_form || 'circular',
                        custom_text: voletajeBase?.custom_text || 'your ad goes here',
                        Text_size: voletajeBase?.Text_size || 0.9,
                        contest_text: voletajeBase?.contest_text || 'Nombre del concurso',
                        distancia_x: voletajeBase?.distancia_x || 60,
                        Fix_width: voletajeBase?.Fix_width,
                        Fix_height: voletajeBase?.Fix_height,
                        general_width: voletajeBase?.general_width,
                        general_height: voletajeBase?.general_height
                      };
                    }
                    
                    // Si es plataforma base
                    if (platform.isBase && platform.id) {
                      const basePlatform = basePlatforms.find((bp: any) => bp.id === platform.id);
                      if (basePlatform) {
                        return {
                          ...basePlatform,
                          isBase: true,
                          image: basePlatform.image_url || basePlatform.image,
                          x: basePlatform.x || 50,
                          y: basePlatform.y || 50,
                          width: basePlatform.width || 200,
                          height: basePlatform.height || 150,
                          logo_url: basePlatform.logo_url || formData.media_assets.logo_url,
                          logo_width: basePlatform.logo_width || 60,
                          logo_height: basePlatform.logo_height || 60,
                          Image_form: basePlatform.Image_form || 'circular',
                          custom_text: basePlatform.custom_text || 'your ad goes here',
                          Text_size: basePlatform.Text_size || 0.9,
                          contest_text: basePlatform.contest_text || 'Nombre del concurso',
                          distancia_x: basePlatform.distancia_x || 60,
                          Fix_width: basePlatform.Fix_width,
                          Fix_height: basePlatform.Fix_height,
                          general_width: basePlatform.general_width,
                          general_height: basePlatform.general_height
                        };
                      }
                    }
                    
                    // Si es plataforma personalizada
                    if (!platform.isBase && !platform.isLegacy) {
                      return {
                        ...platform,
                        isBase: false
                      };
                    }
                    
                    // Si es legacy
                    if (platform.isLegacy) {
                      return {
                        name: customPlatformName?.trim() || t('preview_modal_info_sponsorship.customPlatform'),
                        image: formData.custom_platform_image,
                        x: formData.flyer_position?.x || 50,
                        y: formData.flyer_position?.y || 50,
                        width: formData.flyer_position?.width || 200,
                        height: formData.flyer_position?.height || 150,
                        isBase: false
                      };
                    }
                    
                    return null;
                  }).filter((p: any) => p !== null);
                  
                  if (allPlatforms.length === 0) {
                    void appDialog.alert('No hay plataformas para descargar');
                    return;
                  }

                  // Función para crear imagen compuesta con volante (misma función que en el modal principal)
                  const createCompositeImage = async (platform: any): Promise<{ data: Uint8Array; width: number; height: number } | null> => {
                    try {
                      console.log(`🔍 createCompositeImage - Platform: ${platform.name}, general_width: ${platform.general_width}, general_height: ${platform.general_height}`);
                      
                      const canvas = document.createElement('canvas');
                      const ctx = canvas.getContext('2d');
                      if (!ctx) return null;

                      // Cargar imagen de fondo
                      const imageUrl = platform.image || platform.image_url;
                      if (!imageUrl) {
                        throw new Error('No image URL provided');
                      }
                      
                      // Normalizar URL si es necesario
                      let normalizedImageUrl = imageUrl;
                      if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                        normalizedImageUrl = getCompleteImagePath(imageUrl) || imageUrl;
                      }
                      
                      // Usar loadImageForWord para manejar CORS automáticamente
                      const imageData = await loadImageForWord(normalizedImageUrl);
                      if (!imageData) {
                        throw new Error(`Failed to load background image: ${normalizedImageUrl}`);
                      }
                      
                      // Crear un elemento Image desde los datos cargados
                      const blob = new Blob([imageData.data as any], { type: 'image/png' });
                      const imageUrlFromBlob = URL.createObjectURL(blob);
                      const bgImage = new Image();
                      
                      await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                          URL.revokeObjectURL(imageUrlFromBlob);
                          reject(new Error('Background image load timeout'));
                        }, 30000);
                        
                        bgImage.onload = () => {
                          clearTimeout(timeout);
                          URL.revokeObjectURL(imageUrlFromBlob);
                          resolve(null);
                        };
                        bgImage.onerror = (error) => {
                          clearTimeout(timeout);
                          URL.revokeObjectURL(imageUrlFromBlob);
                          reject(new Error(`Failed to load background image from blob`));
                        };
                        bgImage.src = imageUrlFromBlob;
                      });
                      
                      // Usar las dimensiones reales de los datos cargados
                      bgImage.width = imageData.width;
                      bgImage.height = imageData.height;

                      console.log(`🖼️ Image loaded - Original size: ${bgImage.width}x${bgImage.height}, DB values: ${platform.general_width}x${platform.general_height}`);

                      // Calcular el tamaño final del canvas según general_width/general_height si están definidos
                      // SIEMPRE usar general_width y general_height si están definidos, sin importar el tamaño original de la imagen
                      let finalCanvasWidth = bgImage.width;
                      let finalCanvasHeight = bgImage.height;
                      let scaleX = 1;
                      let scaleY = 1;

                      // Aplicar tamaño general si está definido - usar EXACTAMENTE los valores de la base de datos
                      if (platform.general_width && platform.general_height) {
                        finalCanvasWidth = platform.general_width;
                        finalCanvasHeight = platform.general_height;
                        scaleX = finalCanvasWidth / bgImage.width;
                        scaleY = finalCanvasHeight / bgImage.height;
                        console.log(`✅ Using general_size from DB - Canvas: ${finalCanvasWidth}x${finalCanvasHeight}, Original: ${bgImage.width}x${bgImage.height}, Scale: ${scaleX.toFixed(2)}x${scaleY.toFixed(2)}`);
                      } else if (platform.general_width) {
                        scaleX = platform.general_width / bgImage.width;
                        finalCanvasWidth = platform.general_width;
                        finalCanvasHeight = bgImage.height * scaleX;
                        console.log(`⚠️ Only general_width defined: ${platform.general_width}, calculated height: ${finalCanvasHeight}`);
                      } else if (platform.general_height) {
                        scaleY = platform.general_height / bgImage.height;
                        finalCanvasWidth = bgImage.width * scaleY;
                        finalCanvasHeight = platform.general_height;
                        console.log(`⚠️ Only general_height defined: ${platform.general_height}, calculated width: ${finalCanvasWidth}`);
                      } else {
                        console.log(`❌ No general_size in DB, using original: ${bgImage.width}x${bgImage.height}`);
                      }

                      canvas.width = finalCanvasWidth;
                      canvas.height = finalCanvasHeight;
                      
                      // Dibujar la imagen de fondo escalada al tamaño exacto del canvas
                      ctx.drawImage(bgImage, 0, 0, finalCanvasWidth, finalCanvasHeight);

                      // Calcular el tamaño del volante basado en el tamaño final del canvas (que ya tiene aplicado general_width/general_height)
                      // Si se aplicó general_size, el canvas ya está escalado, así que debemos calcular el volante basado en el canvas final
                      let flyerWidth: number;
                      let flyerHeight: number;
                      let tallImageScaleFactor: number = 1.0;
                      
                      if (platform.Fix_width && platform.Fix_height) {
                        // Si hay Fix_width y Fix_height, usarlos directamente
                        flyerWidth = platform.Fix_width;
                        flyerHeight = platform.Fix_height;
                        
                        // Si se aplicó general_size, escalar Fix_width y Fix_height proporcionalmente
                        if (platform.general_width || platform.general_height) {
                          if (platform.general_width && platform.general_height) {
                            scaleX = finalCanvasWidth / bgImage.width;
                            scaleY = finalCanvasHeight / bgImage.height;
                          } else if (platform.general_width) {
                            scaleX = finalCanvasWidth / bgImage.width;
                            scaleY = scaleX;
                          } else if (platform.general_height) {
                            scaleY = finalCanvasHeight / bgImage.height;
                            scaleX = scaleY;
                          }
                          flyerWidth = flyerWidth * scaleX;
                          flyerHeight = flyerHeight * scaleY;
                        }
                      } else {
                        // Calcular el tamaño del volante basado en porcentajes del canvas final
                        // Los valores x, y, width, height son porcentajes (0-100) del tamaño de visualización
                        const previewMaxWidth = 800; // maxWidth del contenedor en el preview
                        // Usar el tamaño final del canvas para calcular el preview display
                        const previewDisplayWidth = Math.min(finalCanvasWidth, previewMaxWidth);
                        const previewDisplayHeight = (finalCanvasHeight / finalCanvasWidth) * previewDisplayWidth;
                        
                        // Calcular el porcentaje que representa el volante en el preview
                        const flyerWidthPercent = ((platform.width || 200) / previewDisplayWidth) * 100;
                        const flyerHeightPercent = ((platform.height || 150) / previewDisplayHeight) * 100;
                        
                        // Aplicar esos porcentajes al tamaño final del canvas (que ya tiene general_size aplicado)
                        flyerWidth = (flyerWidthPercent / 100) * finalCanvasWidth;
                        flyerHeight = (flyerHeightPercent / 100) * finalCanvasHeight;
                        
                        // Aplicar el factor de escala para imágenes altas (solo si no se aplicó general_size)
                        if (!platform.general_width && !platform.general_height) {
                          const maxDimension = Math.max(bgImage.width, bgImage.height);
                          const minDimension = Math.min(bgImage.width, bgImage.height);
                          const differencePercent = (minDimension / maxDimension) * 100;
                          
                          if (bgImage.height > bgImage.width && differencePercent < 100) {
                            if (differencePercent <= 40.18) {
                              tallImageScaleFactor = 0.5;
                            } else if (differencePercent <= 50) {
                              tallImageScaleFactor = 0.5 + ((differencePercent - 40.18) / (50 - 40.18)) * (0.65 - 0.5);
                            } else {
                              tallImageScaleFactor = 0.65 + ((differencePercent - 50) / 50) * 0.35;
                            }
                            flyerWidth = flyerWidth * tallImageScaleFactor;
                            flyerHeight = flyerHeight * tallImageScaleFactor;
                          }
                        }
                      }
                      
                      // Aplicar el escalado a las posiciones del volante
                      const flyerX = ((platform.x || 50) / 100) * finalCanvasWidth;
                      const flyerY = ((platform.y || 50) / 100) * finalCanvasHeight;

                      ctx.fillStyle = '#f5f5dc';
                      ctx.strokeStyle = '#ff6b35';
                      ctx.lineWidth = 2;
                      ctx.beginPath();
                      const radius = 8;
                      ctx.moveTo(flyerX + radius, flyerY);
                      ctx.lineTo(flyerX + flyerWidth - radius, flyerY);
                      ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY, flyerX + flyerWidth, flyerY + radius);
                      ctx.lineTo(flyerX + flyerWidth, flyerY + flyerHeight - radius);
                      ctx.quadraticCurveTo(flyerX + flyerWidth, flyerY + flyerHeight, flyerX + flyerWidth - radius, flyerY + flyerHeight);
                      ctx.lineTo(flyerX + radius, flyerY + flyerHeight);
                      ctx.quadraticCurveTo(flyerX, flyerY + flyerHeight, flyerX, flyerY + flyerHeight - radius);
                      ctx.lineTo(flyerX, flyerY + radius);
                      ctx.quadraticCurveTo(flyerX, flyerY, flyerX + radius, flyerY);
                      ctx.closePath();
                      ctx.fill();
                      ctx.stroke();

                      const logoUrl = formData.media_assets.logo_url;
                      if (logoUrl) {
                        try {
                          // Normalizar URL del logo si es necesario
                          let normalizedLogoUrl = logoUrl;
                          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
                            normalizedLogoUrl = getCompleteImagePath(logoUrl) || logoUrl;
                          }
                          
                          const logoImage = new Image();
                          logoImage.crossOrigin = 'anonymous';
                          await new Promise((resolve, reject) => {
                            logoImage.onload = resolve;
                            logoImage.onerror = () => {
                              console.error('Error loading logo image:', normalizedLogoUrl);
                              reject(new Error(`Failed to load logo image: ${normalizedLogoUrl}`));
                            };
                            logoImage.src = normalizedLogoUrl;
                          });

                          // Reducir el tamaño del logo (aplicar factor de escala de 0.7 para hacerlo más pequeño)
                          const logoScaleFactor = 0.7;
                          const maxLogoWidth = ((platform.logo_width / 100) * flyerWidth) * logoScaleFactor;
                          const maxLogoHeight = ((platform.logo_height / 100) * flyerHeight) * logoScaleFactor;
                          
                          const imageAspectRatio = logoImage.width / logoImage.height;
                          const maxAspectRatio = maxLogoWidth / maxLogoHeight;
                          
                          let logoWidth, logoHeight;
                          if (imageAspectRatio > maxAspectRatio) {
                            logoWidth = maxLogoWidth;
                            logoHeight = maxLogoWidth / imageAspectRatio;
                          } else {
                            logoHeight = maxLogoHeight;
                            logoWidth = maxLogoHeight * imageAspectRatio;
                          }
                          
                          const logoX = flyerX + (flyerWidth - logoWidth) / 2;
                          const logoY = flyerY + (platform.distancia_x || 60); // Espacio desde la parte superior

                          if (platform.Image_form === 'circular') {
                            ctx.save();
                            ctx.beginPath();
                            ctx.arc(logoX + logoWidth / 2, logoY + logoHeight / 2, Math.min(logoWidth, logoHeight) / 2, 0, Math.PI * 2);
                            ctx.clip();
                          }

                          ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
                          
                          if (platform.Image_form === 'circular') {
                            ctx.restore();
                          }
                          
                          // Guardar la posición donde termina el logo para posicionar el texto justo debajo
                          const logoBottom = logoY + logoHeight;
                          
                          // Dibujar texto del concurso justo debajo del logo con salto de línea automático
                          const contestName = (selectedContest?.name || 'NOMBRE DEL CONCURSO').toUpperCase();
                          // Aplicar el escalado al tamaño de fuente si se aplicó general_size
                          let fontSizeScale = tallImageScaleFactor;
                          if (platform.general_width || platform.general_height) {
                            if (platform.general_width && platform.general_height) {
                              fontSizeScale = Math.min(finalCanvasWidth / bgImage.width, finalCanvasHeight / bgImage.height);
                            } else if (platform.general_width) {
                              fontSizeScale = finalCanvasWidth / bgImage.width;
                            } else if (platform.general_height) {
                              fontSizeScale = finalCanvasHeight / bgImage.height;
                            }
                          }
                          const fontSize = (platform.Text_size || 0.9) * 16 * fontSizeScale;
                          ctx.fillStyle = '#7c3aed';
                          ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                          ctx.textAlign = 'center';
                          ctx.textBaseline = 'top';
                          // Espacio entre logo y primer texto
                          const textMargin = Math.max(40, 40 * fontSizeScale);
                          let contestY = logoBottom + textMargin;
                          
                          // Función helper para dividir texto en líneas
                          const drawMultilineText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number) => {
                            const words = text.split(' ');
                            const lines: string[] = [];
                            let currentLine = words[0];
                            
                            for (let i = 1; i < words.length; i++) {
                              const word = words[i];
                              const testLine = currentLine + ' ' + word;
                              const metrics = ctx.measureText(testLine);
                              if (metrics.width > maxWidth && currentLine.length > 0) {
                                lines.push(currentLine);
                                currentLine = word;
                              } else {
                                currentLine = testLine;
                              }
                            }
                            lines.push(currentLine);
                            
                            // Dibujar cada línea
                            lines.forEach((line, index) => {
                              ctx.fillText(line, x, y + (index * lineHeight));
                            });
                            
                            return lines.length;
                          };
                          
                          // Ancho disponible para el texto (con márgenes laterales)
                          const textMaxWidth = flyerWidth * 0.9; // 90% del ancho del voleto
                          const lineHeight = fontSize * 1.2;
                          const lineCount = drawMultilineText(contestName, flyerX + flyerWidth / 2, contestY, textMaxWidth, lineHeight);
                          contestY += (lineCount - 1) * lineHeight; // Ajustar posición Y si hay múltiples líneas

                          // Dibujar texto personalizado debajo del nombre del concurso
                          const customText = platform.custom_text || 'your ad goes here';
                          // Espacio entre los dos textos (aumentado)
                          const customTextY = contestY + fontSize * 1.8;
                          ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
                        } catch (logoError) {
                          console.error('Error loading logo:', logoError);
                        }
                      } else {
                        // Si no hay logo, posicionar el texto desde arriba
                        const contestName = (selectedContest?.name || 'NOMBRE DEL CONCURSO').toUpperCase();
                        // Aplicar el escalado al tamaño de fuente si se aplicó general_size
                        let fontSizeScale = tallImageScaleFactor;
                        if (platform.general_width || platform.general_height) {
                          if (platform.general_width && platform.general_height) {
                            fontSizeScale = Math.min(finalCanvasWidth / bgImage.width, finalCanvasHeight / bgImage.height);
                          } else if (platform.general_width) {
                            fontSizeScale = finalCanvasWidth / bgImage.width;
                          } else if (platform.general_height) {
                            fontSizeScale = finalCanvasHeight / bgImage.height;
                          }
                        }
                        const fontSize = (platform.Text_size || 0.9) * 16 * fontSizeScale;
                        ctx.fillStyle = '#7c3aed';
                        ctx.font = `bold ${fontSize}px "DejaVu Sans", "Liberation Sans", Arial, Helvetica, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        const contestY = flyerY + 20;
                        ctx.fillText(contestName, flyerX + flyerWidth / 2, contestY);

                        const customText = platform.custom_text || 'your ad goes here';
                        const customTextY = contestY + fontSize * 1.2;
                        ctx.fillText(customText.toUpperCase(), flyerX + flyerWidth / 2, customTextY);
                      }

                      return new Promise((resolve) => {
                        canvas.toBlob((blob) => {
                          if (!blob) {
                            resolve(null);
                            return;
                          }
                          blob.arrayBuffer().then((buffer) => {
                            // Usar explícitamente general_width y general_height si están definidos
                            // para asegurar que el tamaño en el documento Word sea exactamente el de la base de datos
                            // El canvas ya tiene el tamaño correcto, pero debemos asegurarnos de usar los valores exactos de la DB
                            const finalWidth = (platform.general_width && platform.general_height) 
                              ? platform.general_width 
                              : canvas.width;
                            const finalHeight = (platform.general_width && platform.general_height) 
                              ? platform.general_height 
                              : canvas.height;
                            
                            console.log(`📄 Platform ${platform.name}: Returning image size - width: ${finalWidth}, height: ${finalHeight}, canvas: ${canvas.width}x${canvas.height}`);
                            
                            resolve({
                              data: new Uint8Array(buffer),
                              width: finalWidth,
                              height: finalHeight
                            });
                          });
                        }, 'image/png');
                      });
                    } catch (error) {
                      console.error('Error creating composite image:', error);
                      return null;
                    }
                  };

                  // Crear secciones para cada plataforma (cada una en su propia página)
                  const sections = await Promise.all(allPlatforms.map(async (platform: any, index: number) => {
                    const children: any[] = [];
                    
                    // Título de la plataforma
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${index + 1}. ${platform.name || platform.platform_type || 'Platform'}${platform.isBase ? ' (Base)' : ''}`,
                            size: 36,
                            bold: true
                          })
                        ],
                        spacing: { before: 400, after: 0, line: 240 } // Sin espaciado entre título e imagen
                      })
                    );

                    if (platform.image || platform.image_url) {
                      try {
                        console.log(`🚀 About to create composite image for: ${platform.name}, general_width: ${platform.general_width}, general_height: ${platform.general_height}`);
                        const compositeImage = await createCompositeImage(platform);
                        
                        if (compositeImage) {
                          // Asegurarse de usar los valores exactos de general_width y general_height de la base de datos
                          const imageWidth = (platform.general_width && platform.general_height) 
                            ? platform.general_width 
                            : compositeImage.width;
                          const imageHeight = (platform.general_width && platform.general_height) 
                            ? platform.general_height 
                            : compositeImage.height;
                          
                          console.log(`📝 Platform ${platform.name}: Inserting into Word - width: ${imageWidth}px, height: ${imageHeight}px, DB values: ${platform.general_width}x${platform.general_height}, compositeImage size: ${compositeImage.width}x${compositeImage.height}`);
                          
                          children.push(
                            new Paragraph({
                              children: [
                                new ImageRun({
                                  data: compositeImage.data,
                                  transformation: {
                                    width: imageWidth,
                                    height: imageHeight,
                                  },
                                  type: 'png',
                                } as any),
                              ],
                              alignment: AlignmentType.CENTER,
                              spacing: { before: 0, after: 300 }
                            })
                          );
                        } else {
                          // Si falla la creación compuesta, intentar solo la imagen de fondo usando la función helper
                          const imageData = await loadImageForWord(platform.image || platform.image_url);
                          
                          if (imageData) {
                            // Usar general_width y general_height si están definidos, sino usar el tamaño original
                            const imageWidth = (platform.general_width && platform.general_height) 
                              ? platform.general_width 
                              : (imageData.width || 600);
                            const imageHeight = (platform.general_width && platform.general_height) 
                              ? platform.general_height 
                              : (imageData.height || 400);
                            
                            children.push(
                              new Paragraph({
                                children: [
                                  new ImageRun({
                                    data: imageData.data,
                                    transformation: {
                                      width: imageWidth,
                                      height: imageHeight,
                                    },
                                    type: 'png',
                                  } as any),
                                ],
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 300 }
                              })
                            );
                          } else {
                            // Si no se puede cargar la imagen, omitirla en lugar de mostrar error
                            console.error('Failed to load fallback image for platform:', platform.name);
                          }
                        }
                      } catch (imgError) {
                        console.error('Error loading image:', imgError);
                        // No agregar mensaje de error al documento, simplemente omitir la imagen
                      }
                    } else {
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'No hay imagen disponible',
                              size: 24,
                              color: '666666'
                            })
                          ],
                          spacing: { after: 200 }
                        })
                      );
                    }

                    return {
                      properties: {
                        type: SectionType.NEXT_PAGE
                      },
                      children: children
                    };
                  }));

                  // Función para cargar imagen del logo con límite de altura (segunda función)
                  let logoImageData2: Uint8Array | null = null;
                  let logoWidth2 = 2000;  // Ancho en TWIPS (default)
                  let logoHeight2 = 2000; // Alto en TWIPS (default)
                  
                  if (formData.media_assets.logo_url) {
                    try {
                      const logoResponse2 = await fetch(formData.media_assets.logo_url);
                      const logoArrayBuffer2 = await logoResponse2.arrayBuffer();
                      logoImageData2 = new Uint8Array(logoArrayBuffer2);
                      
                      // Cargar la imagen para obtener sus dimensiones reales
                      const img2 = new Image();
                      const blob2 = new Blob([logoArrayBuffer2], { type: 'image/png' });
                      const imgUrl2 = URL.createObjectURL(blob2);
                      
                      await new Promise((resolve, reject) => {
                        img2.onload = resolve;
                        img2.onerror = reject;
                        img2.src = imgUrl2;
                      });
                      
                      // Calcular dimensiones manteniendo proporción y limitando altura a 18.75px (8 veces más pequeño que 150px original)
                      const maxHeightPx2 = 18.75;
                      let finalWidthPx2 = img2.width;
                      let finalHeightPx2 = img2.height;
                      
                      if (finalHeightPx2 > maxHeightPx2) {
                        const scale2 = maxHeightPx2 / finalHeightPx2;
                        finalWidthPx2 = finalWidthPx2 * scale2;
                        finalHeightPx2 = maxHeightPx2;
                      }
                      
                      // Convertir píxeles a TWIPS
                      // Para Word: 1 pulgada = 1440 TWIPS
                      // Asumiendo 96 DPI (pantalla estándar): 1 pulgada = 96 píxeles
                      // Por lo tanto: 1 píxel = 1440/96 = 15 TWIPS
                      logoWidth2 = Math.round(finalWidthPx2 * 15);
                      logoHeight2 = Math.round(finalHeightPx2 * 15);
                      
                      URL.revokeObjectURL(imgUrl2);
                    } catch (logoError2) {
                      console.error('Error loading logo:', logoError2);
                    }
                  }

                  // Formatear fechas (segunda función)
                  const formatDate2 = (dateString: string) => {
                    if (!dateString) return 'N/A';
                    try {
                      const date = new Date(dateString);
                      return date.toLocaleDateString('es-ES', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      });
                    } catch {
                      return dateString;
                    }
                  };

                  const firstSectionChildren2: any[] = [
                    // Título principal
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Contest Information - ${selectedContest?.name || 'Contest'}`,
                          size: 44,
                          bold: true
                        })
                      ],
                      heading: HeadingLevel.HEADING_1,
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 600 }
                    }),
                    
                    // Nombre del patrocinador
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Patrocinador:',
                          size: 32,
                          bold: true
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 100 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: formData.sponsor_name || 'N/A',
                          size: 32
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 300 }
                    }),

                    // Logo de la empresa
                    ...(logoImageData2 ? [
                      new Paragraph({
                        children: [
                          new ImageRun({
                            data: logoImageData2,
                            transformation: {
                              width: logoWidth2,
                              height: logoHeight2,
                            },
                            type: 'png',
                          } as any),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 300 }
                      })
                    ] : []),

                    // Slogan
                    ...(formData.media_assets.slogan ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formData.media_assets.slogan,
                            size: 28,
                            italics: true
                          })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 300 }
                      })
                    ] : []),

                    // Social Links
                    ...(formData.social_links ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Redes Sociales:',
                            size: 28,
                            bold: true
                          })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 100 }
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: formData.social_links,
                            size: 28
                          })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 300 }
                      })
                    ] : []),

                    // Fechas
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Start Date:',
                          size: 28,
                          bold: true
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 100 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: formatDate2(formData.schedule.start),
                          size: 28
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 200 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'End Date:',
                          size: 28,
                          bold: true
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 100 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: formatDate2(formData.schedule.end),
                          size: 28
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 300 }
                    }),

                    // Costo Total
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Total Cost:',
                          size: 32,
                          bold: true
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 100 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `$${formData.total_sponsor_cost?.toFixed(2) || '0.00'}`,
                          size: 32,
                          bold: true,
                          color: '2563EB' // Color azul
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 400 }
                    })
                  ];

                  const doc = new Document({
                    sections: [
                      // Primera sección con el título general e información del patrocinador
                      {
                        properties: {
                          page: {
                            size: {
                              width: 15840, // 11 pulgadas (ancho aumentado desde 8.5")
                              height: 15000, // 14 pulgadas (alto aumentado - tamaño Legal)
                            },
                            margins: {
                              top: 720,    // 0.5 pulgadas (margen superior reducido)
                              right: 720,  // 0.5 pulgadas (margen derecho reducido)
                              bottom: 720, // 0.5 pulgadas (margen inferior reducido)
                              left: 360,   // 0.25 pulgadas (margen izquierdo muy reducido - imágenes más cerca del borde)
                            },
                          },
                        },
                        children: firstSectionChildren2
                      },
                      // Secciones para cada plataforma (cada una en su propia página)
                      ...sections.map((section: any) => ({
                        ...section,
                        properties: {
                          ...section.properties,
                          page: {
                            size: {
                              width: 15840, // 11 pulgadas (ancho aumentado desde 8.5")
                              height: 15000, // 14 pulgadas (alto aumentado - tamaño Legal)
                            },
                            margins: {
                              top: 720,    // 0.5 pulgadas (margen superior reducido)
                              right: 720,  // 0.5 pulgadas (margen derecho reducido)
                              bottom: 720, // 0.5 pulgadas (margen inferior reducido)
                              left: 360,   // 0.25 pulgadas (margen izquierdo muy reducido - imágenes más cerca del borde)
                            },
                          },
                        },
                      }))
                    ]
                  });

                  const blob = await Packer.toBlob(doc);
                  const fileName = `Platform_Images_${selectedContest?.name?.replace(/[^a-z0-9]/gi, '_') || 'Contest'}_${new Date().toISOString().split('T')[0]}.docx`;
                  saveAs(blob, fileName);
                } catch (docError) {
                  console.error('Error generating Word document:', docError);
                  void appDialog.alert('Error al generar el documento. Por favor, inténtalo de nuevo.');
                }
              }}
              sx={{
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' }
              }}
            >
              {t('preview_modal_info_sponsorship.downloadImages') || 'Descargar Información del Concurso'}
            </Button>
            <Button
              onClick={downloadAllPlatformsWord}
              variant="contained"
              startIcon={<FileTextIcon />}
              sx={{
                backgroundColor: '#9c27b0',
                '&:hover': { backgroundColor: '#7b1fa2' },
                ml: 1
              }}
            >
              {t('preview_modal_info_sponsorship.downloadAllPlatformsWord') || 'Descargar Todas las Plataformas (Word)'}
            </Button>
          <Button
            onClick={() => setShowCustomPlatformPreview(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {(() => {
            let platform = null;
            
            // Si hay una preview temporal, usarla primero (viene del botón "View Preview" en el modal de edición)
            if (tempPreviewPlatform) {
              platform = tempPreviewPlatform;
            } else {
              // Si no hay preview temporal, buscar en el array usando el índice seleccionado
            const filteredPlatforms = getFilteredCustomPlatforms();
              if (selectedPlatformIndex !== null && selectedPlatformIndex >= 0 && selectedPlatformIndex < filteredPlatforms.length) {
                platform = filteredPlatforms[selectedPlatformIndex];
              } else if (filteredPlatforms.length > 0) {
                // Si no hay índice seleccionado o es inválido, usar la primera plataforma
                platform = filteredPlatforms[0];
              }
            }

            return platform && platform.image ? (
              <Box sx={{ 
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                margin: '0 auto',
                backgroundColor: '#f9f9f9',
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                {/* Imagen principal */}
                <img
                  src={platform.image}
                  alt={t('preview_modal_info_sponsorship.mainImage')}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
                
                {/* Volante superpuesto */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${platform.x || 50}%`,
                    top: `${platform.y || 50}%`,
                    width: `${platform.width || 200}px`,
                    height: `${platform.height || 150}px`,
                  backgroundColor: '#f5f5dc',
                  border: '2px solid #ff6b35',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  padding: 1,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                }}
              >
                {/* Logo del patrocinador - usar la misma lógica que el botón Word */}
                {(() => {
                  // Usar logo_url del platform si existe, sino del formulario (igual que createCompositeImage)
                  const platformLogoUrl = (platform as any)?.logo_url || formData.media_assets.logo_url;
                  // Normalizar URL si es necesario
                  let normalizedLogoUrl = platformLogoUrl;
                  if (platformLogoUrl && !platformLogoUrl.startsWith('http://') && !platformLogoUrl.startsWith('https://')) {
                    normalizedLogoUrl = getCompleteImagePath(platformLogoUrl) || platformLogoUrl;
                  }
                  const logoWidth = (platform as any)?.logo_width || 60;
                  const logoHeight = (platform as any)?.logo_height || 60;
                  const shape = (platform as any)?.Image_form || 'circular';
                  
                  return normalizedLogoUrl ? (
                    <Box
                      component="img"
                      src={normalizedLogoUrl}
                      alt="Logo"
                      crossOrigin="anonymous"
                      sx={{
                        width: `${logoWidth}%`,
                        height: `${logoHeight}%`,
                        objectFit: 'contain',
                        borderRadius: shape === 'circular' ? '50%' : 0,
                        mb: 1
                      }}
                      onError={(e) => {
                        console.error('Error loading logo in preview:', normalizedLogoUrl);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                  <Box sx={{
                      width: `${logoWidth}%`,
                      height: `${logoHeight}%`,
                      borderRadius: shape === 'circular' ? '50%' : 0,
                    border: '2px solid black',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      LOGO
                  </Box>
                  );
                })()}

                {/* Título del concurso */}
                <Typography
                  sx={{
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    fontSize: `${(platform as any)?.Text_size || 0.9}rem`,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                    mb: 1.5
                  }}
                >
                  {selectedContest?.name?.toUpperCase() || 'NOMBRE DEL CONCURSO'}
                </Typography>

                {/* Texto personalizado */}
                <Typography
                  sx={{
                    color: '#ff6b35',
                    fontWeight: 'bold',
                    fontSize: `${(platform as any)?.Text_size || 0.9}rem`,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                    mt: 1.5
                  }}
                >
                  {(platform as any)?.custom_text || 'your ad goes here'}
                </Typography>
              </Box>
            </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '300px',
                color: 'text.secondary'
              }}>
                <ImageIcon sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6">
                  No hay imagen configurada
                </Typography>
                <Typography variant="body2">
                  Configura una plataforma personalizada primero
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowCustomPlatformPreview(false)}
            variant="outlined"
            sx={{ borderColor: '#666', color: '#666' }}
          >
            {t('preview_modal_info_sponsorship.close')}
          </Button>
          <Button 
            onClick={() => {
              // Obtener la plataforma que se está visualizando
              // Primero verificar si hay preview temporal
              let platform = tempPreviewPlatform;
              
              // Si no hay preview temporal, buscar en el array
              if (!platform) {
              const filteredPlatforms = getFilteredCustomPlatforms();
                platform = selectedPlatformIndex !== null && filteredPlatforms[selectedPlatformIndex]
                ? filteredPlatforms[selectedPlatformIndex]
                : (filteredPlatforms.length > 0)
                  ? filteredPlatforms[0]
                  : null;
              }
              
              if (platform) {
                const platformData = platform as any;
                // Cargar datos de la plataforma existente
                setCustomPlatformImage(platformData.image || null);
                setFlyerPosition({
                  x: platformData.x || 50,
                  y: platformData.y || 50,
                  width: platformData.width || 200,
                  height: platformData.height || 150
                });
                setCustomPlatformName(platformData.name || t('preview_modal_info_sponsorship.customPlatform'));
                setPlatformShape(((platformData as any).Image_form || (platformData as any).shape || 'circular') as 'circular' | 'square');
                setPlatformLogoUrl((platformData as any).logo_url || '');
                setPlatformCustomText((platformData as any).custom_text || 'your ad goes here');
                setPlatformFontSize((platformData as any).Text_size || 0.9);
                setLogoSize({
                  width: (platformData as any).logo_width || 60,
                  height: (platformData as any).logo_height || 60
                });
                setTempWidth(String(platformData.width || 200));
                setTempHeight(String(platformData.height || 150));
                setTempLogoWidth(String((platformData as any).logo_width || 60));
                setTempLogoHeight(String((platformData as any).logo_height || 60));
                // Encontrar el índice real en el array completo basado en el índice filtrado
                if (formData.custom_platforms) {
                  const actualIndex = formData.custom_platforms.findIndex(p => 
                    p.name === platformData.name && 
                    p.image === platformData.image &&
                    (p as any).contest_id === platformData.contest_id
                  );
                  setEditingPlatformIndex(actualIndex >= 0 ? actualIndex : null);
                } else {
                  setEditingPlatformIndex(null);
                }
              }
              
              // Limpiar preview temporal
              setTempPreviewPlatform(null);
              setShowCustomPlatformPreview(false);
              setShowAddPlatformModal(true);
            }}
            variant="contained"
            sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }}
          >
            {t('preview_modal_info_sponsorship.adjustSettings')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de selección de patrocinadores */}
      <Dialog
        open={showSponsorList}
        onClose={() => {
          console.log('Closing sponsor list modal');
          setShowSponsorList(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.25rem' }}>
            {t('create_sponsorship_form.select_sponsor')}
          </Typography>
          <Button
            onClick={() => setShowSponsorList(false)}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            ✕
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {/* Buscador */}
          <TextField
            fullWidth
            placeholder={t('create_sponsorship_form.search_sponsor')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  🔍
                </Box>
              )
            }}
          />
          
          {/* Lista de patrocinadores */}
          <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {(() => {
              // Función para verificar si el término de búsqueda es una subsecuencia del texto
              // (los caracteres deben aparecer en el mismo orden, pero no necesariamente consecutivos)
              const isSubsequence = (searchTerm: string, text: string): boolean => {
                if (!text || !searchTerm) return false;
                searchTerm = searchTerm.toLowerCase().trim();
                text = text.toLowerCase().trim();
                if (searchTerm.length === 0) return true;
                if (searchTerm.length > text.length) return false;
                
                let searchIndex = 0;
                for (let i = 0; i < text.length && searchIndex < searchTerm.length; i++) {
                  if (text[i] === searchTerm[searchIndex]) {
                    searchIndex++;
                  }
                }
                return searchIndex === searchTerm.length;
              };
              
              // Filtrar patrocinadores según el término de búsqueda
              const filteredSponsors = sponsors.filter(sponsor => {
                const search = searchTerm.trim();
                if (!search) return true;
                
                const sponsorName = (sponsor.sponsor_name || '').trim();
                const sponsorShortName = (sponsor.sponsor_short_name || '').trim();
                const industry = (sponsor.industry || '').trim();
                
                return (
                  isSubsequence(search, sponsorName) ||
                  isSubsequence(search, sponsorShortName) ||
                  isSubsequence(search, industry)
                );
              });
              
              if (filteredSponsors.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {sponsors.length === 0 
                        ? 'No hay patrocinadores registrados'
                        : 'No se encontraron patrocinadores que coincidan con la búsqueda'}
                    </Typography>
                  </Box>
                );
              }
              
              return filteredSponsors.map((sponsor) => (
                <Card
                  key={sponsor.id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={() => selectSponsor(sponsor)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: '#ff6b35',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.2rem'
                      }}>
                        {sponsor.sponsor_name?.charAt(0) || 'S'}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {sponsor.sponsor_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sponsor.sponsor_short_name} • {sponsor.industry}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sponsor.contact_name} • {sponsor.contact_email}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ));
            })()}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
          <Button 
            onClick={() => setShowSponsorList(false)}
            variant="outlined"
            sx={{ borderColor: '#666', color: '#666' }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Base Platform Preview Modal */}
      {selectedBasePlatformForPreview && (
        <Dialog
          open={showBasePlatformPreview}
          onClose={() => {
            setShowBasePlatformPreview(false);
            setSelectedBasePlatformForPreview(null);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">
              {t('preview_modal_info_sponsorship.preview')} - {selectedBasePlatformForPreview.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                type="number"
                label={t('platforms.previewScale')}
                value={tempPreviewScale}
                onChange={(e) => {
                  setTempPreviewScale(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const value = parseFloat(tempPreviewScale);
                    if (!isNaN(value)) {
                      const clampedValue = Math.max(30, Math.min(100, value));
                      setPreviewScale(clampedValue);
                      setTempPreviewScale(String(clampedValue));
                    }
                  }
                }}
                inputProps={{ 
                  min: 30, 
                  max: 100, 
                  step: 1 
                }}
                size="small"
                sx={{ width: '120px' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const value = parseFloat(tempPreviewScale);
                  if (!isNaN(value)) {
                    const clampedValue = Math.max(30, Math.min(100, value));
                    setPreviewScale(clampedValue);
                    setTempPreviewScale(String(clampedValue));
                  }
                }}
                sx={{ minWidth: 'auto' }}
              >
                {t('platforms.apply')}
              </Button>
            <Button
              onClick={() => {
                setShowBasePlatformPreview(false);
                setSelectedBasePlatformForPreview(null);
                  setPreviewScale(100);
                  setTempPreviewScale('100');
              }}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {selectedBasePlatformForPreview.image ? (
              <Box sx={{ 
                position: 'relative', 
                width: '100%', 
                maxWidth: '800px', 
                margin: '0 auto',
                borderRadius: 2,
                overflow: 'hidden',
                transform: `scale(${previewScale / 100})`,
                transformOrigin: 'center top'
              }}>
                <img
                  src={selectedBasePlatformForPreview.image}
                  alt={selectedBasePlatformForPreview.name}
                  style={{ 
                    width: '100%',
                    height: 'auto',
                    display: 'block' 
                  }}
                />
                {/* Volante con datos por defecto (se reemplazará con datos del patrocinador) */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${selectedBasePlatformForPreview.x || 50}%`,
                    top: `${selectedBasePlatformForPreview.y || 50}%`,
                    width: `${selectedBasePlatformForPreview.width || 200}px`,
                    height: `${selectedBasePlatformForPreview.height || 150}px`,
                    backgroundColor: '#f5f5dc',
                    border: '2px solid #7c3aed',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: 1,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  {/* Logo con forma configurable - usar la misma lógica que el botón Word */}
                  {(() => {
                    // Usar logo_url del platform si existe, sino del formulario (igual que createCompositeImage)
                    const platformLogoUrl = selectedBasePlatformForPreview.logo_url || formData.media_assets.logo_url;
                    // Normalizar URL si es necesario
                    let normalizedLogoUrl = platformLogoUrl;
                    if (platformLogoUrl && !platformLogoUrl.startsWith('http://') && !platformLogoUrl.startsWith('https://')) {
                      normalizedLogoUrl = getCompleteImagePath(platformLogoUrl) || platformLogoUrl;
                    }
                    return normalizedLogoUrl ? (
                      <Box
                        component="img"
                        src={normalizedLogoUrl}
                        alt="Logo"
                        crossOrigin="anonymous"
                        sx={{
                          width: `${selectedBasePlatformForPreview.logo_width || 60}%`,
                          height: `${selectedBasePlatformForPreview.logo_height || 60}%`,
                          objectFit: 'contain',
                          borderRadius: ((selectedBasePlatformForPreview.Image_form || selectedBasePlatformForPreview.shape || 'circular') === 'circular') ? '50%' : 0,
                          mb: 1
                        }}
                        onError={(e) => {
                          console.error('Error loading logo in preview:', normalizedLogoUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                    <Box sx={{
                      width: `${selectedBasePlatformForPreview.logo_width || 60}%`,
                      height: `${selectedBasePlatformForPreview.logo_height || 60}%`,
                      borderRadius: ((selectedBasePlatformForPreview.Image_form || selectedBasePlatformForPreview.shape || 'circular') === 'circular') ? '50%' : 0,
                      border: '2px solid black',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      LOGO
                    </Box>
                    );
                  })()}

                  {/* Texto del concurso */}
                  <Typography
                    sx={{
                      color: '#7c3aed',
                      fontWeight: 'bold',
                      fontSize: `${selectedBasePlatformForPreview.Text_size || 0.9}rem`,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                      mb: 1.5
                    }}
                  >
                    {selectedContest?.name?.toUpperCase() || 'NOMBRE DEL CONCURSO'}
                  </Typography>

                  {/* Texto personalizado */}
                  <Typography
                    sx={{
                      color: '#7c3aed',
                      fontWeight: 'bold',
                      fontSize: `${selectedBasePlatformForPreview.Text_size || 0.9}rem`,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                      mt: 1.5
                    }}
                  >
                    {selectedBasePlatformForPreview.custom_text || 'your ad goes here'}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ textAlign: 'center', py: 4 }}>{t('platforms.noImage')}</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#f5f5f5', padding: 2 }}>
            <Button 
              onClick={() => {
                setShowBasePlatformPreview(false);
                setSelectedBasePlatformForPreview(null);
                setPreviewScale(100);
                setTempPreviewScale('100');
              }}
              variant="outlined"
              sx={{ borderColor: '#7c3aed', color: '#7c3aed' }}
            >
              {t('preview_modal_info_sponsorship.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Diálogo para cambiar tamaño del voleto */}
      <Dialog 
        open={showVoletoSizeDialog} 
        onClose={() => {
          setShowVoletoSizeDialog(false);
          setSelectedPlatformForWord(null);
          setVoletoSize(null);
          setDistanciaX(60);
          setImageGeneralSize({ width: null, height: null });
          setOriginalImageSize(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('preview_modal_info_sponsorship.changeVoletoSize')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label={t('preview_modal_info_sponsorship.width')}
              type="number"
              value={voletoSize?.width || ''}
              onChange={(e) => {
                const width = parseInt(e.target.value) || 0;
                setVoletoSize(prev => prev ? { ...prev, width } : { width, height: 0 });
              }}
              fullWidth
            />
            <TextField
              label={t('preview_modal_info_sponsorship.height')}
              type="number"
              value={voletoSize?.height || ''}
              onChange={(e) => {
                const height = parseInt(e.target.value) || 0;
                setVoletoSize(prev => prev ? { ...prev, height } : { width: 0, height });
              }}
              fullWidth
            />
            <TextField
              label={t('preview_modal_info_sponsorship.distanceX')}
              type="number"
              value={distanciaX}
              onChange={(e) => {
                const distancia = parseInt(e.target.value) || 60;
                setDistanciaX(distancia);
              }}
              fullWidth
              helperText={t('preview_modal_info_sponsorship.distanceXHelper')}
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7c3aed' }}>
              {t('preview_modal_info_sponsorship.generalImageSize')}
            </Typography>
            <TextField
              label={t('preview_modal_info_sponsorship.generalImageWidth')}
              type="number"
              value={imageGeneralSize.width || ''}
              onChange={(e) => {
                const width = e.target.value ? parseInt(e.target.value) : null;
                if (width !== null && originalImageSize) {
                  // Calcular la altura manteniendo la proporción
                  const aspectRatio = originalImageSize.width / originalImageSize.height;
                  const height = Math.round(width / aspectRatio);
                  setImageGeneralSize({ width, height });
                } else {
                  setImageGeneralSize(prev => ({ ...prev, width }));
                }
              }}
              fullWidth
              helperText={t('preview_modal_info_sponsorship.generalImageWidthHelper')}
            />
            <TextField
              label={t('preview_modal_info_sponsorship.generalImageHeight')}
              type="number"
              value={imageGeneralSize.height || ''}
              onChange={(e) => {
                const height = e.target.value ? parseInt(e.target.value) : null;
                if (height !== null && originalImageSize) {
                  // Calcular el ancho manteniendo la proporción
                  const aspectRatio = originalImageSize.width / originalImageSize.height;
                  const width = Math.round(height * aspectRatio);
                  setImageGeneralSize({ width, height });
                } else {
                  setImageGeneralSize(prev => ({ ...prev, height }));
                }
              }}
              fullWidth
              helperText={t('preview_modal_info_sponsorship.generalImageHeightHelper')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowVoletoSizeDialog(false);
              setSelectedPlatformForWord(null);
              setVoletoSize(null);
              setDistanciaX(60);
              setImageGeneralSize({ width: null, height: null });
              setOriginalImageSize(null);
            }}
            variant="outlined"
          >
            {t('buttonsforms.Cancel')}
          </Button>
          <Button 
            onClick={async () => {
              if (selectedPlatformForWord && voletoSize) {
                // Guardar Fix_width y Fix_height en la base de datos
                try {
                  // Determinar el ID de la plataforma base a actualizar
                  let platformIdToUpdate = selectedPlatformForWord.id;
                  
                  // Si es voletaje, buscar el ID de la plataforma base de voletaje
                  if (selectedPlatformForWord.type === 'voletaje' || selectedPlatformForWord.platform_type === 'voletaje') {
                    const voletajeBase = basePlatforms.find((bp: any) => bp.platform_type === 'voletaje');
                    if (voletajeBase) {
                      platformIdToUpdate = voletajeBase.id;
                    }
                  }
                  
                  const platformData = {
                    ...selectedPlatformForWord,
                    Fix_width: voletoSize.width,
                    Fix_height: voletoSize.height,
                    distancia_x: distanciaX,
                    general_width: imageGeneralSize.width,
                    general_height: imageGeneralSize.height
                  };
                  
                  const response = await fetch(`${environment.apiBaseUrl}/platforms/${platformIdToUpdate}`, {
                    method: 'PUT',
                    headers: getApiHeaders(),
                    body: JSON.stringify(platformData)
                  });
                  
                  if (response.ok) {
                    // Actualizar el estado basePlatforms con los nuevos valores
                    setBasePlatforms(prevPlatforms => 
                      prevPlatforms.map(p => 
                        p.id === platformIdToUpdate 
                          ? { ...p, Fix_width: voletoSize.width, Fix_height: voletoSize.height, distancia_x: distanciaX, general_width: imageGeneralSize.width, general_height: imageGeneralSize.height }
                          : p
                      )
                    );
                    
                    // Generar y descargar el documento Word con distancia_x y tamaño general
                    generateAndDownloadWord(selectedPlatformForWord, voletoSize, distanciaX, imageGeneralSize);
                  } else {
                    console.error('Error guardando Fix_width y Fix_height');
                    // Aún así generar el documento
                    generateAndDownloadWord(selectedPlatformForWord, voletoSize, distanciaX, imageGeneralSize);
                  }
                } catch (error) {
                  console.error('Error guardando Fix_width y Fix_height:', error);
                  // Aún así generar el documento
                  generateAndDownloadWord(selectedPlatformForWord, voletoSize, distanciaX, imageGeneralSize);
                }
              }
            }}
            variant="contained"
            sx={{ backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6a1b9a' } }}
          >
            {t('preview_modal_info_sponsorship.continue')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de configuración Word */}
      <Dialog
        open={showWordConfigModal}
        onClose={() => {
          setShowWordConfigModal(false);
          if (previewImageUrl) {
            window.URL.revokeObjectURL(previewImageUrl);
            setPreviewImageUrl('');
          }
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Configurar Word - {selectedPlatformForWordConfig?.name || 'Plataforma'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
            <Box sx={{ 
              flex: 1, 
              minWidth: 0,
              maxHeight: '70vh',
              overflowY: 'auto',
              pr: 1
            }}>
              <Typography variant="h6" gutterBottom>Configuración del Cuadro</Typography>
              <TextField
                label="Ancho del cuadro (px)"
                type="number"
                fullWidth
                value={wordConfig.box_width}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value) || 0;
                  const updatedConfig = { ...wordConfig, box_width: newValue };
                  setWordConfig(updatedConfig);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, box_width: newValue });
                  }
                }}
                margin="normal"
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Alto del cuadro (px)"
                type="number"
                fullWidth
                value={wordConfig.box_height}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, box_height: parseFloat(e.target.value) || 0 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, box_height: newValue });
                  }
                }}
                margin="normal"
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Posición X (px)"
                type="number"
                fullWidth
                value={wordConfig.box_x}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, box_x: parseFloat(e.target.value) || 0 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, box_x: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Posición Y (px)"
                type="number"
                fullWidth
                value={wordConfig.box_y}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, box_y: parseFloat(e.target.value) || 0 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, box_y: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Grosor del borde (px)"
                type="number"
                fullWidth
                value={wordConfig.border_width}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, border_width: parseInt(e.target.value) || 3 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseInt(target.value) || 3;
                    updatePreviewWithConfig({ ...wordConfig, border_width: newValue });
                  }
                }}
                margin="normal"
              />
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Configuración de Textos</Typography>
              <TextField
                label="Posición Y Contest Name (px)"
                type="number"
                fullWidth
                value={wordConfig.contest_name_y}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, contest_name_y: parseFloat(e.target.value) || 0 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, contest_name_y: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Posición Y Custom Text (px)"
                type="number"
                fullWidth
                value={wordConfig.text_y}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, text_y: parseFloat(e.target.value) || 0 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, text_y: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Tamaño fuente Contest Name"
                type="number"
                fullWidth
                value={wordConfig.font_size_contest_name}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, font_size_contest_name: parseInt(e.target.value) || 18 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseInt(target.value) || 18;
                    updatePreviewWithConfig({ ...wordConfig, font_size_contest_name: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Tamaño fuente Custom Text"
                type="number"
                fullWidth
                value={wordConfig.font_size_custom_text}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, font_size_custom_text: parseInt(e.target.value) || 16 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseInt(target.value) || 16;
                    updatePreviewWithConfig({ ...wordConfig, font_size_custom_text: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Posición Y Logo (px)"
                type="number"
                fullWidth
                value={wordConfig.logo_y}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, logo_y: parseFloat(e.target.value) || 0 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 0;
                    updatePreviewWithConfig({ ...wordConfig, logo_y: newValue });
                  }
                }}
                margin="normal"
              />
              <TextField
                label="Distancia Logo - Parte Superior (px)"
                type="number"
                fullWidth
                value={wordConfig.logo_top_distance}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, logo_top_distance: parseFloat(e.target.value) || 15 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 15;
                    updatePreviewWithConfig({ ...wordConfig, logo_top_distance: newValue });
                  }
                }}
                margin="normal"
                helperText="Distancia entre el logo y la parte superior del cuadro"
              />
              <TextField
                label="Distancia Logo - Textos (px)"
                type="number"
                fullWidth
                value={wordConfig.logo_text_distance}
                onChange={(e) => {
                  setWordConfig({ ...wordConfig, logo_text_distance: parseFloat(e.target.value) || 40 });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;
                    const newValue = parseFloat(target.value) || 40;
                    updatePreviewWithConfig({ ...wordConfig, logo_text_distance: newValue });
                  }
                }}
                margin="normal"
                helperText="Distancia entre el logo y los textos"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>Vista Previa</Typography>
              {previewImageUrl && (
                <Box 
                  ref={previewContainerRef}
                  onKeyDown={handlePreviewKeyDown}
                  onClick={(e) => {
                    // Si se hace click en el contenedor (no en el cuadro), enfocar para poder presionar Enter
                    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'IMG') {
                      e.currentTarget.focus();
                    }
                  }}
                  tabIndex={0}
                  sx={{ 
                    border: '1px solid #ccc', 
                    p: 1, 
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    overflow: 'auto',
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    position: 'relative',
                    outline: 'none',
                    '&:focus': {
                      border: '2px solid #1976d2'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img 
                      ref={previewImageRef}
                      src={previewImageUrl} 
                      alt="Preview" 
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth && img.naturalHeight) {
                          const aspectRatio = img.naturalWidth / img.naturalHeight;
                          // Si la imagen es más ancha que alta, ajustar para que se muestre completa
                          if (aspectRatio > 1) {
                            img.style.width = 'auto';
                            img.style.maxWidth = '100%';
                            img.style.height = 'auto';
                            img.style.objectFit = 'contain';
                          } else {
                            img.style.width = '100%';
                            img.style.height = 'auto';
                            img.style.objectFit = 'contain';
                          }
                          
                          // Guardar dimensiones después de que la imagen se haya renderizado
                          setTimeout(() => {
                            const rect = img.getBoundingClientRect();
                            setPreviewImageSize({
                              width: rect.width,
                              height: rect.height,
                              naturalWidth: img.naturalWidth,
                              naturalHeight: img.naturalHeight
                            });
                          }, 100);
                        }
                      }}
                      style={{ 
                        width: 'auto',
                        maxWidth: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                    {previewImageSize && (
                      <Box
                        onMouseDown={handleBoxMouseDown}
                        sx={{
                          position: 'absolute',
                          border: `${wordConfig.border_width * (previewImageSize.width / previewImageSize.naturalWidth)}px solid #ff0000`,
                          cursor: isDragging ? 'grabbing' : 'grab',
                          userSelect: 'none',
                          pointerEvents: 'auto',
                          ...(previewImageSize && {
                            left: `${realToDisplayCoords(wordConfig.box_x, wordConfig.box_y).x}px`,
                            top: `${realToDisplayCoords(wordConfig.box_x, wordConfig.box_y).y}px`,
                            width: `${wordConfig.box_width * (previewImageSize.width / previewImageSize.naturalWidth)}px`,
                            height: `${wordConfig.box_height * (previewImageSize.height / previewImageSize.naturalHeight)}px`,
                          }),
                          backgroundColor: 'rgba(255, 0, 0, 0.1)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.2)',
                          }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowWordConfigModal(false);
            if (previewImageUrl) {
              window.URL.revokeObjectURL(previewImageUrl);
              setPreviewImageUrl('');
            }
          }}>
            Cancelar
          </Button>
          <Button onClick={saveWordConfig} variant="contained" color="primary">
            Guardar Configuración
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateSponsorshipForm;
